"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowLeft, ArrowUp, BadgeCheck, ChevronLeft, ChevronRight, Download, FileUp, History, Layers3, Loader2, Lock, Pencil, Play, Plus, Redo2, RotateCw, Save, ShieldAlert, Trash2, Undo2, Unlock, Wind, X, ZoomIn, ZoomOut } from "lucide-react";
import { memo, type PointerEvent as ReactPointerEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAppDialogs } from "@/components/dialogs/app-dialog-provider";
import { Button, Card } from "@/components/ui-system";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { ThemedSelect } from "@/components/ui-system/select";
import { getActiveAlertSummary } from "@/lib/api/endpoints/alerts";
import { getCommandById, listCommandsForDevice, sendCommand } from "@/lib/api/endpoints/commands";
import { listDevicesPage } from "@/lib/api/endpoints/devices";
import { listDeviceChannels } from "@/lib/api/endpoints/device-channels";
import {
  deleteHmiImageAsset,
  getHmiImageAssetBlob,
  listHmiDashboardDevices,
  updateDashboard,
  uploadHmiImageAsset,
  validateHmiCanvas,
} from "@/lib/api/endpoints/dashboards";
import { listMetrics } from "@/lib/api/endpoints/metrics";
import type { DashboardDto, DeviceChannelDto, DeviceDto, MetricDto } from "@/lib/api/types";
import type { JsonObject } from "@/lib/api/types/json";
import { alertBindingFingerprintFromDataSource } from "@/modules/alerts/utils/alert-binding-fingerprint";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

import { RuntimeScheduler } from "../runtime/RuntimeScheduler";
import { HmiRuntimeEngine, type HmiRuntimeSnapshot } from "../runtime/HmiRuntimeEngine";
import { sourceQueryId } from "../runtime/sourceFingerprint";
import { builtinHmiSymbols, getBuiltinSymbolById } from "../registry/builtin-symbols";
import { hmiElementRegistry } from "../registry/element-registry";
import { hmiTemplates } from "../registry/hmi-templates";
import { useHmiEditorStore, useHmiIsDirty } from "../store/hmi-editor-store";
import type { HmiCanvasDocument, HmiElement } from "../schema/hmi-canvas.schema";
import type { HmiBinding } from "../schema/hmi-bindings.schema";
import type { HmiSymbolDefinition } from "../schema/hmi-symbol.schema";
import { cloneHmiDocument, createDefaultHmiDocument, toDashboardLayout } from "../utils/default-document";
import { canCreateCustomSymbol, saveElementAsCustomSymbol, upgradeSymbolInstances } from "../utils/custom-symbols";
import { readHmiLayout } from "../utils/layout-kind";
import { sanitizeInlineSvg } from "../utils/svg-sanitize";
import { formatBindingValue, formatCanvasBindingValue, getBindingConnectionState } from "../utils/value-format";
import { zoomViewportAroundPoint } from "../editor/HmiEditorEngine";

function formatElementTitle(type: HmiElement["type"]): string {
  return type.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

const HMI_COLOR_SWATCHES = [
  "#f6fbf7",
  "#ffffff",
  "#dcefe2",
  "#8fd3a7",
  "#2c5f4e",
  "#0f172a",
  "#e2e8f0",
  "#0ea5e9",
  "#f59e0b",
  "#dc2626",
  "#8b5cf6",
  "#111827",
] as const;

const HMI_DEFAULT_LINE_COLOR = "#1f6f54";
const EMPTY_BINDINGS: HmiBinding[] = [];
const HMI_SOURCE_TYPE_OPTIONS = [
  { value: "static", label: "Static placeholder" },
  { value: "telemetry", label: "Live reading" },
] as const;
const HMI_READ_MODE_OPTIONS = [
  { value: "live", label: "Live reading" },
  { value: "window", label: "Window query" },
] as const;
const HMI_TIME_RANGE_OPTIONS = [
  { value: "last_5m", label: "Last 5m" },
  { value: "last_1h", label: "Last 1h" },
  { value: "last_24h", label: "Last 24h" },
  { value: "last_7d", label: "Last 7d" },
] as const;
const HMI_AGGREGATION_OPTIONS = [
  { value: "avg", label: "Average" },
  { value: "min", label: "Minimum" },
  { value: "max", label: "Maximum" },
] as const;
const HMI_LIVE_POLL_INTERVAL_MS = 5_000;

function normalizeHexColor(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  if (/^#[0-9a-fA-F]{6}$/.test(trimmed)) {
    return trimmed.toLowerCase();
  }
  const short = trimmed.match(/^#([0-9a-fA-F]{3})$/);
  if (!short) {
    return null;
  }
  const chars = short[1] ?? "";
  return `#${chars
    .split("")
    .map((char) => `${char}${char}`)
    .join("")
    .toLowerCase()}`;
}

function readElementBackgroundColor(element: HmiElement): string | null {
  const candidate = (element.style as Record<string, unknown> | undefined)?.backgroundColor;
  return typeof candidate === "string" && candidate.trim() ? candidate.trim() : null;
}

function readElementLineColor(element: HmiElement): string | null {
  const candidate = (element.style as Record<string, unknown> | undefined)?.stroke;
  return typeof candidate === "string" && candidate.trim() ? candidate.trim() : null;
}

function readFlowLineStripeColor(element: HmiElement): string | null {
  if (element.type !== "flow_line") {
    return null;
  }
  const candidate = element.flow.stripeColor;
  return typeof candidate === "string" && candidate.trim() ? candidate.trim() : "#ffffff";
}

function buildArrowGeometry(
  direction: "right" | "left" | "up" | "down",
  width: number,
  height: number,
): { shaft: { x1: number; y1: number; x2: number; y2: number }; headPoints: string } {
  const inset = 8;
  const headLength = Math.max(16, Math.min(28, direction === "left" || direction === "right" ? width * 0.16 : height * 0.16));
  const headHalf = Math.max(6, Math.min(14, direction === "left" || direction === "right" ? height * 0.22 : width * 0.22));
  const centerX = width / 2;
  const centerY = height / 2;

  if (direction === "left") {
    return {
      shaft: { x1: width - inset, y1: centerY, x2: inset + headLength, y2: centerY },
      headPoints: `${inset},${centerY} ${inset + headLength},${centerY - headHalf} ${inset + headLength},${centerY + headHalf}`,
    };
  }
  if (direction === "up") {
    return {
      shaft: { x1: centerX, y1: height - inset, x2: centerX, y2: inset + headLength },
      headPoints: `${centerX},${inset} ${centerX - headHalf},${inset + headLength} ${centerX + headHalf},${inset + headLength}`,
    };
  }
  if (direction === "down") {
    return {
      shaft: { x1: centerX, y1: inset, x2: centerX, y2: height - inset - headLength },
      headPoints: `${centerX},${height - inset} ${centerX - headHalf},${height - inset - headLength} ${centerX + headHalf},${height - inset - headLength}`,
    };
  }
  return {
    shaft: { x1: inset, y1: centerY, x2: width - inset - headLength, y2: centerY },
    headPoints: `${width - inset},${centerY} ${width - inset - headLength},${centerY - headHalf} ${width - inset - headLength},${centerY + headHalf}`,
  };
}

function getNextArrowDirection(direction: "right" | "left" | "up" | "down"): "right" | "left" | "up" | "down" {
  switch (direction) {
    case "right":
      return "down";
    case "down":
      return "left";
    case "left":
      return "up";
    case "up":
    default:
      return "right";
  }
}

function readSymbolOverrideBoolean(overrides: Record<string, unknown> | null | undefined, key: string): boolean | null {
  const value = overrides?.[key];
  return typeof value === "boolean" ? value : null;
}

function readSymbolOverrideNumber(overrides: Record<string, unknown> | null | undefined, key: string): number | null {
  const value = overrides?.[key];
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function supportsBuiltinSymbolStateToggle(definitionId: string | null | undefined): boolean {
  return Boolean(
    definitionId &&
      definitionId !== "builtin.process.pipe" &&
      definitionId !== "builtin.hvac.duct" &&
      definitionId !== "builtin.process.tank",
  );
}

function buildFlowLineGeometry(
  shape:
    | "horizontal"
    | "vertical"
    | "elbow_right_down"
    | "elbow_right_up"
    | "elbow_left_down"
    | "elbow_left_up"
    | "elbow_down_right"
    | "elbow_down_left"
    | "elbow_up_right"
    | "elbow_up_left",
  width: number,
  height: number,
  inset: number,
  turnRatio: number,
): { d: string; start: { x: number; y: number }; end: { x: number; y: number } } {
  const safeInset = Math.max(0, Math.min(inset, width / 2 - 1, height / 2 - 1));
  const safeTurnRatio = Math.max(0.15, Math.min(0.85, turnRatio));
  const midX = width / 2;
  const midY = height / 2;
  const left = safeInset;
  const right = width - safeInset;
  const top = safeInset;
  const bottom = height - safeInset;
  const turnX = left + (right - left) * safeTurnRatio;
  const turnXReverse = right - (right - left) * safeTurnRatio;
  const turnY = top + (bottom - top) * safeTurnRatio;
  const turnYReverse = bottom - (bottom - top) * safeTurnRatio;

  switch (shape) {
    case "vertical":
      return {
        d: `M ${midX} ${top} L ${midX} ${bottom}`,
        start: { x: midX, y: top },
        end: { x: midX, y: bottom },
      };
    case "elbow_right_down":
      return {
        d: `M ${left} ${midY} L ${turnX} ${midY} L ${turnX} ${bottom}`,
        start: { x: left, y: midY },
        end: { x: turnX, y: bottom },
      };
    case "elbow_right_up":
      return {
        d: `M ${left} ${midY} L ${turnX} ${midY} L ${turnX} ${top}`,
        start: { x: left, y: midY },
        end: { x: turnX, y: top },
      };
    case "elbow_left_down":
      return {
        d: `M ${right} ${midY} L ${turnXReverse} ${midY} L ${turnXReverse} ${bottom}`,
        start: { x: right, y: midY },
        end: { x: turnXReverse, y: bottom },
      };
    case "elbow_left_up":
      return {
        d: `M ${right} ${midY} L ${turnXReverse} ${midY} L ${turnXReverse} ${top}`,
        start: { x: right, y: midY },
        end: { x: turnXReverse, y: top },
      };
    case "elbow_down_right":
      return {
        d: `M ${midX} ${top} L ${midX} ${turnY} L ${right} ${turnY}`,
        start: { x: midX, y: top },
        end: { x: right, y: turnY },
      };
    case "elbow_down_left":
      return {
        d: `M ${midX} ${top} L ${midX} ${turnY} L ${left} ${turnY}`,
        start: { x: midX, y: top },
        end: { x: left, y: turnY },
      };
    case "elbow_up_right":
      return {
        d: `M ${midX} ${bottom} L ${midX} ${turnYReverse} L ${right} ${turnYReverse}`,
        start: { x: midX, y: bottom },
        end: { x: right, y: turnYReverse },
      };
    case "elbow_up_left":
      return {
        d: `M ${midX} ${bottom} L ${midX} ${turnYReverse} L ${left} ${turnYReverse}`,
        start: { x: midX, y: bottom },
        end: { x: left, y: turnYReverse },
      };
    case "horizontal":
    default:
      return {
        d: `M ${left} ${midY} L ${right} ${midY}`,
        start: { x: left, y: midY },
        end: { x: right, y: midY },
      };
  }
}

function readElementTextColor(element: HmiElement): "#0f172a" | "#ffffff" | null {
  const candidate = (element.style as Record<string, unknown> | undefined)?.textColor;
  if (candidate === "#ffffff") {
    return "#ffffff";
  }
  if (candidate === "#0f172a" || candidate === "#000000") {
    return "#0f172a";
  }
  return null;
}

function supportsElementTextTone(element: HmiElement): boolean {
  return (
    element.type === "text" ||
    element.type === "value" ||
    element.type === "stat_card" ||
    element.type === "data_table" ||
    element.type === "gauge" ||
    element.type === "trend_chart" ||
    element.type === "command_button" ||
    element.type === "command_toggle" ||
    element.type === "setpoint"
  );
}

function latestSeriesValue(values: Array<number | null> | undefined): number | null {
  if (!values || values.length === 0) {
    return null;
  }
  for (let index = values.length - 1; index >= 0; index -= 1) {
    const value = values[index];
    if (value !== null && value !== undefined) {
      return value;
    }
  }
  return null;
}

function gaugeSeverityColor(
  value: number,
  thresholds: {
    min: number;
    warningLow: number;
    warningHigh: number;
    max: number;
  },
): string {
  if (value <= thresholds.min || value >= thresholds.max) {
    return "#dc2626";
  }
  if (value < thresholds.warningLow || value > thresholds.warningHigh) {
    return "#f59e0b";
  }
  return "#16a34a";
}

function validateGaugeThresholdOrder(element: HmiElement | null): string | null {
  if (!element || element.type !== "gauge") {
    return null;
  }
  const { min, warningLow, warningHigh, max } = element.gauge;
  if (!(min < warningLow && warningLow < warningHigh && warningHigh < max)) {
    return "Gauge thresholds must follow: min < warning low < warning high < max.";
  }
  return null;
}

function getCanvasSelectionBounds(elements: HmiElement[]): { x: number; y: number; width: number; height: number } | null {
  const visible = elements.filter((element) => element.visible);
  if (visible.length === 0) {
    return null;
  }
  const left = Math.min(...visible.map((element) => element.x));
  const top = Math.min(...visible.map((element) => element.y));
  const right = Math.max(...visible.map((element) => element.x + element.width));
  const bottom = Math.max(...visible.map((element) => element.y + element.height));
  return {
    x: left,
    y: top,
    width: Math.max(1, right - left),
    height: Math.max(1, bottom - top),
  };
}

function coerceNumericState(value: number | string | null | undefined): number {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "on" || normalized === "open" || normalized === "running" || normalized === "active") {
      return 1;
    }
    if (normalized === "alarm" || normalized === "fault" || normalized === "trip") {
      return 2;
    }
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
}

function createLocalId(prefix: string): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

function createUuidLikeId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const chunk = () => Math.random().toString(16).slice(2, 10).padEnd(8, "0");
  const a = chunk();
  const b = chunk();
  const c = chunk();
  const d = chunk();
  return `${a}-${b.slice(0, 4)}-4${b.slice(1, 4)}-a${c.slice(1, 4)}-${c.slice(4, 8)}${d}`;
}

function stripNestedRevisionHistory(document: HmiCanvasDocument): HmiCanvasDocument {
  const revisions = document.metadata.revisions ?? [];
  if (revisions.length === 0) {
    return document;
  }

  let changed = false;
  const compactedRevisions = revisions.map((revision) => {
    const rawDocument = revision?.document;
    if (!rawDocument || typeof rawDocument !== "object") {
      return revision;
    }
    const nestedDocument = rawDocument as HmiCanvasDocument;
    const nestedRevisions = nestedDocument.metadata?.revisions ?? [];
    if (nestedRevisions.length === 0) {
      return revision;
    }
    changed = true;
    return {
      ...revision,
      document: {
        ...nestedDocument,
        metadata: {
          ...nestedDocument.metadata,
          revisions: [],
        },
      },
    };
  });

  if (!changed) {
    return document;
  }

  return {
    ...document,
    metadata: {
      ...document.metadata,
      revisions: compactedRevisions,
    },
  };
}

function getPrimaryBindingTarget(element: HmiElement): HmiBinding["target"] | null {
  const bindingTargetMap: Partial<Record<HmiElement["type"], HmiBinding["target"]>> = {
    value: "value",
    stat_card: "value",
    symbol_instance: "status",
    trend_chart: "chart_series",
    gauge: "gauge_value",
    data_table: "chart_series",
    command_toggle: "status",
    setpoint: "value",
  };
  return bindingTargetMap[element.type] ?? null;
}

function getElementBindings(bindings: HmiBinding[], element: HmiElement | null): HmiBinding[] {
  if (!element) {
    return [];
  }
  const target = getPrimaryBindingTarget(element);
  return bindings.filter((binding) => binding.elementId === element.id && (!target || binding.target === target));
}

function getImageAssetStoragePath(document: HmiCanvasDocument | null | undefined, assetId: string | null | undefined): string | null {
  if (!document || !assetId) {
    return null;
  }
  const asset = document.assets.find((entry) => entry.kind === "image" && entry.id === assetId);
  return asset?.storagePath ?? null;
}

function createTelemetryBinding(element: HmiElement, target: HmiBinding["target"], index = 0): HmiBinding {
  return {
    id: createLocalId("binding"),
    elementId: element.id,
    target,
    label: target === "chart_series" ? `Series ${index + 1}` : undefined,
    source: {
      kind: "telemetry",
      deviceId: "",
      channel: undefined,
      formula: undefined,
      metricId: undefined,
      readMode: "live",
      timeRange: "last_1h",
      aggregation: "avg",
    },
    rules: [],
    format: {},
  };
}

async function listHmiScopedDevices(tenantId: string, allWorkspaces: boolean): Promise<DeviceDto[]> {
  const page = await listDevicesPage({
    tenantId: allWorkspaces ? null : tenantId,
    workspaceScope: allWorkspaces ? "all" : null,
    limit: 200,
    offset: 0,
  });
  return page.items;
}

async function listHmiRenderableDevices(params: {
  tenantId: string;
  allWorkspaces: boolean;
  dashboardId?: string | null;
  preferDashboardScope?: boolean;
}): Promise<DeviceDto[]> {
  if (params.preferDashboardScope && params.dashboardId && !params.allWorkspaces) {
    return listHmiDashboardDevices(params.dashboardId);
  }
  return listHmiScopedDevices(params.tenantId, params.allWorkspaces);
}

function bindingAlertFingerprint(binding: HmiBinding): string | null {
  if (binding.source.kind !== "telemetry") {
    return null;
  }
  return alertBindingFingerprintFromDataSource({
    device_id: binding.source.deviceId,
    channel: binding.source.channel ?? null,
    formula: binding.source.formula ?? null,
    metric_id: binding.source.metricId ?? null,
  });
}

type HmiPendingCommandRequest =
  | {
      elementId: string;
      title: string;
      message: string;
      deviceId: string;
      payload: Record<string, unknown>;
    }
  | null;

type HmiCommandRuntimeState = {
  commandId?: string;
  status: string;
  response?: Record<string, unknown> | null;
  createdAt?: string | null;
  error?: string | null;
};

type HmiPersistMode = "save" | "publish";
type HmiResizeHandle = "n" | "e" | "s" | "w" | "ne" | "nw" | "se" | "sw";
type HmiInteractionMode = "move" | "resize" | "pan" | "move_all";
type HmiElementFrame = Pick<HmiElement, "x" | "y" | "width" | "height">;
type HmiTransformDraft = Record<string, HmiElementFrame>;
type HmiDragState = {
  mode: HmiInteractionMode;
  elementId?: string;
  handle?: HmiResizeHandle;
  startX: number;
  startY: number;
  initialElements: HmiTransformDraft;
};

function isTerminalCommandStatus(status: string | null | undefined): boolean {
  const normalized = (status ?? "").toLowerCase();
  return normalized === "acknowledged" || normalized === "completed" || normalized === "success" || normalized === "failed" || normalized === "cancelled";
}

function commandDeviceId(element: HmiElement): string | null {
  if (element.type === "command_button") {
    return element.command.deviceId ?? null;
  }
  if (element.type === "command_toggle") {
    return element.toggle.deviceId ?? null;
  }
  if (element.type === "setpoint") {
    return element.setpoint.deviceId ?? null;
  }
  return null;
}

function snapToGrid(value: number, gridSize: number, snapEnabled: boolean): number {
  if (!snapEnabled || gridSize <= 0) {
    return value;
  }
  return Math.round(value / gridSize) * gridSize;
}

function resizeElementWithHandle(
  element: HmiElement,
  handle: HmiResizeHandle,
  deltaX: number,
  deltaY: number,
  gridSize: number,
  snapEnabled: boolean,
): HmiElement {
  const minWidth = 40;
  const minHeight = 24;

  let nextX = element.x;
  let nextY = element.y;
  let nextWidth = element.width;
  let nextHeight = element.height;

  if (handle.includes("e")) {
    nextWidth = Math.max(minWidth, element.width + deltaX);
  }

  if (handle.includes("s")) {
    nextHeight = Math.max(minHeight, element.height + deltaY);
  }

  if (handle.includes("w")) {
    const adjustedWidth = Math.max(minWidth, element.width - deltaX);
    nextX = element.x + (element.width - adjustedWidth);
    nextWidth = adjustedWidth;
  }

  if (handle.includes("n")) {
    const adjustedHeight = Math.max(minHeight, element.height - deltaY);
    nextY = element.y + (element.height - adjustedHeight);
    nextHeight = adjustedHeight;
  }

  return {
    ...element,
    x: snapToGrid(nextX, gridSize, snapEnabled),
    y: snapToGrid(nextY, gridSize, snapEnabled),
    width: Math.max(minWidth, snapToGrid(nextWidth, gridSize, snapEnabled)),
    height: Math.max(minHeight, snapToGrid(nextHeight, gridSize, snapEnabled)),
  };
}

function frameFromElement(element: HmiElement): HmiElementFrame {
  return {
    x: element.x,
    y: element.y,
    width: element.width,
    height: element.height,
  };
}

function elementFromFrame(element: HmiElement, frame: HmiElementFrame): HmiElement {
  return {
    ...element,
    x: frame.x,
    y: frame.y,
    width: frame.width,
    height: frame.height,
  };
}

function HmiHeader({
  dashboard,
  canEditDashboard,
  runtimeMode,
  saving,
  isDirty,
  saveError,
  publicationStatus,
  validating,
  validationMessage,
  onBack,
  onExportJson,
  onImportJson,
  onSave,
  onValidate,
  onPublish,
  onToggleMode,
}: {
  dashboard: DashboardDto;
  canEditDashboard: boolean;
  runtimeMode: boolean;
  saving: boolean;
  isDirty: boolean;
  saveError: string | null;
  publicationStatus: "draft" | "published";
  validating: boolean;
  validationMessage: string | null;
  onBack: () => void;
  onExportJson: () => void;
  onImportJson: () => void;
  onSave: () => void;
  onValidate: () => void;
  onPublish: () => void;
  onToggleMode: (value: boolean) => void;
}) {
  return (
    <div className="border-b border-border/60 px-ds-4 py-ds-3">
      <div className="flex items-center justify-between gap-ds-3 overflow-hidden">
        <div className="flex min-w-0 items-center gap-ds-2">
          <div className="flex shrink-0 items-center gap-ds-2">
            <Button type="button" variant="ghost" size="sm" className="!px-2" onClick={onBack} aria-label="Back" title="Back">
              <ArrowLeft className="h-4 w-4" aria-hidden />
            </Button>
            <span className="rounded-full border border-sky-500/30 bg-sky-500/10 px-ds-2 py-[2px] text-[11px] font-semibold uppercase tracking-[0.08em] text-sky-700">
              HMI Canvas
            </span>
            {isDirty ? (
              <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-ds-2 py-[2px] text-[11px] font-semibold uppercase tracking-[0.08em] text-amber-700">
                Unsaved changes
              </span>
            ) : null}
            <span
              className={`rounded-full px-ds-2 py-[2px] text-[11px] font-semibold uppercase tracking-[0.08em] ${
                publicationStatus === "published"
                  ? "border border-emerald-500/30 bg-emerald-500/10 text-emerald-700"
                  : "border border-slate-400/30 bg-slate-400/10 text-foreground"
              }`}
            >
              {publicationStatus === "published" ? "Published" : "Draft"}
            </span>
          </div>
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-ds-2">
              <h1 className="text-[1.02rem] font-semibold tracking-tight text-foreground">{dashboard.name}</h1>
              <span className="hidden truncate text-micro text-muted xl:inline">
                {dashboard.description?.trim() || "Industrial HMI canvas for live process and logger visibility."}
              </span>
            </div>
          </div>
        </div>
        <div className="scrollbar-hidden flex shrink-0 items-center gap-ds-2 overflow-x-auto whitespace-nowrap pb-[2px]">
          <div className="inline-flex items-center gap-[3px] rounded-[1.1rem] border border-border/70 bg-slate-950/[0.03] p-[3px] shadow-[inset_0_1px_0_rgba(255,255,255,0.45)]">
            <button
              type="button"
              className={`inline-flex h-9 w-9 items-center justify-center rounded-[0.95rem] border border-transparent transition ${
                !runtimeMode
                  ? "bg-[color:var(--accent)] text-white shadow-[0_14px_22px_-16px_rgba(15,23,42,0.5)]"
                  : "text-muted hover:bg-card hover:text-foreground"
              }`}
              onClick={() => onToggleMode(false)}
              aria-label="Edit mode"
              aria-pressed={!runtimeMode}
              title="Edit mode"
            >
              <Pencil className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              className={`inline-flex h-9 w-9 items-center justify-center rounded-[0.95rem] border border-transparent transition ${
                runtimeMode
                  ? "bg-[color:var(--accent)] text-white shadow-[0_14px_22px_-16px_rgba(15,23,42,0.5)]"
                  : "text-muted hover:bg-card hover:text-foreground"
              }`}
              onClick={() => onToggleMode(true)}
              aria-label="Runtime mode"
              aria-pressed={runtimeMode}
              title="Runtime mode"
            >
              <Play className="h-4 w-4" aria-hidden />
            </button>
          </div>
          {canEditDashboard ? (
            <>
              <Button type="button" variant="secondary" size="sm" className="gap-ds-2" onClick={onValidate} disabled={validating}>
                <ShieldAlert className="h-4 w-4" aria-hidden />
                {validating ? "Validating..." : "Validate"}
              </Button>
              <Button type="button" variant="secondary" size="sm" className="gap-ds-2" onClick={onExportJson}>
                <Download className="h-4 w-4" aria-hidden />
                Export JSON
              </Button>
              <Button type="button" variant="secondary" size="sm" className="gap-ds-2" onClick={onImportJson}>
                <FileUp className="h-4 w-4" aria-hidden />
                Import JSON
              </Button>
              <Button type="button" variant="secondary" size="sm" className="gap-ds-2" onClick={onPublish} disabled={saving}>
                <BadgeCheck className="h-4 w-4" aria-hidden />
                {publicationStatus === "published" ? "Republish" : "Publish"}
              </Button>
            </>
          ) : null}
          {canEditDashboard ? (
            <Button type="button" variant="primary" size="sm" className="gap-ds-2" disabled={saving || !isDirty} onClick={onSave}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Save className="h-4 w-4" aria-hidden />}
              {saving ? "Saving..." : "Save"}
            </Button>
          ) : null}
        </div>
      </div>
      {saveError || validationMessage ? (
        <div className="mt-ds-2 flex flex-wrap items-center gap-ds-3">
          {saveError ? (
            <p className="text-micro text-error" role="alert">
              {saveError}
            </p>
          ) : null}
          {validationMessage ? (
            <p className="text-micro text-muted" role="status">
              {validationMessage}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function HmiToolbar({
  zoom,
  allLayersSelected,
  runtimeMode,
  canEditDashboard,
  canUndo,
  canRedo,
  onZoom,
  onSelectAllLayers,
  onUndo,
  onRedo,
}: {
  zoom: number;
  allLayersSelected: boolean;
  runtimeMode: boolean;
  canEditDashboard: boolean;
  canUndo: boolean;
  canRedo: boolean;
  onZoom: (delta: number) => void;
  onSelectAllLayers: () => void;
  onUndo: () => void;
  onRedo: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-ds-3 border-b border-border/50 bg-card/55 px-ds-4 py-ds-2.5">
      <div className="flex flex-wrap items-center gap-ds-2">
        <Button type="button" variant="secondary" size="sm" className="gap-ds-2" onClick={onUndo} disabled={runtimeMode || !canEditDashboard || !canUndo}>
          <Undo2 className="h-4 w-4" aria-hidden />
          Undo
        </Button>
        <Button type="button" variant="secondary" size="sm" className="gap-ds-2" onClick={onRedo} disabled={runtimeMode || !canEditDashboard || !canRedo}>
          <Redo2 className="h-4 w-4" aria-hidden />
          Redo
        </Button>
        {!runtimeMode && canEditDashboard ? (
          <Button type="button" variant={allLayersSelected ? "primary" : "secondary"} size="sm" onClick={onSelectAllLayers}>
            Select all
          </Button>
        ) : null}
        {!runtimeMode && canEditDashboard ? (
          <span className="rounded-xl border border-border/60 bg-background/60 px-ds-3 py-ds-2 text-micro text-muted">
            Click to select, use Ctrl + A to select all, drag empty canvas to pan, and use Ctrl + wheel to zoom.
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-ds-2">
        <Button type="button" variant="secondary" size="sm" onClick={() => onZoom(-0.1)}>
          <ZoomOut className="h-4 w-4" aria-hidden />
        </Button>
        <div className="rounded-xl border border-border/60 px-ds-3 py-ds-2 text-caption font-medium text-foreground">
          {(zoom * 100).toFixed(0)}%
        </div>
        <Button type="button" variant="secondary" size="sm" onClick={() => onZoom(0.1)}>
          <ZoomIn className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </div>
  );
}

function ElementBindingInspector({
  tenantId,
  allWorkspaces,
  element,
  bindings,
  onBindingsChange,
}: {
  tenantId: string;
  allWorkspaces: boolean;
  element: HmiElement;
  bindings: HmiBinding[];
  onBindingsChange: (updater: (bindings: HmiBinding[]) => HmiBinding[]) => void;
}) {
  const devicesQuery = useQuery({
    queryKey: ["hmi-devices", allWorkspaces ? "all" : tenantId],
    queryFn: () => listHmiScopedDevices(tenantId, allWorkspaces),
    staleTime: 30_000,
  });

  const selectedTelemetryBinding = bindings.find(
    (binding): binding is HmiBinding & { source: Extract<HmiBinding["source"], { kind: "telemetry" }> } =>
      binding.source.kind === "telemetry",
  );
  const selectedDeviceId = selectedTelemetryBinding?.source.deviceId ?? "";
  const channelsQuery = useQuery({
    queryKey: ["hmi-device-channels", selectedDeviceId, allWorkspaces ? "all" : "tenant"],
    queryFn: () => listDeviceChannels(selectedDeviceId, { workspaceScope: allWorkspaces ? "all" : null }),
    enabled: Boolean(selectedDeviceId),
    staleTime: 30_000,
  });
  const metricsQuery = useQuery({
    queryKey: ["hmi-metrics", tenantId],
    queryFn: () => listMetrics(tenantId),
    staleTime: 30_000,
  });

  const target = getPrimaryBindingTarget(element);

  if (!target) {
    return null;
  }

  const allowsMultiple = target === "chart_series";
  const currentBindings = bindings.length
    ? bindings
    : target === "chart_series"
      ? []
      : [
          {
            id: `${element.id}_binding`,
            elementId: element.id,
            target,
            source: { kind: "static", value: null },
            rules: [],
          } satisfies HmiBinding,
        ];
  const updateBindingAtIndex = (index: number, updater: (binding: HmiBinding) => HmiBinding) => {
    onBindingsChange((current) => {
      const nextCurrent = current.length
        ? [...current]
        : [...currentBindings];
      const existing = nextCurrent[index] ?? {
        id: `${element.id}_binding`,
        elementId: element.id,
        target,
        source: { kind: "static" as const, value: null },
        rules: [],
      };
      nextCurrent[index] = updater(existing);
      return nextCurrent;
    });
  };

  const devices = devicesQuery.data ?? [];
  const channels = channelsQuery.data ?? [];
  const metrics = metricsQuery.data ?? [];

  return (
    <div className="space-y-ds-3 rounded-2xl border border-border/60 bg-card/40 p-ds-4">
      <div>
        <p className="text-caption font-semibold text-foreground">Telemetry binding</p>
        <p className="mt-1 text-micro text-muted">Bind this element to a device channel, formula, or workspace metric.</p>
      </div>
      {currentBindings.map((binding, index) => {
        const activeBinding = binding;
        return (
          <div key={binding.id} className="space-y-ds-3 rounded-2xl border border-border/50 bg-background/45 p-ds-3">
            {allowsMultiple ? (
              <div className="flex items-center justify-between gap-ds-2">
                <input
                  className="ui-control py-ds-2"
                  value={activeBinding.label ?? `Series ${index + 1}`}
                  onChange={(event) => updateBindingAtIndex(index, (current) => ({ ...current, label: event.target.value }))}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-error hover:!text-error"
                  onClick={() => onBindingsChange((current) => current.filter((_, bindingIndex) => bindingIndex !== index))}
                >
                  Remove
                </Button>
              </div>
            ) : null}
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Source type</span>
              <ThemedSelect
                value={activeBinding.source.kind}
                onChange={(nextValue) =>
                  updateBindingAtIndex(index, (current) => ({
                    ...current,
                    source:
                      nextValue === "telemetry"
                        ? createTelemetryBinding(element, target, index).source
                        : {
                            kind: "static",
                            value: null,
                          },
                  }))
                }
                options={[...HMI_SOURCE_TYPE_OPTIONS]}
                ariaLabel="Telemetry source type"
                triggerClassName="min-h-10 py-ds-2"
              />
            </label>
            {activeBinding.source.kind === "telemetry" ? (
              <>
                <label className="flex flex-col gap-ds-1">
                  <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Reading mode</span>
                  <ThemedSelect
                    value={activeBinding.source.readMode ?? "live"}
                    onChange={(nextValue) =>
                      updateBindingAtIndex(index, (current) =>
                        current.source.kind === "telemetry"
                          ? {
                              ...current,
                              source: {
                                ...current.source,
                                readMode: nextValue as "live" | "window",
                              },
                            }
                          : current,
                      )
                    }
                    options={[...HMI_READ_MODE_OPTIONS]}
                    ariaLabel="Telemetry reading mode"
                    triggerClassName="min-h-10 py-ds-2"
                  />
                </label>
                <p className="rounded-xl border border-border/50 bg-muted-bg/25 px-ds-3 py-ds-2 text-[11px] leading-5 text-muted">
                  {activeBinding.source.readMode === "window"
                    ? "Window query uses the selected range and aggregation for historical series."
                    : "Live reading polls every 5 seconds like the Live page. If the logger heartbeat goes stale, the canvas shows OFFLINE."}
                </p>
                <label className="flex flex-col gap-ds-1">
                  <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Device</span>
                  <ThemedSelect
                    value={activeBinding.source.deviceId}
                    onChange={(nextValue) =>
                      updateBindingAtIndex(index, (current) =>
                        current.source.kind === "telemetry"
                          ? {
                              ...current,
                              source: {
                                ...current.source,
                                deviceId: nextValue,
                                channel: undefined,
                              },
                            }
                          : current,
                      )
                    }
                    options={[
                      { value: "", label: "Select a device" },
                      ...devices.map((device: DeviceDto) => ({
                        value: device.id,
                        label: device.name?.trim() || device.device_uid,
                        description: device.device_uid,
                      })),
                    ]}
                    ariaLabel="Telemetry binding device"
                    searchable
                    searchPlaceholder="Search devices"
                    triggerClassName="min-h-10 py-ds-2"
                  />
                </label>
                <label className="flex flex-col gap-ds-1">
                  <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Channel</span>
                  <ThemedSelect
                    value={activeBinding.source.channel ?? ""}
                    onChange={(nextValue) =>
                      updateBindingAtIndex(index, (current) =>
                        current.source.kind === "telemetry"
                          ? {
                              ...current,
                              source: {
                                ...current.source,
                                channel: nextValue || undefined,
                                formula: undefined,
                                metricId: undefined,
                                readMode: nextValue ? "live" : current.source.readMode,
                              },
                            }
                          : current,
                      )
                    }
                    options={[
                      { value: "", label: "Select a channel" },
                      ...channels.map((channel: DeviceChannelDto) => ({
                        value: channel.key,
                        label: channel.label?.trim() || channel.key,
                        description: channel.unit ?? undefined,
                      })),
                    ]}
                    ariaLabel="Telemetry binding channel"
                    triggerClassName="min-h-10 py-ds-2"
                  />
                </label>
                <label className="flex flex-col gap-ds-1">
                  <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Formula override</span>
                  <input
                    className="ui-control py-ds-2"
                    value={activeBinding.source.formula ?? ""}
                    placeholder="Optional formula like avg(temp, humidity)"
                    onChange={(event) =>
                      updateBindingAtIndex(index, (current) =>
                        current.source.kind === "telemetry"
                          ? {
                              ...current,
                              source: {
                                ...current.source,
                                formula: event.target.value || undefined,
                                channel: undefined,
                                metricId: undefined,
                                readMode: event.target.value ? "window" : current.source.readMode,
                              },
                            }
                          : current,
                      )
                    }
                  />
                </label>
                <label className="flex flex-col gap-ds-1">
                  <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Catalog metric</span>
                  <ThemedSelect
                    value={activeBinding.source.metricId ?? ""}
                    onChange={(nextValue) =>
                      updateBindingAtIndex(index, (current) =>
                        current.source.kind === "telemetry"
                          ? {
                              ...current,
                              source: {
                                ...current.source,
                                metricId: nextValue || undefined,
                                channel: undefined,
                                formula: undefined,
                                readMode: nextValue ? "window" : current.source.readMode,
                              },
                            }
                          : current,
                      )
                    }
                    options={[
                      { value: "", label: "No metric" },
                      ...metrics.map((metric: MetricDto) => ({
                        value: metric.id,
                        label: metric.name,
                      })),
                    ]}
                    ariaLabel="Telemetry binding metric"
                    triggerClassName="min-h-10 py-ds-2"
                  />
                </label>
                {activeBinding.source.readMode === "window" ? (
                  <div className="grid grid-cols-2 gap-ds-3">
                    <label className="flex flex-col gap-ds-1">
                      <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Range</span>
                      <ThemedSelect
                        value={activeBinding.source.timeRange}
                        onChange={(nextValue) =>
                          updateBindingAtIndex(index, (current) =>
                            current.source.kind === "telemetry"
                              ? {
                                  ...current,
                                  source: {
                                    ...current.source,
                                    timeRange: nextValue as typeof current.source.timeRange,
                                  },
                                }
                              : current,
                          )
                        }
                        options={[...HMI_TIME_RANGE_OPTIONS]}
                        ariaLabel="Telemetry binding time range"
                        triggerClassName="min-h-10 py-ds-2"
                      />
                    </label>
                    <label className="flex flex-col gap-ds-1">
                      <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Aggregation</span>
                      <ThemedSelect
                        value={activeBinding.source.aggregation}
                        onChange={(nextValue) =>
                          updateBindingAtIndex(index, (current) =>
                            current.source.kind === "telemetry"
                              ? {
                                  ...current,
                                  source: {
                                    ...current.source,
                                    aggregation: nextValue as typeof current.source.aggregation,
                                  },
                                }
                              : current,
                          )
                        }
                        options={[...HMI_AGGREGATION_OPTIONS]}
                        ariaLabel="Telemetry binding aggregation"
                        triggerClassName="min-h-10 py-ds-2"
                      />
                    </label>
                  </div>
                ) : null}
                <div className="grid grid-cols-2 gap-ds-3">
                  <label className="flex flex-col gap-ds-1">
                    <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Decimals</span>
                    <input
                      className="ui-control py-ds-2"
                      type="number"
                      min={0}
                      max={6}
                      value={activeBinding.format?.decimals ?? 2}
                      onChange={(event) =>
                        updateBindingAtIndex(index, (current) => ({
                          ...current,
                          format: {
                            ...(current.format ?? {}),
                            decimals: Number(event.target.value),
                          },
                        }))
                      }
                    />
                  </label>
                  <label className="flex flex-col gap-ds-1">
                    <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Null text</span>
                    <input
                      className="ui-control py-ds-2"
                      value={activeBinding.format?.nullText ?? ""}
                      onChange={(event) =>
                        updateBindingAtIndex(index, (current) => ({
                          ...current,
                          format: {
                            ...(current.format ?? {}),
                            nullText: event.target.value || undefined,
                          },
                        }))
                      }
                    />
                  </label>
                  <label className="flex flex-col gap-ds-1">
                    <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Prefix</span>
                    <input
                      className="ui-control py-ds-2"
                      value={activeBinding.format?.prefix ?? ""}
                      onChange={(event) =>
                        updateBindingAtIndex(index, (current) => ({
                          ...current,
                          format: {
                            ...(current.format ?? {}),
                            prefix: event.target.value || undefined,
                          },
                        }))
                      }
                    />
                  </label>
                  <label className="flex flex-col gap-ds-1">
                    <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Suffix / Unit</span>
                    <input
                      className="ui-control py-ds-2"
                      value={activeBinding.format?.suffix ?? ""}
                      onChange={(event) =>
                        updateBindingAtIndex(index, (current) => ({
                          ...current,
                          format: {
                            ...(current.format ?? {}),
                            suffix: event.target.value || undefined,
                          },
                        }))
                      }
                    />
                  </label>
                </div>
              </>
            ) : (
              <label className="flex flex-col gap-ds-1">
                <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Placeholder value</span>
                <input
                  className="ui-control py-ds-2"
                  value={activeBinding.source.kind === "static" ? String(activeBinding.source.value ?? "") : ""}
                  onChange={(event) =>
                    updateBindingAtIndex(index, (current) => ({
                      ...current,
                      source: {
                        kind: "static",
                        value: event.target.value,
                      },
                    }))
                  }
                />
              </label>
            )}
          </div>
        );
      })}
      {allowsMultiple ? (
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="gap-ds-2"
          onClick={() =>
            onBindingsChange((current) => [...current, createTelemetryBinding(element, target, current.length)])
          }
        >
          <Plus className="h-4 w-4" aria-hidden />
          Add series
        </Button>
      ) : null}
    </div>
  );
}

function HmiInspector({
  document,
  tenantId,
  allWorkspaces,
  element,
  bindings,
  canEditDashboard,
  onDeleteImageAsset,
  onUpdateElement,
  onBindingsChange,
  onUpdateDocument,
  onSaveAsSymbol,
  onUpgradeSymbolInstances,
  presentation = "sidebar",
}: {
  document: HmiCanvasDocument;
  tenantId: string;
  allWorkspaces: boolean;
  element: HmiElement | null;
  bindings: HmiBinding[];
  canEditDashboard: boolean;
  onDeleteImageAsset: (storagePath: string) => void;
  onUpdateElement: (updater: (element: HmiElement) => HmiElement) => void;
  onBindingsChange: (updater: (bindings: HmiBinding[]) => HmiBinding[]) => void;
  onUpdateDocument: (updater: (document: HmiCanvasDocument) => HmiCanvasDocument) => void;
  onSaveAsSymbol: (existingSymbolId?: string | null) => void;
  onUpgradeSymbolInstances: (definitionId: string, mode: "selected" | "all") => void;
  presentation?: "sidebar" | "modal";
}) {
  const dialogs = useAppDialogs();
  const [imageImporting, setImageImporting] = useState(false);
  const selectedCommandDeviceId = element ? commandDeviceId(element) : null;
  const commandDevicesQuery = useQuery({
    queryKey: ["hmi-command-devices", allWorkspaces ? "all" : tenantId],
    queryFn: () => listHmiScopedDevices(tenantId, allWorkspaces),
    staleTime: 30_000,
    enabled: Boolean(element),
  });
  const recentCommandsQuery = useQuery({
    queryKey: ["hmi-command-audit", selectedCommandDeviceId ?? ""],
    queryFn: () => listCommandsForDevice(selectedCommandDeviceId!, { limit: 5 }),
    enabled: Boolean(selectedCommandDeviceId),
    staleTime: 5_000,
    refetchInterval: 5_000,
    refetchOnWindowFocus: false,
  });
  const commandDevices = commandDevicesQuery.data ?? [];
  const selectedDefinition =
    element?.type === "symbol_instance"
      ? document.symbols.find((symbol) => symbol.id === element.symbol.definitionId) ?? getBuiltinSymbolById(element.symbol.definitionId)
      : null;
  const imageAssets = document.assets.filter((asset) => asset.kind === "image");

  const containerClass =
    presentation === "modal"
      ? "w-full max-w-none"
      : "w-full max-w-[22rem] overflow-y-auto border-l border-card-border bg-card/95 p-ds-4";

  if (!element) {
    return (
      <div className={presentation === "modal" ? "rounded-2xl border border-card-border bg-muted-bg/30 p-ds-4" : containerClass}>
        <p className="text-caption text-muted">Select an element to edit its geometry, content, and bindings.</p>
      </div>
    );
  }

  const updateNumber = (field: keyof Pick<HmiElement, "x" | "y" | "width" | "height">, value: string) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return;
    }
    onUpdateElement((current) => ({
      ...current,
      [field]: parsed,
    }));
  };

  const updateBackgroundColor = (value: string | null) => {
    onUpdateElement((current) => {
      const nextStyle = { ...(current.style as Record<string, unknown>) };
      if (value && value.trim()) {
        nextStyle.backgroundColor = value.trim();
      } else {
        delete nextStyle.backgroundColor;
      }
      return {
        ...current,
        style: nextStyle,
      };
    });
  };

  const updateLineColor = (value: string | null) => {
    onUpdateElement((current) => {
      if (current.type !== "line" && current.type !== "arrow" && current.type !== "flow_line") {
        return current;
      }
      const nextStyle = { ...(current.style as Record<string, unknown>) };
      if (value && value.trim()) {
        nextStyle.stroke = value.trim();
      } else {
        delete nextStyle.stroke;
      }
      return {
        ...current,
        style: nextStyle,
      };
    });
  };

  const updateTextColor = (value: "#0f172a" | "#ffffff" | null) => {
    onUpdateElement((current) => {
      const nextStyle = { ...(current.style as Record<string, unknown>) };
      if (value) {
        nextStyle.textColor = value;
      } else {
        delete nextStyle.textColor;
      }
      return {
        ...current,
        style: nextStyle,
      };
    });
  };

  const importImageAsset = async (file: File | null | undefined) => {
    if (!file || !canEditDashboard || element.type !== "image_asset") {
      return;
    }
    setImageImporting(true);
    try {
      const previousStoragePath = getImageAssetStoragePath(document, element.asset.assetId);
      const uploaded = await uploadHmiImageAsset(file);
      const assetId = createUuidLikeId();
      const title = file.name.replace(/\.[^.]+$/, "").trim() || "Imported image";
      onUpdateDocument((current) => ({
        ...current,
        assets: [
          ...current.assets,
          {
            id: assetId,
            kind: "image",
            title,
            source: "imported",
            storagePath: uploaded.path,
          },
        ],
      }));
      onUpdateElement((current) =>
        current.type === "image_asset"
          ? {
              ...current,
              name: current.name === "image asset" ? title : current.name,
              asset: {
                ...current.asset,
                assetId,
                alt: current.asset.alt || title,
              },
            }
          : current,
      );
      if (
        previousStoragePath &&
        document.elements.filter((entry) => entry.id !== element.id).every((entry) => {
          if (entry.type !== "image_asset") {
            return true;
          }
          return getImageAssetStoragePath(document, entry.asset.assetId) !== previousStoragePath;
        })
      ) {
        onDeleteImageAsset(previousStoragePath);
      }
    } catch (error) {
      await dialogs.alert({
        title: "Could not import image",
        description: error instanceof Error ? error.message : "Could not import the image.",
        tone: "danger",
      });
    } finally {
      setImageImporting(false);
    }
  };

  return (
    <div className={containerClass}>
      <div className="space-y-ds-4">
        <div className="rounded-[20px] border border-card-border bg-muted-bg/30 px-ds-4 py-ds-4">
          <p className="text-caption font-semibold text-foreground">{formatElementTitle(element.type)}</p>
          <p className="mt-1 text-[11px] leading-5 text-muted">Element properties and telemetry binding.</p>
        </div>
        <label className="flex flex-col gap-ds-1.5 rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4">
          <span className="form-label">Name</span>
          <input
            className="ui-control py-ds-2"
            value={element.name}
            disabled={!canEditDashboard}
            onChange={(event) => onUpdateElement((current) => ({ ...current, name: event.target.value }))}
          />
          <span className={canEditDashboard ? "form-helper" : "form-helper-disabled"}>Give the canvas element a clear internal name for future edits and bindings.</span>
        </label>
        <div className="grid grid-cols-2 gap-ds-3 rounded-[20px] border border-card-border bg-muted-bg/30 p-ds-4">
          {(["x", "y", "width", "height"] as const).map((field) => (
            <label key={field} className="flex flex-col gap-ds-1">
              <span className="form-label">{field}</span>
              <input
                className="ui-control py-ds-2"
                type="number"
                value={element[field]}
                disabled={!canEditDashboard}
                onChange={(event) => updateNumber(field, event.target.value)}
              />
            </label>
          ))}
        </div>
        {element.type === "flow_line" ? (
          <div className="space-y-ds-3">
            <ModernColorField
              label="Background color"
              description="Set the flow line tile background. Clear it for a transparent surface."
              value={readElementBackgroundColor(element)}
              disabled={!canEditDashboard}
              onChange={updateBackgroundColor}
              fallbackColor="#f6fbf7"
              placeholder="Transparent"
            />
            <ModernColorField
              label="Line color"
              description="Set the pipe stroke color for this flow line."
              value={readElementLineColor(element)}
              disabled={!canEditDashboard}
              onChange={updateLineColor}
              fallbackColor={HMI_DEFAULT_LINE_COLOR}
              placeholder="Default line"
            />
            <ModernColorField
              label="Stripe color"
              description="Choose the moving stripe color from the HMI palette or enter a custom tone."
              value={readFlowLineStripeColor(element)}
              disabled={!canEditDashboard}
              onChange={(value) =>
                onUpdateElement((current) =>
                  "flow" in current
                    ? {
                        ...current,
                        flow: {
                          ...current.flow,
                          stripeColor: value,
                        },
                      }
                    : current,
                )
              }
              fallbackColor="#ffffff"
              placeholder="Default stripe"
            />
          </div>
        ) : element.type !== "line" && element.type !== "arrow" ? (
          <ModernColorField
            label="Background color"
            description="Set the element surface color. Clear it anytime to fall back to the default HMI styling."
            value={readElementBackgroundColor(element)}
            disabled={!canEditDashboard}
            onChange={updateBackgroundColor}
          />
        ) : (
          <ModernColorField
            label="Line color"
            description="Set the stroke color for this line. Clear it anytime to use the default HMI line tone."
            value={readElementLineColor(element)}
            disabled={!canEditDashboard}
            onChange={updateLineColor}
            fallbackColor={HMI_DEFAULT_LINE_COLOR}
            placeholder="Default line"
          />
        )}
        {element.type === "arrow" ? (
          <label className="flex flex-col gap-ds-1">
            <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Direction</span>
            <ThemedSelect
              value={element.arrow.direction}
              disabled={!canEditDashboard}
              onChange={(nextValue) =>
                onUpdateElement((current) =>
                  current.type === "arrow"
                    ? {
                        ...current,
                        arrow: {
                          ...current.arrow,
                          direction: nextValue as typeof current.arrow.direction,
                        },
                      }
                    : current,
                )
              }
              options={[
                { value: "right", label: "Right" },
                { value: "left", label: "Left" },
                { value: "up", label: "Top" },
                { value: "down", label: "Down" },
              ]}
              ariaLabel="Arrow direction"
              triggerClassName="min-h-10 py-ds-2"
            />
          </label>
        ) : null}
        {"flow" in element ? (
          <div className="grid grid-cols-2 gap-ds-3">
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Shape</span>
              <ThemedSelect
                value={element.flow.shape}
                disabled={!canEditDashboard}
                onChange={(nextValue) =>
                  onUpdateElement((current) =>
                    "flow" in current
                      ? {
                          ...current,
                          flow: { ...current.flow, shape: nextValue as typeof current.flow.shape },
                        }
                      : current,
                  )
                }
              options={[
                { value: "horizontal", label: "Horizontal" },
                { value: "vertical", label: "Vertical" },
                { value: "elbow_right_down", label: "Elbow right then down" },
                { value: "elbow_right_up", label: "Elbow right then up" },
                { value: "elbow_left_down", label: "Elbow left then down" },
                { value: "elbow_left_up", label: "Elbow left then up" },
                { value: "elbow_down_right", label: "Elbow down then right" },
                { value: "elbow_down_left", label: "Elbow down then left" },
                { value: "elbow_up_right", label: "Elbow up then right" },
                { value: "elbow_up_left", label: "Elbow up then left" },
              ]}
              ariaLabel="Flow line shape"
              triggerClassName="min-h-10 py-ds-2"
            />
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Direction</span>
              <ThemedSelect
                value={element.flow.direction}
                disabled={!canEditDashboard}
                onChange={(nextValue) =>
                  onUpdateElement((current) =>
                    "flow" in current
                      ? {
                          ...current,
                          flow: { ...current.flow, direction: nextValue as typeof current.flow.direction },
                        }
                      : current,
                  )
                }
                options={[
                  { value: "forward", label: "Forward" },
                  { value: "reverse", label: "Reverse" },
                ]}
                ariaLabel="Flow line direction"
                triggerClassName="min-h-10 py-ds-2"
              />
            </label>
            {(
              [
                { field: "lineWidth", label: "Line width", min: 4, max: 48 },
                { field: "speed", label: "Flow speed", min: 4, max: 240 },
                { field: "stripeLength", label: "Stripe length", min: 4, max: 64 },
                { field: "stripeGap", label: "Stripe gap", min: 2, max: 64 },
              ] as const
            ).map((option) => (
              <label key={option.field} className="flex flex-col gap-ds-1">
                <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">{option.label}</span>
                <input
                  className="ui-control py-ds-2"
                  type="number"
                  min={option.min}
                  max={option.max}
                  value={element.flow[option.field]}
                  disabled={!canEditDashboard}
                  onChange={(event) =>
                    onUpdateElement((current) =>
                      "flow" in current
                        ? {
                            ...current,
                            flow: {
                              ...current.flow,
                              [option.field]: Number(event.target.value),
                            },
                          }
                        : current,
                    )
                  }
                />
              </label>
            ))}
            {element.flow.shape !== "horizontal" && element.flow.shape !== "vertical" ? (
              <label className="col-span-2 flex flex-col gap-ds-1">
                <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">
                  Turn position ({Math.round(element.flow.turnRatio * 100)}%)
                </span>
                <input
                  className="accent-[color:var(--accent)]"
                  type="range"
                  min={15}
                  max={85}
                  step={1}
                  value={Math.round(element.flow.turnRatio * 100)}
                  disabled={!canEditDashboard}
                  onChange={(event) =>
                    onUpdateElement((current) =>
                      "flow" in current
                        ? {
                            ...current,
                            flow: {
                              ...current.flow,
                              turnRatio: Number(event.target.value) / 100,
                            },
                          }
                        : current,
                    )
                  }
                />
                <span className="text-micro text-muted">Move where the line bends before it turns.</span>
              </label>
            ) : null}
            <label className="col-span-2 flex items-center gap-ds-2 text-caption text-foreground">
              <input
                type="checkbox"
                checked={element.flow.animate}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "flow" in current
                      ? {
                          ...current,
                          flow: { ...current.flow, animate: event.target.checked },
                        }
                      : current,
                  )
                }
              />
              Animate flow stripes
            </label>
          </div>
        ) : null}
        {supportsElementTextTone(element) ? (
          <TextToneField
            value={readElementTextColor(element)}
            disabled={!canEditDashboard}
            onChange={updateTextColor}
          />
        ) : null}
        {"text" in element ? (
          <label className="flex flex-col gap-ds-1">
            <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Text</span>
            <textarea
              className="ui-control min-h-[6rem] py-ds-2"
              value={element.text}
              disabled={!canEditDashboard}
              onChange={(event) => onUpdateElement((current) => ("text" in current ? { ...current, text: event.target.value } : current))}
            />
          </label>
        ) : null}
        {"placeholder" in element ? (
          <label className="flex flex-col gap-ds-1">
            <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Placeholder</span>
            <input
              className="ui-control py-ds-2"
              value={element.placeholder}
              disabled={!canEditDashboard}
              onChange={(event) =>
                onUpdateElement((current) => ("placeholder" in current ? { ...current, placeholder: event.target.value } : current))
              }
            />
          </label>
        ) : null}
        {"label" in element ? (
          <label className="flex flex-col gap-ds-1">
            <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Label</span>
            <input
              className="ui-control py-ds-2"
              value={element.label}
              disabled={!canEditDashboard}
              onChange={(event) => onUpdateElement((current) => ("label" in current ? { ...current, label: event.target.value } : current))}
            />
          </label>
        ) : null}
        {"table" in element ? (
          <div className="grid grid-cols-2 gap-ds-3">
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Row limit</span>
              <input
                className="ui-control py-ds-2"
                type="number"
                min={1}
                max={50}
                value={element.table.rowLimit}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) => ("table" in current ? { ...current, table: { ...current.table, rowLimit: Number(event.target.value) } } : current))
                }
              />
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Columns</span>
              <input
                className="ui-control py-ds-2"
                value={element.table.columns.join(", ")}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "table" in current
                      ? {
                          ...current,
                          table: {
                            ...current.table,
                            columns: event.target.value.split(",").map((item) => item.trim()).filter(Boolean).slice(0, 8),
                          },
                        }
                      : current,
                  )
                }
              />
            </label>
          </div>
        ) : null}
        {"chart" in element ? (
          <div className="grid grid-cols-2 gap-ds-3">
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Mode</span>
              <ThemedSelect
                value={element.chart.mode}
                disabled={!canEditDashboard}
                onChange={(nextValue) =>
                  onUpdateElement((current) =>
                    "chart" in current
                      ? {
                          ...current,
                          chart: { ...current.chart, mode: nextValue as typeof current.chart.mode },
                        }
                      : current,
                  )
                }
                options={[
                  { value: "compact", label: "Compact" },
                  { value: "standard", label: "Standard" },
                  { value: "area", label: "Area" },
                ]}
                ariaLabel="Chart mode"
                triggerClassName="min-h-10 py-ds-2"
              />
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Line width</span>
              <input
                className="ui-control py-ds-2"
                type="number"
                min={1}
                max={8}
                value={element.chart.lineWidth}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "chart" in current
                      ? {
                          ...current,
                          chart: { ...current.chart, lineWidth: Number(event.target.value) },
                        }
                      : current,
                  )
                }
              />
            </label>
            <label className="flex items-center gap-ds-2 text-caption text-foreground">
              <input
                type="checkbox"
                checked={element.chart.showLegend}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "chart" in current
                      ? {
                          ...current,
                          chart: { ...current.chart, showLegend: event.target.checked },
                        }
                      : current,
                  )
                }
              />
              Show legend
            </label>
            <label className="flex items-center gap-ds-2 text-caption text-foreground">
              <input
                type="checkbox"
                checked={element.chart.showAxes}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "chart" in current
                      ? {
                          ...current,
                          chart: { ...current.chart, showAxes: event.target.checked },
                        }
                      : current,
                  )
                }
              />
              Show axes
            </label>
            <label className="flex items-center gap-ds-2 text-caption text-foreground">
              <input
                type="checkbox"
                checked={element.chart.showGrid}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "chart" in current
                      ? {
                          ...current,
                          chart: { ...current.chart, showGrid: event.target.checked },
                        }
                      : current,
                  )
                }
              />
              Show grid
            </label>
          </div>
        ) : null}
        {"gauge" in element ? (
          <div className="grid grid-cols-2 gap-ds-3">
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Min</span>
              <input
                className="ui-control py-ds-2"
                type="number"
                value={element.gauge.min}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "gauge" in current ? { ...current, gauge: { ...current.gauge, min: Number(event.target.value) } } : current,
                  )
                }
              />
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Warning low</span>
              <input
                className="ui-control py-ds-2"
                type="number"
                value={element.gauge.warningLow}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "gauge" in current ? { ...current, gauge: { ...current.gauge, warningLow: Number(event.target.value) } } : current,
                  )
                }
              />
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Warning high</span>
              <input
                className="ui-control py-ds-2"
                type="number"
                value={element.gauge.warningHigh}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "gauge" in current ? { ...current, gauge: { ...current.gauge, warningHigh: Number(event.target.value) } } : current,
                  )
                }
              />
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Max</span>
              <input
                className="ui-control py-ds-2"
                type="number"
                value={element.gauge.max}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "gauge" in current ? { ...current, gauge: { ...current.gauge, max: Number(event.target.value) } } : current,
                  )
                }
              />
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Unit</span>
              <input
                className="ui-control py-ds-2"
                value={element.gauge.unit ?? ""}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "gauge" in current
                      ? { ...current, gauge: { ...current.gauge, unit: event.target.value || undefined } }
                      : current,
                  )
                }
              />
            </label>
            <label className="flex items-center gap-ds-2 text-caption text-foreground">
              <input
                type="checkbox"
                checked={element.gauge.showTicks}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "gauge" in current ? { ...current, gauge: { ...current.gauge, showTicks: event.target.checked } } : current,
                  )
                }
              />
              Show ticks
            </label>
          </div>
        ) : null}
        {"symbol" in element ? (
          <div className="rounded-2xl border border-border/60 bg-background/55 p-ds-3">
            <p className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Symbol definition</p>
            <p className="mt-1 text-caption font-semibold text-foreground">
              {selectedDefinition?.title ?? element.symbol.definitionId}
            </p>
            <p className="mt-1 text-micro text-muted">Bind status telemetry to drive running, open, level, or alarm state.</p>
            {selectedDefinition ? (
              <>
                <p className="mt-2 text-micro text-muted">
                  Instance version {element.symbol.definitionVersion} of latest definition {selectedDefinition.version}
                </p>
                {canEditDashboard && element.symbol.definitionVersion !== selectedDefinition.version ? (
                  <div className="mt-ds-3 flex flex-wrap gap-ds-2">
                    <Button type="button" variant="secondary" size="sm" onClick={() => onUpgradeSymbolInstances(element.symbol.definitionId, "selected")}>
                      Upgrade selected
                    </Button>
                    <Button type="button" variant="secondary" size="sm" onClick={() => onUpgradeSymbolInstances(element.symbol.definitionId, "all")}>
                      Upgrade all
                    </Button>
                  </div>
                ) : null}
              </>
            ) : null}
            {selectedDefinition?.builtIn ? (
              <div className="mt-ds-3 space-y-ds-3 rounded-2xl border border-border/50 bg-card/70 p-ds-3">
                <p className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Preview behavior</p>
                {selectedDefinition.id === "builtin.status.lamp" ? (
                  <label className="flex items-center gap-ds-2 text-caption text-foreground">
                    <input
                      type="checkbox"
                      checked={readSymbolOverrideBoolean(element.symbol.overrides as Record<string, unknown>, "alarm") ?? false}
                      disabled={!canEditDashboard}
                      onChange={(event) =>
                        onUpdateElement((current) =>
                          current.type === "symbol_instance"
                            ? {
                                ...current,
                                symbol: {
                                  ...current.symbol,
                                  overrides: {
                                    ...current.symbol.overrides,
                                    alarm: event.target.checked,
                                  },
                                },
                              }
                            : current,
                        )
                      }
                    />
                    Alarm state
                  </label>
                ) : null}
                {selectedDefinition.id === "builtin.process.tank" ? (
                  <label className="flex flex-col gap-ds-1">
                    <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Fill percent</span>
                    <input
                      className="ui-control py-ds-2"
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={readSymbolOverrideNumber(element.symbol.overrides as Record<string, unknown>, "levelPercent") ?? 62}
                      disabled={!canEditDashboard}
                      onChange={(event) =>
                        onUpdateElement((current) =>
                          current.type === "symbol_instance"
                            ? {
                                ...current,
                                symbol: {
                                  ...current.symbol,
                                  overrides: {
                                    ...current.symbol.overrides,
                                    levelPercent: Number(event.target.value),
                                  },
                                },
                              }
                            : current,
                        )
                      }
                    />
                    <span className="text-micro text-muted">
                      {Math.round(readSymbolOverrideNumber(element.symbol.overrides as Record<string, unknown>, "levelPercent") ?? 62)}%
                    </span>
                  </label>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {canEditDashboard && canCreateCustomSymbol(element) ? (
          <div className="space-y-ds-3 rounded-2xl border border-border/60 bg-card/40 p-ds-4">
            <div>
              <p className="text-caption font-semibold text-foreground">Custom symbol</p>
              <p className="mt-1 text-micro text-muted">Convert this primitive element into a reusable workspace symbol.</p>
            </div>
            <div className="flex flex-wrap gap-ds-2">
              <Button type="button" variant="secondary" size="sm" onClick={() => onSaveAsSymbol(null)}>
                Save as new symbol
              </Button>
              {document.symbols.filter((symbol) => !symbol.builtIn).length ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    void (async () => {
                      const symbolId = await dialogs.input({
                        title: "Update existing symbol",
                        description: "Enter the custom symbol id you want to replace with this element.",
                        label: "Custom symbol id",
                        initialValue: document.symbols.find((symbol) => !symbol.builtIn)?.id ?? "",
                        confirmLabel: "Update symbol",
                      });
                      if (!symbolId) {
                        return;
                      }
                      onSaveAsSymbol(symbolId);
                    })();
                  }}
                >
                  Update existing symbol
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
        {element.type === "svg_asset" ? (
          <div className="space-y-ds-3 rounded-2xl border border-border/60 bg-card/40 p-ds-4">
            <div>
              <p className="text-caption font-semibold text-foreground">SVG asset</p>
              <p className="mt-1 text-micro text-muted">Choose a curated asset or import a sanitized inline SVG into this document.</p>
            </div>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Asset</span>
              <ThemedSelect
                value={element.asset.assetId}
                disabled={!canEditDashboard}
                onChange={(nextValue) =>
                  onUpdateElement((current) =>
                    current.type === "svg_asset"
                      ? { ...current, asset: { ...current.asset, assetId: nextValue } }
                      : current,
                  )
                }
                options={document.assets.filter((asset) => asset.kind === "svg").map((asset) => ({
                  value: asset.id,
                  label: asset.title,
                }))}
                ariaLabel="SVG asset"
                triggerClassName="min-h-10 py-ds-2"
              />
            </label>
            {canEditDashboard ? (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  void (async () => {
                    const title = await dialogs.input({
                      title: "Import SVG",
                      description: "Add a title for this SVG asset.",
                      label: "SVG asset title",
                      initialValue: "Imported SVG",
                      confirmLabel: "Continue",
                    });
                    if (!title) {
                      return;
                    }
                    const rawMarkup = await dialogs.input({
                      title: "Import SVG markup",
                      description: "Paste the full SVG markup to import it into this dashboard.",
                      label: "SVG markup",
                      placeholder: "<svg ...>...</svg>",
                      multiline: true,
                      confirmLabel: "Import SVG",
                    });
                    if (!rawMarkup) {
                      return;
                    }
                    try {
                      const sanitized = sanitizeInlineSvg(rawMarkup);
                      const assetId = createUuidLikeId();
                      onUpdateDocument((current) => ({
                        ...current,
                        assets: [
                          ...current.assets,
                          {
                            id: assetId,
                            kind: "svg",
                            title,
                            source: "imported",
                            content: sanitized.content,
                            viewBox: sanitized.viewBox,
                          },
                        ],
                      }));
                      onUpdateElement((current) =>
                        current.type === "svg_asset"
                          ? { ...current, asset: { ...current.asset, assetId } }
                          : current,
                      );
                    } catch (error) {
                      await dialogs.alert({
                        title: "Could not import SVG",
                        description: error instanceof Error ? error.message : "Could not import the SVG.",
                        tone: "danger",
                      });
                    }
                  })();
                }}
              >
                Import SVG
              </button>
            ) : null}
          </div>
        ) : null}
        {element.type === "image_asset" ? (
          <div className="space-y-ds-3 rounded-2xl border border-border/60 bg-card/40 p-ds-4">
            <div>
              <p className="text-caption font-semibold text-foreground">Image asset</p>
              <p className="mt-1 text-micro text-muted">Upload a bitmap image into this dashboard and place it on the canvas.</p>
            </div>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Asset</span>
              <ThemedSelect
                value={element.asset.assetId ?? ""}
                disabled={!canEditDashboard}
                onChange={(nextValue) =>
                  onUpdateElement((current) =>
                    current.type === "image_asset"
                      ? { ...current, asset: { ...current.asset, assetId: nextValue || undefined } }
                      : current,
                  )
                }
                options={[
                  { value: "", label: "No image selected" },
                  ...imageAssets.map((asset) => ({
                    value: asset.id,
                    label: asset.title,
                  })),
                ]}
                ariaLabel="Image asset"
                triggerClassName="min-h-10 py-ds-2"
              />
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Fit</span>
              <ThemedSelect
                value={element.asset.fit}
                disabled={!canEditDashboard}
                onChange={(nextValue) =>
                  onUpdateElement((current) =>
                    current.type === "image_asset"
                      ? { ...current, asset: { ...current.asset, fit: nextValue as typeof current.asset.fit } }
                      : current,
                  )
                }
                options={[
                  { value: "contain", label: "Contain" },
                  { value: "cover", label: "Cover" },
                  { value: "fill", label: "Stretch" },
                ]}
                ariaLabel="Image fit"
                triggerClassName="min-h-10 py-ds-2"
              />
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Alt text</span>
              <input
                className="ui-control py-ds-2"
                value={element.asset.alt ?? ""}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    current.type === "image_asset"
                      ? { ...current, asset: { ...current.asset, alt: event.target.value || undefined } }
                      : current,
                  )
                }
              />
            </label>
            {canEditDashboard ? (
              <label className="btn btn-secondary btn-sm w-fit cursor-pointer">
                {imageImporting ? "Importing..." : "Upload image"}
                <input
                  className="sr-only"
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  disabled={imageImporting}
                  onChange={(event) => {
                    void importImageAsset(event.target.files?.[0]);
                    event.currentTarget.value = "";
                  }}
                />
              </label>
            ) : null}
          </div>
        ) : null}
        {"command" in element ? (
          <div className="space-y-ds-3 rounded-2xl border border-border/60 bg-card/40 p-ds-4">
            <div>
              <p className="text-caption font-semibold text-foreground">Command action</p>
              <p className="mt-1 text-micro text-muted">Configure the target device, action name, confirmation prompt, and payload.</p>
            </div>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Device</span>
              <ThemedSelect
                value={element.command.deviceId ?? ""}
                disabled={!canEditDashboard}
                onChange={(nextValue) =>
                  onUpdateElement((current) =>
                    "command" in current
                      ? { ...current, command: { ...current.command, deviceId: nextValue || undefined } }
                      : current,
                  )
                }
                options={[
                  { value: "", label: "Select a device" },
                  ...commandDevices.map((device) => ({
                    value: device.id,
                    label: device.name?.trim() || device.device_uid,
                    description: device.device_uid,
                  })),
                ]}
                ariaLabel="Command device"
                searchable
                searchPlaceholder="Search devices"
                triggerClassName="min-h-10 py-ds-2"
              />
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Button label</span>
              <input
                className="ui-control py-ds-2"
                value={element.command.label}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "command" in current
                      ? { ...current, command: { ...current.command, label: event.target.value } }
                      : current,
                  )
                }
              />
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Action</span>
              <input
                className="ui-control py-ds-2"
                value={element.command.action}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "command" in current
                      ? { ...current, command: { ...current.command, action: event.target.value } }
                      : current,
                  )
                }
              />
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Payload JSON</span>
              <textarea
                className="ui-control min-h-[7rem] py-ds-2"
                value={JSON.stringify(element.command.payload, null, 2)}
                disabled={!canEditDashboard}
                onChange={(event) => {
                  try {
                    const parsed = JSON.parse(event.target.value) as Record<string, unknown>;
                    onUpdateElement((current) =>
                      "command" in current
                        ? { ...current, command: { ...current.command, payload: parsed } }
                        : current,
                    );
                  } catch {
                    // Keep editor forgiving while the user is typing JSON.
                  }
                }}
              />
            </label>
            <div className="grid grid-cols-2 gap-ds-3">
              <label className="flex items-center gap-ds-2 text-caption text-foreground">
                <input
                  type="checkbox"
                  checked={element.command.requireConfirm}
                  disabled={!canEditDashboard}
                  onChange={(event) =>
                    onUpdateElement((current) =>
                      "command" in current
                        ? { ...current, command: { ...current.command, requireConfirm: event.target.checked } }
                        : current,
                    )
                  }
                />
                Require confirmation
              </label>
              <label className="flex flex-col gap-ds-1">
                <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Variant</span>
                <ThemedSelect
                  value={element.command.variant}
                  disabled={!canEditDashboard}
                  onChange={(nextValue) =>
                    onUpdateElement((current) =>
                      "command" in current
                        ? { ...current, command: { ...current.command, variant: nextValue as typeof current.command.variant } }
                        : current,
                    )
                  }
                  options={[
                    { value: "primary", label: "Primary" },
                    { value: "secondary", label: "Secondary" },
                    { value: "danger", label: "Danger" },
                  ]}
                  ariaLabel="Command variant"
                  triggerClassName="min-h-10 py-ds-2"
                />
              </label>
            </div>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Confirmation text</span>
              <input
                className="ui-control py-ds-2"
                value={element.command.confirmText ?? ""}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "command" in current
                      ? { ...current, command: { ...current.command, confirmText: event.target.value || undefined } }
                      : current,
                  )
                }
              />
            </label>
          </div>
        ) : null}
        {"toggle" in element ? (
          <div className="space-y-ds-3 rounded-2xl border border-border/60 bg-card/40 p-ds-4">
            <div>
              <p className="text-caption font-semibold text-foreground">Toggle action</p>
              <p className="mt-1 text-micro text-muted">Bind a status value to visualize state, then configure on/off command payloads.</p>
            </div>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Device</span>
              <ThemedSelect
                value={element.toggle.deviceId ?? ""}
                disabled={!canEditDashboard}
                onChange={(nextValue) =>
                  onUpdateElement((current) =>
                    "toggle" in current
                      ? { ...current, toggle: { ...current.toggle, deviceId: nextValue || undefined } }
                      : current,
                  )
                }
                options={[
                  { value: "", label: "Select a device" },
                  ...commandDevices.map((device) => ({
                    value: device.id,
                    label: device.name?.trim() || device.device_uid,
                    description: device.device_uid,
                  })),
                ]}
                ariaLabel="Toggle device"
                searchable
                searchPlaceholder="Search devices"
                triggerClassName="min-h-10 py-ds-2"
              />
            </label>
            <div className="grid grid-cols-2 gap-ds-3">
              <label className="flex flex-col gap-ds-1">
                <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Label</span>
                <input
                  className="ui-control py-ds-2"
                  value={element.toggle.label}
                  disabled={!canEditDashboard}
                  onChange={(event) =>
                    onUpdateElement((current) =>
                      "toggle" in current ? { ...current, toggle: { ...current.toggle, label: event.target.value } } : current,
                    )
                  }
                />
              </label>
              <label className="flex flex-col gap-ds-1">
                <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Action</span>
                <input
                  className="ui-control py-ds-2"
                  value={element.toggle.action}
                  disabled={!canEditDashboard}
                  onChange={(event) =>
                    onUpdateElement((current) =>
                      "toggle" in current ? { ...current, toggle: { ...current.toggle, action: event.target.value } } : current,
                    )
                  }
                />
              </label>
              <label className="flex flex-col gap-ds-1">
                <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">On label</span>
                <input
                  className="ui-control py-ds-2"
                  value={element.toggle.onLabel}
                  disabled={!canEditDashboard}
                  onChange={(event) =>
                    onUpdateElement((current) =>
                      "toggle" in current ? { ...current, toggle: { ...current.toggle, onLabel: event.target.value } } : current,
                    )
                  }
                />
              </label>
              <label className="flex flex-col gap-ds-1">
                <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Off label</span>
                <input
                  className="ui-control py-ds-2"
                  value={element.toggle.offLabel}
                  disabled={!canEditDashboard}
                  onChange={(event) =>
                    onUpdateElement((current) =>
                      "toggle" in current ? { ...current, toggle: { ...current.toggle, offLabel: event.target.value } } : current,
                    )
                  }
                />
              </label>
            </div>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">On payload JSON</span>
              <textarea
                className="ui-control min-h-[5rem] py-ds-2"
                value={JSON.stringify(element.toggle.onPayload, null, 2)}
                disabled={!canEditDashboard}
                onChange={(event) => {
                  try {
                    const parsed = JSON.parse(event.target.value) as Record<string, unknown>;
                    onUpdateElement((current) =>
                      "toggle" in current ? { ...current, toggle: { ...current.toggle, onPayload: parsed } } : current,
                    );
                  } catch {}
                }}
              />
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Off payload JSON</span>
              <textarea
                className="ui-control min-h-[5rem] py-ds-2"
                value={JSON.stringify(element.toggle.offPayload, null, 2)}
                disabled={!canEditDashboard}
                onChange={(event) => {
                  try {
                    const parsed = JSON.parse(event.target.value) as Record<string, unknown>;
                    onUpdateElement((current) =>
                      "toggle" in current ? { ...current, toggle: { ...current.toggle, offPayload: parsed } } : current,
                    );
                  } catch {}
                }}
              />
            </label>
            <label className="flex items-center gap-ds-2 text-caption text-foreground">
              <input
                type="checkbox"
                checked={element.toggle.requireConfirm}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "toggle" in current ? { ...current, toggle: { ...current.toggle, requireConfirm: event.target.checked } } : current,
                  )
                }
              />
              Require confirmation
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Confirmation text</span>
              <input
                className="ui-control py-ds-2"
                value={element.toggle.confirmText ?? ""}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "toggle" in current ? { ...current, toggle: { ...current.toggle, confirmText: event.target.value || undefined } } : current,
                  )
                }
              />
            </label>
          </div>
        ) : null}
        {"setpoint" in element ? (
          <div className="space-y-ds-3 rounded-2xl border border-border/60 bg-card/40 p-ds-4">
            <div>
              <p className="text-caption font-semibold text-foreground">Setpoint action</p>
              <p className="mt-1 text-micro text-muted">Configure the target device, allowed range, step, and the command action used to submit the new value.</p>
            </div>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Device</span>
              <ThemedSelect
                value={element.setpoint.deviceId ?? ""}
                disabled={!canEditDashboard}
                onChange={(nextValue) =>
                  onUpdateElement((current) =>
                    "setpoint" in current ? { ...current, setpoint: { ...current.setpoint, deviceId: nextValue || undefined } } : current,
                  )
                }
                options={[
                  { value: "", label: "Select a device" },
                  ...commandDevices.map((device) => ({
                    value: device.id,
                    label: device.name?.trim() || device.device_uid,
                    description: device.device_uid,
                  })),
                ]}
                ariaLabel="Setpoint device"
                searchable
                searchPlaceholder="Search devices"
                triggerClassName="min-h-10 py-ds-2"
              />
            </label>
            <div className="grid grid-cols-2 gap-ds-3">
              <label className="flex flex-col gap-ds-1">
                <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Label</span>
                <input
                  className="ui-control py-ds-2"
                  value={element.setpoint.label}
                  disabled={!canEditDashboard}
                  onChange={(event) =>
                    onUpdateElement((current) =>
                      "setpoint" in current ? { ...current, setpoint: { ...current.setpoint, label: event.target.value } } : current,
                    )
                  }
                />
              </label>
              <label className="flex flex-col gap-ds-1">
                <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Action</span>
                <input
                  className="ui-control py-ds-2"
                  value={element.setpoint.action}
                  disabled={!canEditDashboard}
                  onChange={(event) =>
                    onUpdateElement((current) =>
                      "setpoint" in current ? { ...current, setpoint: { ...current.setpoint, action: event.target.value } } : current,
                    )
                  }
                />
              </label>
              {(["defaultValue", "min", "max", "step"] as const).map((field) => (
                <label key={field} className="flex flex-col gap-ds-1">
                  <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">{field}</span>
                  <input
                    className="ui-control py-ds-2"
                    type="number"
                    value={element.setpoint[field]}
                    disabled={!canEditDashboard}
                    onChange={(event) =>
                      onUpdateElement((current) =>
                        "setpoint" in current
                          ? { ...current, setpoint: { ...current.setpoint, [field]: Number(event.target.value) } }
                          : current,
                      )
                    }
                  />
                </label>
              ))}
              <label className="flex flex-col gap-ds-1">
                <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Unit</span>
                <input
                  className="ui-control py-ds-2"
                  value={element.setpoint.unit ?? ""}
                  disabled={!canEditDashboard}
                  onChange={(event) =>
                    onUpdateElement((current) =>
                      "setpoint" in current ? { ...current, setpoint: { ...current.setpoint, unit: event.target.value || undefined } } : current,
                    )
                  }
                />
              </label>
            </div>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Payload JSON</span>
              <textarea
                className="ui-control min-h-[5rem] py-ds-2"
                value={JSON.stringify(element.setpoint.payload, null, 2)}
                disabled={!canEditDashboard}
                onChange={(event) => {
                  try {
                    const parsed = JSON.parse(event.target.value) as Record<string, unknown>;
                    onUpdateElement((current) =>
                      "setpoint" in current ? { ...current, setpoint: { ...current.setpoint, payload: parsed } } : current,
                    );
                  } catch {}
                }}
              />
            </label>
            <label className="flex items-center gap-ds-2 text-caption text-foreground">
              <input
                type="checkbox"
                checked={element.setpoint.requireConfirm}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "setpoint" in current ? { ...current, setpoint: { ...current.setpoint, requireConfirm: event.target.checked } } : current,
                  )
                }
              />
              Require confirmation
            </label>
            <label className="flex flex-col gap-ds-1">
              <span className="text-micro font-medium uppercase tracking-[0.08em] text-muted">Confirmation text</span>
              <input
                className="ui-control py-ds-2"
                value={element.setpoint.confirmText ?? ""}
                disabled={!canEditDashboard}
                onChange={(event) =>
                  onUpdateElement((current) =>
                    "setpoint" in current ? { ...current, setpoint: { ...current.setpoint, confirmText: event.target.value || undefined } } : current,
                  )
                }
              />
            </label>
          </div>
        ) : null}
        <ElementBindingInspector
          tenantId={tenantId}
          allWorkspaces={allWorkspaces}
          element={element}
          bindings={bindings}
          onBindingsChange={onBindingsChange}
        />
        {selectedCommandDeviceId ? (
          <div className="space-y-ds-3 rounded-2xl border border-border/60 bg-background/55 p-ds-4">
            <div>
              <p className="text-caption font-semibold text-foreground">Recent commands</p>
              <p className="mt-1 text-micro text-muted">Latest command audit trail for the selected control device.</p>
            </div>
            {recentCommandsQuery.isLoading ? (
              <p className="text-caption text-muted">Loading commands...</p>
            ) : recentCommandsQuery.isError ? (
              <p className="text-caption text-error">Could not load recent commands.</p>
            ) : (recentCommandsQuery.data?.length ?? 0) === 0 ? (
              <p className="text-caption text-muted">No commands yet for this device.</p>
            ) : (
              <div className="space-y-ds-2">
                {recentCommandsQuery.data?.map((command) => (
                  <div key={command.id} className="rounded-xl border border-border/50 bg-card/35 px-ds-3 py-ds-2">
                    <div className="flex items-center justify-between gap-ds-2">
                      <span className="text-caption font-medium text-foreground">{command.status ?? "pending"}</span>
                      <span className="text-micro text-muted">{command.created_at ? new Date(command.created_at).toLocaleString() : ""}</span>
                    </div>
                    <p className="mt-1 truncate text-micro text-muted">{command.id}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ModernColorField({
  label,
  description,
  value,
  disabled,
  onChange,
  fallbackColor = "#f6fbf7",
  placeholder = "Default surface",
}: {
  label: string;
  description: string;
  value: string | null;
  disabled: boolean;
  onChange: (value: string | null) => void;
  fallbackColor?: string;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState(value ?? "");
  const pickerValue = normalizeHexColor(value ?? "") ?? fallbackColor;

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  const commitDraft = () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setDraft("");
      onChange(null);
      return;
    }
    const normalized = normalizeHexColor(trimmed);
    if (normalized) {
      setDraft(normalized);
      onChange(normalized);
      return;
    }
    setDraft(value ?? "");
  };

  return (
    <div className="space-y-ds-2 rounded-2xl border border-border/60 bg-card/40 p-ds-3">
      <div className="flex items-start justify-between gap-ds-3">
        <div className="min-w-0">
          <p className="text-caption font-semibold text-foreground">{label}</p>
          <p className="mt-1 text-micro text-muted">{description}</p>
        </div>
        <label
          className={`relative block h-10 w-10 shrink-0 overflow-hidden rounded-xl border border-border/60 bg-card shadow-[0_14px_24px_-22px_rgba(15,23,42,0.5)] ${
            disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
          }`}
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(45deg, rgba(148,163,184,0.14) 25%, transparent 25%, transparent 75%, rgba(148,163,184,0.14) 75%, rgba(148,163,184,0.14)), linear-gradient(45deg, rgba(148,163,184,0.14) 25%, transparent 25%, transparent 75%, rgba(148,163,184,0.14) 75%, rgba(148,163,184,0.14))",
              backgroundPosition: "0 0, 6px 6px",
              backgroundSize: "12px 12px",
            }}
          />
          <div className="absolute inset-[4px] rounded-[10px] border border-white/70" style={{ backgroundColor: value ?? "transparent" }} />
          <input
            type="color"
            className="absolute inset-0 cursor-pointer opacity-0"
            value={pickerValue}
            disabled={disabled}
            onChange={(event) => {
              setDraft(event.target.value);
              onChange(event.target.value);
            }}
          />
        </label>
      </div>
      <div className="grid grid-cols-6 gap-1.5">
        {HMI_COLOR_SWATCHES.map((swatch) => {
          const active = value?.toLowerCase() === swatch.toLowerCase();
          return (
            <button
              key={swatch}
              type="button"
              disabled={disabled}
              className={`h-7 w-full rounded-lg border transition ${
                active
                  ? "border-[color:var(--accent)] shadow-[0_0_0_2px_rgba(44,95,78,0.18)]"
                  : "border-border/50 hover:border-border"
              } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
              style={{ backgroundColor: swatch }}
              onClick={() => {
                setDraft(swatch);
                onChange(swatch);
              }}
              aria-label={`Use color ${swatch}`}
              title={swatch}
            />
          );
        })}
      </div>
      <div className="flex items-center gap-1.5">
        <input
          className="ui-control min-w-0 flex-1 py-2"
          value={draft}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commitDraft();
            }
          }}
        />
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={disabled && !value}
          onClick={() => {
            setDraft("");
            onChange(null);
          }}
        >
          Default
        </Button>
      </div>
    </div>
  );
}

function TextToneField({
  value,
  disabled,
  onChange,
}: {
  value: "#0f172a" | "#ffffff" | null;
  disabled: boolean;
  onChange: (value: "#0f172a" | "#ffffff" | null) => void;
}) {
  const activeValue = value ?? "#0f172a";

  return (
    <div className="space-y-ds-3 rounded-2xl border border-border/60 bg-card/40 p-ds-4">
      <div>
        <p className="text-caption font-semibold text-foreground">Text color</p>
        <p className="mt-1 text-micro text-muted">Choose a simple light or dark text tone for readability.</p>
      </div>
      <div className="grid grid-cols-2 gap-ds-2">
        {[
          { label: "Black", value: "#0f172a" as const },
          { label: "White", value: "#ffffff" as const },
        ].map((option) => {
          const active = activeValue === option.value;
          return (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              className={`flex items-center justify-between rounded-2xl border px-ds-3 py-ds-3 text-left transition ${
                active
                  ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 shadow-[0_12px_24px_-20px_rgba(15,23,42,0.35)]"
                  : "border-border/60 bg-card/75 hover:border-border hover:bg-card"
              } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
              onClick={() => onChange(option.value === "#0f172a" ? null : option.value)}
            >
              <span className="text-caption font-medium text-foreground">{option.label}</span>
              <span
                className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-border/60"
                style={{ backgroundColor: option.value }}
                aria-hidden
              >
                {option.value === "#ffffff" ? <span className="h-2 w-2 rounded-full bg-slate-400/70" /> : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SymbolLibraryPanel({
  onAddSymbol,
  canEditDashboard,
  runtimeMode,
  customSymbols,
  compact = false,
}: {
  onAddSymbol: (definitionId: string) => void;
  canEditDashboard: boolean;
  runtimeMode: boolean;
  customSymbols: HmiSymbolDefinition[];
  compact?: boolean;
}) {
  return (
    <Card className={compact ? "border-border/60 bg-card/45 p-ds-3 shadow-none" : "m-ds-4 mb-0 border-border/60 bg-card/45 p-ds-4"}>
      <div className="flex items-center gap-ds-2">
        <Wind className="h-4 w-4 text-muted" aria-hidden />
        <p className="text-caption font-semibold text-foreground">Built-in symbols</p>
      </div>
      <div className={`mt-ds-3 grid gap-ds-3 ${compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 xl:grid-cols-4"}`}>
        {builtinHmiSymbols.map((symbol) => (
          <div key={symbol.id} className="rounded-2xl border border-border/50 bg-background/55 p-ds-3">
            <div className="mb-ds-3">
              <BuiltinSymbolPreview
                symbolId={symbol.id}
                stateOn={symbol.id !== "builtin.process.pipe"}
                inAlarm={symbol.id === "builtin.status.lamp"}
                normalizedLevel={0.62}
                compact
              />
            </div>
            <div className="flex items-center justify-between gap-ds-2">
              <div>
                <p className="text-caption font-medium text-foreground">{symbol.title}</p>
                <p className="text-micro uppercase tracking-[0.08em] text-muted">{symbol.category}</p>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={runtimeMode || !canEditDashboard}
                onClick={() => onAddSymbol(symbol.id)}
              >
                Add
              </Button>
            </div>
            <p className="mt-ds-2 text-micro text-muted">{symbol.metadata?.description ?? "Industrial symbol."}</p>
          </div>
        ))}
      </div>
      {customSymbols.length ? (
        <>
          <div className="mt-ds-5 flex items-center gap-ds-2">
            <BadgeCheck className="h-4 w-4 text-muted" aria-hidden />
            <p className="text-caption font-semibold text-foreground">Workspace symbols</p>
          </div>
          <div className={`mt-ds-3 grid gap-ds-3 ${compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 xl:grid-cols-4"}`}>
            {customSymbols.map((symbol) => (
              <div key={symbol.id} className="rounded-2xl border border-border/50 bg-background/55 p-ds-3">
                <div className="flex items-center justify-between gap-ds-2">
                  <div>
                    <p className="text-caption font-medium text-foreground">{symbol.title}</p>
                    <p className="text-micro uppercase tracking-[0.08em] text-muted">
                      {symbol.category} - v{symbol.version}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={runtimeMode || !canEditDashboard}
                    onClick={() => onAddSymbol(symbol.id)}
                  >
                    Add
                  </Button>
                </div>
                <p className="mt-ds-2 text-micro text-muted">{symbol.metadata?.description ?? "Custom workspace symbol."}</p>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </Card>
  );
}

function TemplateGalleryPanel({
  canEditDashboard,
  runtimeMode,
  onApplyTemplate,
  compact = false,
}: {
  canEditDashboard: boolean;
  runtimeMode: boolean;
  onApplyTemplate: (templateId: string) => void;
  compact?: boolean;
}) {
  return (
    <Card className={compact ? "border-border/60 bg-card/45 p-ds-3 shadow-none" : "m-ds-4 mt-0 border-border/60 bg-card/45 p-ds-4"}>
      <div className="flex items-center gap-ds-2">
        <BadgeCheck className="h-4 w-4 text-muted" aria-hidden />
        <p className="text-caption font-semibold text-foreground">Template gallery</p>
      </div>
      <div className={`mt-ds-3 grid gap-ds-3 ${compact ? "grid-cols-1" : "grid-cols-1 xl:grid-cols-3"}`}>
        {hmiTemplates.map((template) => (
          <div key={template.id} className="rounded-2xl border border-border/50 bg-background/55 p-ds-3">
            <div className="flex items-center justify-between gap-ds-2">
              <div>
                <p className="text-caption font-medium text-foreground">{template.title}</p>
                <p className="text-micro uppercase tracking-[0.08em] text-muted">{template.category}</p>
              </div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={runtimeMode || !canEditDashboard}
                onClick={() => onApplyTemplate(template.id)}
              >
                Apply
              </Button>
            </div>
            <p className="mt-ds-2 text-micro text-muted">{template.description}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

function BuiltinSymbolPreview({
  symbolId,
  stateOn = true,
  animate = true,
  inAlarm = false,
  normalizedLevel = 0.62,
  compact = false,
}: {
  symbolId: string;
  stateOn?: boolean;
  animate?: boolean;
  inAlarm?: boolean;
  normalizedLevel?: number;
  compact?: boolean;
}) {
  const shellClass = compact
    ? "relative h-24 w-full overflow-hidden rounded-[18px] border border-emerald-900/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(241,247,243,0.92)_100%)]"
    : "relative flex h-full w-full items-center justify-center overflow-hidden rounded-[22px] border border-emerald-900/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(241,247,243,0.92)_100%)]";
  const level = Math.max(0, Math.min(1, normalizedLevel));

  if (symbolId === "builtin.hvac.fan") {
    return (
      <div className={shellClass}>
        <div className={`absolute inset-[18%] rounded-full bg-emerald-200/55 blur-xl transition ${stateOn ? "opacity-90" : "opacity-25"}`} />
        <svg className="relative mx-auto my-auto h-[78%] w-[78%]" viewBox="0 0 180 180">
          <circle cx="90" cy="90" r="74" fill="url(#fanBody)" stroke="#1f4f3f" strokeWidth="8" />
          <g className={stateOn && animate ? "animate-spin origin-center" : ""} style={{ transformOrigin: "90px 90px" }}>
            <path d="M90 90 C88 74, 92 56, 106 42 C120 32, 128 36, 130 50 C127 66, 117 78, 101 88 C96 91, 92 91, 90 90 Z" fill="#1f7a4a" />
            <path d="M90 90 C88 74, 92 56, 106 42 C120 32, 128 36, 130 50 C127 66, 117 78, 101 88 C96 91, 92 91, 90 90 Z" fill="#1f7a4a" transform="rotate(90 90 90)" />
            <path d="M90 90 C88 74, 92 56, 106 42 C120 32, 128 36, 130 50 C127 66, 117 78, 101 88 C96 91, 92 91, 90 90 Z" fill="#1f7a4a" transform="rotate(180 90 90)" />
            <path d="M90 90 C88 74, 92 56, 106 42 C120 32, 128 36, 130 50 C127 66, 117 78, 101 88 C96 91, 92 91, 90 90 Z" fill="#1f7a4a" transform="rotate(270 90 90)" />
          </g>
          <circle cx="90" cy="90" r="20" fill="#ffffff" stroke="#2f7f59" strokeWidth="6" />
          <circle cx="90" cy="90" r="10" fill="#dfeee5" stroke="#2b624b" strokeWidth="3" />
          <defs>
            <radialGradient id="fanBody" cx="30%" cy="28%" r="80%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="68%" stopColor="#f2f8f4" />
              <stop offset="100%" stopColor="#e1ece5" />
            </radialGradient>
          </defs>
        </svg>
      </div>
    );
  }

  if (symbolId === "builtin.process.pump") {
    return (
      <div className={shellClass}>
        <svg className="relative mx-auto my-auto h-[76%] w-[88%]" viewBox="0 0 220 150">
          <rect x="18" y="92" width="24" height="30" rx="2" fill="#ffffff" stroke="#1f4f3f" strokeWidth="5" />
          <rect x="178" y="92" width="24" height="30" rx="2" fill="#ffffff" stroke="#1f4f3f" strokeWidth="5" />
          <line x1="42" y1="102" x2="178" y2="102" stroke="#1f4f3f" strokeWidth="5" strokeLinecap="round" />
          <line x1="42" y1="112" x2="178" y2="112" stroke="#1f4f3f" strokeWidth="5" strokeLinecap="round" />
          <circle cx="110" cy="70" r="42" fill="rgba(255,255,255,0.98)" />
          <path
            d="M86 102 A40 40 0 0 1 110 30 A40 40 0 0 1 134 102"
            fill="none"
            stroke="#1f4f3f"
            strokeWidth="6"
            strokeLinecap="round"
          />
          <circle cx="110" cy="70" r="23" fill="none" stroke="#1f4f3f" strokeWidth="4" />
          <g className={stateOn && animate ? "animate-spin origin-center" : ""} style={{ transformOrigin: "110px 70px" }}>
            {[...Array(12)].map((_, index) => {
              const angle = index * 30;
              return (
                <line
                  key={angle}
                  x1="110"
                  y1="38"
                  x2="110"
                  y2="49"
                  stroke={stateOn ? "#1f4f3f" : "#7f988b"}
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  transform={`rotate(${angle} 110 70)`}
                />
              );
            })}
            <circle cx="110" cy="70" r="13" fill={stateOn ? "#2a7d58" : "#a9beb3"} stroke="#1f4f3f" strokeWidth="3" />
          </g>
        </svg>
      </div>
    );
  }

  if (symbolId === "builtin.process.valve") {
    return (
      <div className={shellClass}>
        <svg className="h-full w-full" viewBox="0 0 160 120">
          <defs>
            <linearGradient id="valve-shell" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#edf4f0" />
            </linearGradient>
          </defs>
          <line x1="12" y1="60" x2="148" y2="60" stroke="#2f5d4d" strokeWidth="8" strokeLinecap="round" />
          <path d="M50 30 L80 60 L50 90 Z M110 30 L80 60 L110 90 Z" fill={stateOn ? "#b9dfc6" : "url(#valve-shell)"} stroke="#2f5d4d" strokeWidth="4" />
          <circle cx="80" cy="22" r="9" fill={stateOn ? "#2f8a5d" : "#c7d4cd"} stroke="#2f5d4d" strokeWidth="3" className={stateOn && animate ? "animate-pulse" : ""} />
        </svg>
      </div>
    );
  }

  if (symbolId === "builtin.hvac.damper") {
    const angle = stateOn ? 12 : 58;
    return (
      <div className={shellClass}>
        <svg className="h-full w-full" viewBox="0 0 180 100">
          <rect x="8" y="18" width="164" height="64" rx="12" fill="#eef4f1" stroke="#2f5d4d" strokeWidth="4" />
          <rect x="18" y="30" width="144" height="12" rx="6" fill="#dce8e1" />
          <rect x="18" y="58" width="144" height="12" rx="6" fill="#dce8e1" />
          <g transform={`rotate(${angle} 90 50)`}>
            <line x1="46" y1="50" x2="134" y2="50" stroke="#1f7a4a" strokeWidth="8" strokeLinecap="round" />
          </g>
          <circle cx="90" cy="50" r="5" fill="#24563f" />
        </svg>
      </div>
    );
  }

  if (symbolId === "builtin.process.tank") {
    return (
      <div className={shellClass}>
        <svg className="h-full w-full" viewBox="0 0 180 240">
          <defs>
            <linearGradient id="tank-shell" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#eef5f1" />
            </linearGradient>
            <linearGradient id="tank-fill" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#9fe0b7" />
              <stop offset="100%" stopColor="#67b98a" />
            </linearGradient>
          </defs>
          <rect x="32" y="18" width="116" height="204" rx="20" fill="url(#tank-shell)" stroke="#2c5a49" strokeWidth="4" />
          <rect x="36" y={26 + (1 - level) * 188} width="108" height={level * 188} rx="16" fill="url(#tank-fill)" opacity="0.95" />
          <ellipse cx="90" cy={30 + (1 - level) * 184} rx="50" ry="7" fill="#d6f1df" opacity="0.72">
            <animate attributeName="ry" values="6;8;6" dur="2.8s" repeatCount="indefinite" />
          </ellipse>
          <rect x="48" y="38" width="8" height="164" rx="4" fill="#d7e5de" />
        </svg>
      </div>
    );
  }

  if (symbolId === "builtin.process.pipe") {
    return (
      <div className={shellClass}>
        <div className="mx-auto my-auto h-3 w-[88%] rounded-full bg-[linear-gradient(180deg,#628f7d_0%,#4f7f6c_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.22)]" />
      </div>
    );
  }

  if (symbolId === "builtin.hvac.duct") {
    return (
      <div className={shellClass}>
        <div className="mx-auto my-auto h-[64%] w-[88%] rounded-[14px] border-4 border-[#49695b] bg-[linear-gradient(180deg,#edf4f0_0%,#d7e4dd_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]" />
      </div>
    );
  }

  if (symbolId === "builtin.status.lamp") {
    const lampColor = inAlarm ? "#dc2626" : stateOn ? "#16a34a" : "#c6d2cb";
    const lampTone = animate && (inAlarm || stateOn) ? "animate-pulse" : "";
    return (
      <div className={shellClass}>
        <div className="absolute inset-[24%] rounded-full bg-card/55 blur-xl" />
        <div
          className={`relative mx-auto my-auto h-[62%] w-[62%] rounded-full border-4 border-[#355b4a] shadow-[inset_0_0_24px_rgba(255,255,255,0.45)] ${lampTone}`}
          style={{ backgroundColor: lampColor, boxShadow: inAlarm ? "0 0 26px rgba(220,38,38,0.45)" : stateOn ? "0 0 24px rgba(22,163,74,0.32)" : undefined }}
        />
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <div className="mx-auto my-auto h-10 w-10 rounded-full bg-emerald-100" />
    </div>
  );
}

function LayerPanel({
  document,
  selectedElementId,
  canEditDashboard,
  runtimeMode,
  onSelect,
  onToggleLock,
  onReorder,
  compact = false,
}: {
  document: HmiCanvasDocument;
  selectedElementId: string | null;
  canEditDashboard: boolean;
  runtimeMode: boolean;
  onSelect: (elementId: string) => void;
  onToggleLock: (elementId: string) => void;
  onReorder: (direction: "forward" | "backward") => void;
  compact?: boolean;
}) {
  const ordered = document.elements.slice().sort((a, b) => b.zIndex - a.zIndex);

  return (
    <Card className={compact ? "border-border/60 bg-card/45 p-ds-3 shadow-none" : "m-ds-4 mt-0 border-border/60 bg-card/45 p-ds-4"}>
      <div className="flex items-center gap-ds-2">
        <Layers3 className="h-4 w-4 text-muted" aria-hidden />
        <p className="text-caption font-semibold text-foreground">Layers</p>
      </div>
      {ordered.length === 0 ? (
        <p className="mt-ds-2 text-caption text-muted">No elements on the canvas yet.</p>
      ) : (
        <div className="mt-ds-3 space-y-ds-2">
          {ordered.map((element) => {
            const symbolTitle =
              element.type === "symbol_instance"
                ? getBuiltinSymbolById(element.symbol.definitionId)?.title ?? "Symbol"
                : null;
            const isSelected = selectedElementId === element.id;
            return (
              <div
                key={element.id}
                className={`flex items-center justify-between gap-ds-3 rounded-xl border px-ds-3 py-ds-2 transition ${
                  isSelected ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10" : "border-border/50 bg-background/50"
                }`}
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => onSelect(element.id)}
                >
                  <p className="truncate text-caption font-medium text-foreground">{element.name}</p>
                  <p className="truncate text-micro text-muted">
                    {symbolTitle ? `${symbolTitle} symbol` : formatElementTitle(element.type)} - z{element.zIndex}
                  </p>
                </button>
                <div className="flex items-center gap-ds-1">
                  <Button type="button" variant="ghost" size="sm" disabled={runtimeMode || !canEditDashboard} onClick={() => onToggleLock(element.id)}>
                    {element.locked ? <Lock className="h-4 w-4" aria-hidden /> : <Unlock className="h-4 w-4" aria-hidden />}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" disabled={runtimeMode || !canEditDashboard || !isSelected} onClick={() => onReorder("forward")}>
                    <ArrowUp className="h-4 w-4" aria-hidden />
                  </Button>
                  <Button type="button" variant="ghost" size="sm" disabled={runtimeMode || !canEditDashboard || !isSelected} onClick={() => onReorder("backward")}>
                    <ArrowDown className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function RevisionPanel({
  document,
  canEditDashboard,
  onRestore,
  compact = false,
}: {
  document: HmiCanvasDocument;
  canEditDashboard: boolean;
  onRestore: (index: number) => void;
  compact?: boolean;
}) {
  const revisions = document.metadata.revisions ?? [];
  return (
    <Card className={compact ? "border-border/60 bg-card/45 p-ds-3 shadow-none" : "m-ds-4 mt-0 border-border/60 bg-card/45 p-ds-4"}>
      <div className="flex items-center gap-ds-2">
        <History className="h-4 w-4 text-muted" aria-hidden />
        <p className="text-caption font-semibold text-foreground">Recent snapshots</p>
      </div>
      {revisions.length === 0 ? (
        <p className="mt-ds-2 text-caption text-muted">No snapshots saved yet. Each HMI save stores a restorable document revision.</p>
      ) : (
        <div className="mt-ds-3 space-y-ds-2">
          {revisions.slice().reverse().map((revision, reverseIndex) => {
            const actualIndex = revisions.length - 1 - reverseIndex;
            return (
              <div key={revision.id} className="flex items-center justify-between rounded-xl border border-border/50 bg-background/50 px-ds-3 py-ds-2">
                <div>
                  <p className="text-caption font-medium text-foreground">{revision.note || "Manual save snapshot"}</p>
                  <p className="text-micro text-muted">{new Date(revision.createdAt).toLocaleString()}</p>
                </div>
                <Button type="button" variant="secondary" size="sm" disabled={!canEditDashboard} onClick={() => onRestore(actualIndex)}>
                  Restore
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function RuntimeDiagnostics({ snapshot, compact = false }: { snapshot: HmiRuntimeSnapshot | null; compact?: boolean }) {
  if (!snapshot) {
    return null;
  }
  return (
    <Card className={compact ? "border-border/60 bg-card/45 p-ds-3 shadow-none" : "m-ds-4 mt-0 border-border/60 bg-card/45 p-ds-4"}>
      <div className="flex items-center gap-ds-2">
        <ShieldAlert className="h-4 w-4 text-muted" aria-hidden />
        <p className="text-caption font-semibold text-foreground">Runtime diagnostics</p>
      </div>
      <div className="mt-ds-3 grid grid-cols-1 gap-ds-3 sm:grid-cols-3">
        <div className="rounded-xl border border-border/50 bg-background/50 px-ds-3 py-ds-2">
          <div className="text-micro uppercase tracking-[0.08em] text-muted">Bindings</div>
          <div className="mt-1 text-title text-foreground">{snapshot.graph.bindings.length}</div>
        </div>
        <div className="rounded-xl border border-border/50 bg-background/50 px-ds-3 py-ds-2">
          <div className="text-micro uppercase tracking-[0.08em] text-muted">Telemetry queries</div>
          <div className="mt-1 text-title text-foreground">{Object.keys(snapshot.telemetry).length}</div>
        </div>
        <div className="rounded-xl border border-border/50 bg-background/50 px-ds-3 py-ds-2">
          <div className="text-micro uppercase tracking-[0.08em] text-muted">Errors</div>
          <div className="mt-1 text-title text-foreground">{snapshot.errors.length}</div>
        </div>
      </div>
    </Card>
  );
}

type HmiSidebarSection = "insert" | "assets" | "templates" | "layers" | "history";

function HmiEditorSidebar({
  document,
  selectedElementId,
  selectedElement,
  selectedBindings,
  tenantId,
  allWorkspaces,
  allLayersSelected,
  runtimeMode,
  canEditDashboard,
  customSymbols,
  snapshot,
  activeSection,
  panelOpen,
  inspectorOpen,
  onAddElement,
  onAddSymbol,
  onApplyTemplate,
  onSelect,
  onToggleLock,
  onReorder,
  onRestore,
  onSelectAllLayers,
  onPanelOpenChange,
  onSectionSelect,
  onCloseInspector,
  onDeleteImageAsset,
  onUpdateElement,
  onBindingsChange,
  onUpdateDocument,
  onSaveAsSymbol,
  onUpgradeSymbolInstances,
}: {
  document: HmiCanvasDocument;
  selectedElementId: string | null;
  selectedElement: HmiElement | null;
  selectedBindings: HmiBinding[];
  tenantId: string;
  allWorkspaces: boolean;
  allLayersSelected: boolean;
  runtimeMode: boolean;
  canEditDashboard: boolean;
  customSymbols: HmiSymbolDefinition[];
  snapshot: HmiRuntimeSnapshot | null;
  activeSection: HmiSidebarSection;
  panelOpen: boolean;
  inspectorOpen: boolean;
  onAddElement: (type: HmiElement["type"]) => void;
  onAddSymbol: (definitionId: string) => void;
  onApplyTemplate: (templateId: string) => void;
  onSelect: (elementId: string | null) => void;
  onToggleLock: (elementId: string) => void;
  onReorder: (direction: "forward" | "backward") => void;
  onRestore: (index: number) => void;
  onSelectAllLayers: () => void;
  onPanelOpenChange: (nextOpen: boolean) => void;
  onSectionSelect: (sectionId: HmiSidebarSection) => void;
  onCloseInspector: () => void;
  onDeleteImageAsset: (storagePath: string) => void;
  onUpdateElement: (updater: (element: HmiElement) => HmiElement) => void;
  onBindingsChange: (updater: (bindings: HmiBinding[]) => HmiBinding[]) => void;
  onUpdateDocument: (updater: (document: HmiCanvasDocument) => HmiCanvasDocument) => void;
  onSaveAsSymbol: (existingSymbolId?: string | null) => void;
  onUpgradeSymbolInstances: (definitionId: string, mode: "selected" | "all") => void;
}) {
  const sections: Array<{
    id: HmiSidebarSection;
    label: string;
    description: string;
    icon: typeof Plus;
  }> = [
    { id: "insert", label: "Insert", description: "Elements and primitives.", icon: Plus },
    { id: "assets", label: "Assets", description: "Built-in and workspace symbols.", icon: Wind },
    { id: "templates", label: "Templates", description: "Starter HMI canvases.", icon: BadgeCheck },
    { id: "layers", label: "Layers", description: "Canvas stacking and lock state.", icon: Layers3 },
    { id: "history", label: "History", description: "Snapshots and runtime diagnostics.", icon: History },
  ];

  const activeSectionMeta =
    sections.find((section) => section.id === activeSection) ??
    sections[0] ?? {
      id: "insert" as const,
      label: "Insert",
      description: "Elements and primitives.",
      icon: Plus,
    };

  const renderPanel = () => {
    if (activeSection === "insert") {
      return (
        <div className="space-y-ds-4">
          <div className="rounded-2xl border border-border/60 bg-card/50 p-ds-3">
            <div className="flex items-start justify-between gap-ds-2">
              <div>
                <p className="text-caption font-semibold text-foreground">Elements</p>
                <p className="mt-1 text-micro text-muted">Add new HMI primitives.</p>
              </div>
              <Button
                type="button"
                variant={allLayersSelected ? "primary" : "secondary"}
                size="sm"
                className="shrink-0"
                onClick={onSelectAllLayers}
                disabled={runtimeMode || !canEditDashboard || document.elements.length === 0}
              >
                Select all
              </Button>
            </div>
            <div className="mt-ds-3 grid grid-cols-1 gap-ds-2">
              {hmiElementRegistry.filter((entry) => entry.type !== "symbol_instance").map((entry) => (
                <Button
                  key={entry.type}
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="min-h-[2.5rem] justify-start gap-ds-2 whitespace-normal px-ds-3 text-left"
                  onClick={() => onAddElement(entry.type)}
                  disabled={runtimeMode || !canEditDashboard}
                >
                  <Plus className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="leading-tight">{entry.title}</span>
                </Button>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-dashed border-border/60 bg-background/60 px-ds-3 py-ds-3 text-micro text-muted">
            Click an item in the rail to switch panels. Click it again or use the handle to collapse this layer.
          </div>
        </div>
      );
    }

    if (activeSection === "assets") {
      return (
        <SymbolLibraryPanel
          compact
          onAddSymbol={onAddSymbol}
          canEditDashboard={canEditDashboard}
          runtimeMode={runtimeMode}
          customSymbols={customSymbols}
        />
      );
    }

    if (activeSection === "templates") {
      return (
        <TemplateGalleryPanel
          compact
          canEditDashboard={canEditDashboard}
          runtimeMode={runtimeMode}
          onApplyTemplate={onApplyTemplate}
        />
      );
    }

    if (activeSection === "layers") {
      return (
        <LayerPanel
          compact
          document={document}
          selectedElementId={selectedElementId}
          canEditDashboard={canEditDashboard}
          runtimeMode={runtimeMode}
          onSelect={onSelect}
          onToggleLock={onToggleLock}
          onReorder={onReorder}
        />
      );
    }

    return (
      <div className="space-y-ds-4">
        <RevisionPanel compact document={document} canEditDashboard={canEditDashboard} onRestore={onRestore} />
        <RuntimeDiagnostics compact snapshot={snapshot} />
      </div>
    );
  };

  return (
    <aside className="relative flex min-h-0 shrink-0 border-r border-border/50 bg-card/82">
      <div className="hmi-builder-rail flex w-[4.25rem] min-w-[4.25rem] flex-col items-center border-r border-border/50 px-[6px] py-ds-3">
        <div className="flex min-h-0 w-full flex-1 flex-col gap-[6px]">
          {sections.map((section) => {
            const Icon = section.icon;
            const active = activeSection === section.id;
            return (
              <button
                key={section.id}
                type="button"
                className={`inline-flex min-h-[4.1rem] w-full flex-col items-center justify-center gap-1 rounded-2xl border px-1 py-2 text-center transition ${
                  active
                    ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 text-[color:var(--accent)] shadow-[0_10px_20px_-18px_rgba(15,23,42,0.45)]"
                    : "border-border/60 bg-card/80 text-muted hover:border-border hover:bg-card hover:text-foreground"
                }`}
                onClick={() => onSectionSelect(section.id)}
                aria-pressed={active}
                title={section.label}
              >
                <Icon className="h-4 w-4 shrink-0" aria-hidden />
                <span className="text-[0.6rem] font-medium leading-[1.05]">{section.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div
        className={`min-h-0 overflow-hidden border-r border-card-border transition-[width,opacity] duration-200 ease-out ${
          panelOpen ? "w-[21rem] opacity-100" : "w-0 opacity-0"
        }`}
      >
        <div className="relative flex h-full w-[21rem] min-w-[21rem] flex-col bg-card shadow-[0_24px_60px_-32px_rgba(15,23,42,0.18)]">
          <div className="shrink-0 border-b border-card-border bg-muted-bg/30 px-ds-3 py-ds-3">
            <p className="text-caption font-semibold text-foreground">{activeSectionMeta.label}</p>
            <p className="mt-1 text-[0.67rem] leading-snug text-muted">{activeSectionMeta.description}</p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-ds-3 py-ds-3">{renderPanel()}</div>
          <div
            className={`absolute inset-0 z-10 flex flex-col bg-card transition-transform duration-200 ease-out ${
              inspectorOpen && selectedElement ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="flex items-center justify-between border-b border-card-border bg-muted-bg/30 px-ds-3 py-ds-3">
              <div className="min-w-0">
                <p className="truncate text-caption font-semibold text-foreground">
                  {selectedElement ? `Configure ${selectedElement.name || formatElementTitle(selectedElement.type)}` : "Configure element"}
                </p>
                <p className="mt-1 text-[0.67rem] leading-snug text-muted">
                  Edit geometry, content, bindings, and behavior directly from the sidebar.
                </p>
              </div>
              <button
                type="button"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-border/60 bg-card text-muted transition hover:text-foreground"
                onClick={onCloseInspector}
                aria-label="Close configure panel"
                title="Close"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-ds-3 py-ds-3">
              <HmiInspector
                presentation="modal"
                document={document}
                tenantId={tenantId}
                allWorkspaces={allWorkspaces}
                element={selectedElement}
                bindings={selectedBindings}
                canEditDashboard={canEditDashboard && !runtimeMode}
                onDeleteImageAsset={onDeleteImageAsset}
                onUpdateElement={onUpdateElement}
                onBindingsChange={onBindingsChange}
                onUpdateDocument={onUpdateDocument}
                onSaveAsSymbol={onSaveAsSymbol}
                onUpgradeSymbolInstances={onUpgradeSymbolInstances}
              />
            </div>
          </div>
        </div>
      </div>
      <button
        type="button"
        className="absolute right-0 top-1/2 z-[1] inline-flex h-10 w-6 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-border/70 bg-card text-muted shadow-[0_18px_26px_-22px_rgba(15,23,42,0.45)] transition hover:text-foreground"
        onClick={() => onPanelOpenChange(!panelOpen)}
        aria-label={panelOpen ? `Collapse ${activeSectionMeta.label} panel` : `Expand ${activeSectionMeta.label} panel`}
        title={panelOpen ? `Collapse ${activeSectionMeta.label}` : `Expand ${activeSectionMeta.label}`}
      >
        {panelOpen ? <ChevronLeft className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
      </button>
    </aside>
  );
}

function RuntimeElement({
  element,
  dashboardId,
  bindings,
  snapshot,
  devicesById,
  activeAlertFingerprints,
  assetsById,
  symbolDefinitionsById,
  runtimeMode,
  canSendCommands,
  commandState,
  onExecuteCommand,
}: {
  element: HmiElement;
  dashboardId?: string | null;
  bindings: HmiBinding[];
  snapshot: HmiRuntimeSnapshot | null;
  devicesById: Map<string, DeviceDto>;
  activeAlertFingerprints: ReadonlySet<string>;
  assetsById: Map<string, HmiCanvasDocument["assets"][number]>;
  symbolDefinitionsById: Map<string, HmiSymbolDefinition>;
  runtimeMode: boolean;
  canSendCommands: boolean;
  commandState: HmiCommandRuntimeState | null;
  onExecuteCommand: (request: HmiPendingCommandRequest) => void;
}) {
  const primaryBinding =
    bindings.find((binding) => binding.target === "value" || binding.target === "gauge_value" || binding.target === "status") ??
    bindings[0] ??
    null;
  const primaryFingerprint = primaryBinding?.source.kind === "telemetry" ? sourceQueryId(primaryBinding.source) : null;
  const primarySeries = primaryFingerprint ? snapshot?.telemetry[primaryFingerprint] : undefined;
  const latest = latestSeriesValue(primarySeries?.values);
  const numericState = coerceNumericState(latest);
  const seriesBindings = bindings.filter((binding) => binding.target === "chart_series");
  const chartSeries = seriesBindings.map((binding, index) => {
    const fingerprint = binding.source.kind === "telemetry" ? sourceQueryId(binding.source) : null;
    const series = fingerprint ? snapshot?.telemetry[fingerprint] : undefined;
    const device = binding.source.kind === "telemetry" ? devicesById.get(binding.source.deviceId) : null;
    return {
      binding,
      series,
      latest: latestSeriesValue(series?.values),
      device: device ?? null,
      connection: getBindingConnectionState(binding, series?.timestamps, device),
      alert: (() => {
        const bindingFingerprint = bindingAlertFingerprint(binding);
        return bindingFingerprint ? activeAlertFingerprints.has(bindingFingerprint) : false;
      })(),
      color: ["#1f7a4a", "#0ea5e9", "#f59e0b", "#8b5cf6"][index % 4],
    };
  });
  const primaryDevice = primaryBinding?.source.kind === "telemetry" ? devicesById.get(primaryBinding.source.deviceId) ?? null : null;
  const primaryConnection = getBindingConnectionState(primaryBinding, primarySeries?.timestamps, primaryDevice);
  const hasAlert = bindings.some((binding) => {
    const fingerprint = bindingAlertFingerprint(binding);
    return fingerprint ? activeAlertFingerprints.has(fingerprint) : false;
  });
  const surfaceBackground = readElementBackgroundColor(element);
  const textTone = readElementTextColor(element) ?? "#0f172a";
  const mutedTextTone = textTone === "#ffffff" ? "rgba(255,255,255,0.76)" : "#64748b";
  const legendChipStyle =
    textTone === "#ffffff"
      ? ({
          backgroundColor: "rgba(255,255,255,0.14)",
          color: "#ffffff",
          border: "1px solid rgba(255,255,255,0.2)",
        } as const)
      : undefined;
  const tableHeaderStyle =
    textTone === "#ffffff"
      ? ({
          backgroundColor: "rgba(255,255,255,0.1)",
          color: "#ffffff",
        } as const)
      : ({ color: mutedTextTone } as const);
  const surfaceStyle = surfaceBackground ? ({ backgroundColor: surfaceBackground } as const) : undefined;
  const isOffline = primaryConnection.offline;
  const isStale = primaryConnection.stale;
  const chromeClass = hasAlert
    ? "border-red-400/60 shadow-[0_0_0_2px_rgba(220,38,38,0.12)]"
    : isOffline
      ? "border-slate-400/50"
      : isStale
        ? "border-amber-400/55"
        : "border-emerald-700/20";
  const commandStatusTone =
    commandState?.status === "failed"
      ? "text-red-600"
      : commandState?.status === "acknowledged" || commandState?.status === "completed" || commandState?.status === "success"
        ? "text-emerald-700"
        : commandState?.status
          ? "text-amber-700"
          : "text-muted";

  const renderConnectionBadge = () => {
    if (hasAlert) {
      return <span className="rounded-full bg-red-600 px-2 py-[2px] text-[10px] font-semibold uppercase tracking-[0.08em] text-white">Alert</span>;
    }
    if (isOffline) {
      return <span className="rounded-full bg-slate-700 px-2 py-[2px] text-[10px] font-semibold uppercase tracking-[0.08em] text-white">Offline</span>;
    }
    if (isStale) {
      return <span className="rounded-full bg-amber-500 px-2 py-[2px] text-[10px] font-semibold uppercase tracking-[0.08em] text-white">Stale</span>;
    }
    return null;
  };
  const [setpointDraft, setSetpointDraft] = useState(
    element.type === "setpoint" ? String(element.setpoint.defaultValue) : "",
  );

  useEffect(() => {
    if (element.type === "setpoint") {
      setSetpointDraft(String(element.setpoint.defaultValue));
    }
  }, [element]);

  if (element.type === "line") {
    const [x1, y1, x2, y2] = element.points;
    const stroke = readElementLineColor(element) ?? HMI_DEFAULT_LINE_COLOR;
    return (
      <svg className="h-full w-full" viewBox={`0 0 ${element.width} ${element.height}`}>
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={stroke} strokeWidth="4" strokeLinecap="round" />
      </svg>
    );
  }

  if (element.type === "arrow") {
    const stroke = readElementLineColor(element) ?? HMI_DEFAULT_LINE_COLOR;
    const geometry = buildArrowGeometry(element.arrow.direction, element.width, element.height);
    return (
      <svg className="h-full w-full overflow-visible" viewBox={`0 0 ${element.width} ${element.height}`}>
        <line
          x1={geometry.shaft.x1}
          y1={geometry.shaft.y1}
          x2={geometry.shaft.x2}
          y2={geometry.shaft.y2}
          stroke={stroke}
          strokeWidth="4"
          strokeLinecap="round"
        />
        <polygon points={geometry.headPoints} fill={stroke} />
      </svg>
    );
  }

  if (element.type === "flow_line") {
    const stroke = readElementLineColor(element) ?? HMI_DEFAULT_LINE_COLOR;
    const stripeColor = readFlowLineStripeColor(element) ?? "#ffffff";
    const surfaceBackground = readElementBackgroundColor(element);
    const safeLineWidth = Math.max(4, Math.min(48, element.flow.lineWidth));
    const safeStripeLength = Math.max(4, Math.min(64, element.flow.stripeLength));
    const safeStripeGap = Math.max(2, Math.min(64, element.flow.stripeGap));
    const dashTravel = safeStripeLength + safeStripeGap;
    const durationSeconds = Math.max(0.25, 240 / Math.max(4, element.flow.speed));
    const inset = Math.max(safeLineWidth / 2 + 2, 6);
    const geometry = buildFlowLineGeometry(
      element.flow.shape,
      element.width,
      element.height,
      inset,
      element.flow.turnRatio,
    );

    return (
      <svg className="h-full w-full overflow-visible" viewBox={`0 0 ${element.width} ${element.height}`}>
        {surfaceBackground ? <rect x="0" y="0" width={element.width} height={element.height} rx="18" fill={surfaceBackground} /> : null}
        <path
          d={geometry.d}
          fill="none"
          stroke={stroke}
          strokeWidth={safeLineWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.95}
        />
        <path
          d={geometry.d}
          fill="none"
          stroke={stripeColor}
          strokeWidth={Math.max(2, safeLineWidth * 0.46)}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={`${safeStripeLength} ${safeStripeGap}`}
        >
          {element.flow.animate ? (
            <animate
              attributeName="stroke-dashoffset"
              from="0"
              to={element.flow.direction === "forward" ? `${-dashTravel}` : `${dashTravel}`}
              dur={`${durationSeconds.toFixed(2)}s`}
              repeatCount="indefinite"
            />
          ) : null}
        </path>
      </svg>
    );
  }

  if (element.type === "circle") {
    return <div className={`h-full w-full rounded-full border-4 border-emerald-700/60 bg-emerald-500/12 ${hasAlert ? "ring-4 ring-red-300/40" : ""}`} style={surfaceStyle} />;
  }

  if (element.type === "rectangle") {
    return <div className={`h-full w-full rounded-[18px] border bg-card/85 inset-highlight ${chromeClass}`} style={surfaceStyle} />;
  }

  if (element.type === "text") {
    return (
      <div
        className={`flex h-full w-full items-center text-[0.95rem] font-semibold text-foreground ${
          surfaceBackground ? "rounded-[16px] border border-card-border px-ds-3 py-ds-2" : "px-ds-3"
        }`}
        style={{ ...surfaceStyle, color: textTone }}
      >
        {element.text}
      </div>
    );
  }

  if (element.type === "value") {
    return (
        <div className={`flex h-full w-full flex-col justify-center rounded-[18px] border bg-card/92 px-ds-3 ${chromeClass}`} style={surfaceStyle}>
        <div className="flex items-center justify-between gap-ds-2">
          <span className="text-micro uppercase tracking-[0.12em]" style={{ color: mutedTextTone }}>{element.name}</span>
          {renderConnectionBadge()}
        </div>
        <span className="text-[1.4rem] font-semibold" style={{ color: textTone }}>
          {latest !== null || isOffline ? formatCanvasBindingValue(latest, primaryBinding, { offline: isOffline }) : element.placeholder}
        </span>
      </div>
    );
  }

  if (element.type === "stat_card") {
    return (
      <div className={`flex h-full w-full flex-col justify-between rounded-[18px] border bg-card/92 px-ds-4 py-ds-3 ${chromeClass}`} style={surfaceStyle}>
        <div className="flex items-center justify-between gap-ds-2">
          <span className="text-micro uppercase tracking-[0.12em]" style={{ color: mutedTextTone }}>{element.label}</span>
          {renderConnectionBadge()}
        </div>
        <span className="text-[1.8rem] font-semibold" style={{ color: textTone }}>
          {latest !== null || isOffline ? formatCanvasBindingValue(latest, primaryBinding, { offline: isOffline }) : element.placeholder}
        </span>
        <span className="text-caption" style={{ color: mutedTextTone }}>{element.name}</span>
      </div>
    );
  }

  if (element.type === "data_table") {
    const tableBindings = chartSeries.length ? chartSeries : [];
    const timestamps = tableBindings[0]?.series?.timestamps ?? [];
    const rows = timestamps
      .slice(-element.table.rowLimit)
      .map((timestamp, index, arr) => {
        const valueIndex = timestamps.length - arr.length + index;
        return {
          timestamp,
          values: tableBindings.map((item) => item.series?.values[valueIndex] ?? null),
        };
      })
      .reverse();
    return (
      <div className={`h-full w-full overflow-hidden rounded-[18px] border bg-card/92 ${chromeClass}`} style={surfaceStyle}>
        <table className="w-full border-collapse text-left text-caption">
          <thead className="bg-[color:color-mix(in_srgb,var(--success)_10%,var(--card))]" style={tableHeaderStyle}>
            <tr>
              {["Timestamp", ...tableBindings.map((item, index) => item.binding.label || element.table.columns[index + 1] || `Series ${index + 1}`)].map((column) => (
                <th key={column} className="px-ds-3 py-ds-2 font-semibold">{column}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.timestamp} className="border-t border-border/40">
                <td className="px-ds-3 py-ds-2 text-micro" style={{ color: mutedTextTone }}>{new Date(row.timestamp).toLocaleTimeString()}</td>
                {row.values.map((value, index) => (
                  <td key={`${row.timestamp}_${index}`} className="px-ds-3 py-ds-2 font-medium" style={{ color: textTone }}>
                    {formatBindingValue(value, tableBindings[index]?.binding)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (element.type === "gauge") {
    const min = element.gauge.min;
    const max = element.gauge.max;
    const percent = latest !== null ? Math.max(0, Math.min(1, (latest - min) / Math.max(1, max - min))) : 0;
    const visiblePercent = latest !== null ? Math.max(percent, 0.025) : 0;
    const arcLength = 251.2;
    const strokeOffset = arcLength - arcLength * visiblePercent;
    const gaugeValue = latest ?? min;
    const gaugeColor = gaugeSeverityColor(gaugeValue, {
      min,
      warningLow: element.gauge.warningLow,
      warningHigh: element.gauge.warningHigh,
      max,
    });
    return (
      <div className={`flex h-full w-full flex-col items-center justify-center gap-ds-2 rounded-[24px] border bg-card/90 p-ds-3 ${chromeClass}`} style={surfaceStyle}>
        <div className="flex w-full items-center justify-between gap-ds-2 px-ds-1">
          <span className="text-micro uppercase tracking-[0.12em]" style={{ color: mutedTextTone }}>{element.name}</span>
          {renderConnectionBadge()}
        </div>
        <svg className="h-[70%] w-full" viewBox="0 0 220 140">
          <path
            d="M30 110 A80 80 0 0 1 190 110"
            fill="none"
            stroke={gaugeColor}
            strokeOpacity="0.16"
            strokeWidth="18"
            strokeLinecap="round"
          />
          <path
            d="M30 110 A80 80 0 0 1 190 110"
            fill="none"
            stroke={gaugeColor}
            strokeWidth="18"
            strokeLinecap="round"
            strokeDasharray={arcLength}
            strokeDashoffset={strokeOffset}
          />
          {element.gauge.showTicks ? (
            <>
              <text x="28" y="128" fontSize="11" fill={mutedTextTone}>{min}</text>
              <text x="180" y="128" fontSize="11" fill={mutedTextTone}>{max}</text>
            </>
          ) : null}
          <text x="110" y="90" textAnchor="middle" fontSize="28" fontWeight="700" fill={textTone}>
            {formatCanvasBindingValue(latest, primaryBinding, { offline: isOffline })}
          </text>
          {element.gauge.unit ? <text x="110" y="112" textAnchor="middle" fontSize="12" fill={mutedTextTone}>{element.gauge.unit}</text> : null}
        </svg>
      </div>
    );
  }

  if (element.type === "trend_chart") {
    const width = Math.max(60, element.width - 24);
    const height = Math.max(40, element.height - 42);
    const allValues = chartSeries.flatMap((item) => item.series?.values ?? []).filter((value): value is number => typeof value === "number");
    const minValue = allValues.length ? Math.min(...allValues) : 0;
    const maxValue = allValues.length ? Math.max(...allValues) : 1;
    const span = maxValue - minValue || 1;
    return (
      <div className={`flex h-full w-full flex-col rounded-[18px] border bg-card/90 px-ds-3 py-ds-2 ${chromeClass}`} style={surfaceStyle}>
        <div className="flex items-center justify-between gap-ds-2">
          <span className="text-micro uppercase tracking-[0.12em]" style={{ color: mutedTextTone }}>{element.name}</span>
          {renderConnectionBadge()}
        </div>
        <div className="mt-ds-2 flex-1">
          <svg className="h-full w-full" viewBox={`0 0 ${width} ${height}`}>
            {element.chart.showGrid ? (
              <>
                {[0.25, 0.5, 0.75].map((ratio) => (
                  <line key={ratio} x1="0" y1={height * ratio} x2={width} y2={height * ratio} stroke="rgba(148,163,184,0.22)" strokeDasharray="4 4" />
                ))}
              </>
            ) : null}
            {chartSeries.map((item) => {
              const values = (item.series?.values ?? []).filter((value): value is number => typeof value === "number");
              if (values.length < 2) {
                return null;
              }
              const points = values
                .map((value, pointIndex) => {
                  const x = (pointIndex / Math.max(1, values.length - 1)) * width;
                  const y = height - ((value - minValue) / span) * height;
                  return `${x.toFixed(1)},${y.toFixed(1)}`;
                })
                .join(" ");
              const areaPoints = `0,${height} ${points} ${width},${height}`;
              return (
                <g key={item.binding.id}>
                  {element.chart.mode === "area" ? (
                    <polygon points={areaPoints} fill={`${item.color}22`} />
                  ) : null}
                  <polyline fill="none" stroke={item.color} strokeWidth={element.chart.lineWidth} points={points} />
                </g>
              );
            })}
            {element.chart.showAxes ? (
              <>
                <line x1="0" y1={height} x2={width} y2={height} stroke="rgba(100,116,139,0.55)" />
                <line x1="0" y1="0" x2="0" y2={height} stroke="rgba(100,116,139,0.55)" />
              </>
            ) : null}
          </svg>
        </div>
        {element.chart.showLegend && chartSeries.length ? (
          <div className="mt-ds-2 flex flex-wrap gap-2">
            {chartSeries.map((item, index) => (
              <div key={item.binding.id} className="inline-flex items-center gap-2 rounded-full bg-muted-bg/90 px-2 py-1 text-[11px] text-foreground" style={legendChipStyle}>
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                <span>{item.binding.label || `Series ${index + 1}`}</span>
                <span className="font-semibold">{formatBindingValue(item.latest, item.binding)}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  if (element.type === "symbol_instance") {
    const symbolDefinition = symbolDefinitionsById.get(element.symbol.definitionId) ?? getBuiltinSymbolById(element.symbol.definitionId);
    const symbolOverrides = element.symbol.overrides as Record<string, unknown>;
    const hasLiveState = latest !== null;
    const overrideStateOn = readSymbolOverrideBoolean(symbolOverrides, "stateOn");
    const overrideAnimate = readSymbolOverrideBoolean(symbolOverrides, "animate");
    const overrideAlarm = readSymbolOverrideBoolean(symbolOverrides, "alarm");
    const overrideLevelPercent = readSymbolOverrideNumber(symbolOverrides, "levelPercent");
    const stateOn = overrideStateOn ?? (hasLiveState ? numericState > 0 : true);
    const inAlarm = overrideAlarm ?? (hasLiveState ? numericState >= 2 : false);
    const normalizedLevel = Math.max(
      0,
      Math.min(1, overrideLevelPercent !== null ? overrideLevelPercent / 100 : hasLiveState ? (numericState > 1 ? numericState / 100 : numericState) : 0.62),
    );
    const animate = overrideAnimate ?? stateOn;

    if (!symbolDefinition) {
      return (
        <div className="flex h-full w-full items-center justify-center rounded-[18px] border border-dashed border-border/70 bg-card/80 text-caption text-muted">
          Missing symbol
        </div>
      );
    }

    if (!symbolDefinition.builtIn) {
      return (
        <div className={`flex h-full w-full items-center justify-center rounded-[22px] border bg-card/90 p-ds-2 ${chromeClass}`} style={surfaceStyle}>
          <svg className="h-full w-full" viewBox={`0 0 ${symbolDefinition.viewport.width} ${symbolDefinition.viewport.height}`}>
            {symbolDefinition.layers.map((layer) => {
              const style = layer.style as Record<string, unknown>;
              if (layer.type === "rect") {
                return (
                  <rect
                    key={layer.id}
                    x={Number(style.x ?? 0)}
                    y={Number(style.y ?? 0)}
                    width={Number(style.width ?? symbolDefinition.viewport.width)}
                    height={Number(style.height ?? symbolDefinition.viewport.height)}
                    rx={Number(style.rx ?? 0)}
                    fill={String(style.fill ?? "#f3f8f5")}
                    stroke={String(style.stroke ?? "#2c5a49")}
                    strokeWidth={Number(style.strokeWidth ?? 2)}
                  />
                );
              }
              if (layer.type === "circle") {
                return (
                  <circle
                    key={layer.id}
                    cx={Number(style.cx ?? symbolDefinition.viewport.width / 2)}
                    cy={Number(style.cy ?? symbolDefinition.viewport.height / 2)}
                    r={Number(style.r ?? Math.min(symbolDefinition.viewport.width, symbolDefinition.viewport.height) / 2)}
                    fill={String(style.fill ?? "#dceae2")}
                    stroke={String(style.stroke ?? "#2c5a49")}
                    strokeWidth={Number(style.strokeWidth ?? 2)}
                  />
                );
              }
              if (layer.type === "line") {
                return (
                  <line
                    key={layer.id}
                    x1={Number(style.x1 ?? 0)}
                    y1={Number(style.y1 ?? symbolDefinition.viewport.height / 2)}
                    x2={Number(style.x2 ?? symbolDefinition.viewport.width)}
                    y2={Number(style.y2 ?? symbolDefinition.viewport.height / 2)}
                    stroke={String(style.stroke ?? "#2f5d4d")}
                    strokeWidth={Number(style.strokeWidth ?? 4)}
                    strokeLinecap={
                      style.strokeLinecap === "butt" ||
                      style.strokeLinecap === "square" ||
                      style.strokeLinecap === "inherit" ||
                      style.strokeLinecap === "round"
                        ? style.strokeLinecap
                        : "round"
                    }
                  />
                );
              }
              if (layer.type === "path" && typeof style.d === "string") {
                return (
                  <path
                    key={layer.id}
                    d={style.d}
                    fill={String(style.fill ?? "transparent")}
                    stroke={String(style.stroke ?? "#2f5d4d")}
                    strokeWidth={Number(style.strokeWidth ?? 2)}
                  />
                );
              }
              if (layer.type === "text") {
                return (
                  <text
                    key={layer.id}
                    x={Number(style.x ?? 0)}
                    y={Number(style.y ?? symbolDefinition.viewport.height / 2)}
                    fill={String(style.fill ?? "#0f172a")}
                    fontSize={Number(style.fontSize ?? 16)}
                    fontWeight={String(style.fontWeight ?? 600)}
                  >
                    {String(style.text ?? symbolDefinition.title)}
                  </text>
                );
              }
              return null;
            })}
          </svg>
        </div>
      );
    }

    if (symbolDefinition.id === "builtin.hvac.fan") {
      return (
        <div className="h-full w-full" style={surfaceStyle}>
          <BuiltinSymbolPreview symbolId={symbolDefinition.id} stateOn={stateOn ?? undefined} animate={animate ?? undefined} />
        </div>
      );
    }

    if (symbolDefinition.id === "builtin.process.pump") {
      return (
        <div className="h-full w-full" style={surfaceStyle}>
          <BuiltinSymbolPreview symbolId={symbolDefinition.id} stateOn={stateOn ?? undefined} animate={animate ?? undefined} />
        </div>
      );
    }

    if (symbolDefinition.id === "builtin.process.valve") {
      return (
        <div className="h-full w-full" style={surfaceStyle}>
          <BuiltinSymbolPreview symbolId={symbolDefinition.id} stateOn={stateOn ?? undefined} animate={animate ?? undefined} />
        </div>
      );
    }

    if (symbolDefinition.id === "builtin.hvac.damper") {
      return (
        <div className="h-full w-full" style={surfaceStyle}>
          <BuiltinSymbolPreview symbolId={symbolDefinition.id} stateOn={stateOn ?? undefined} />
        </div>
      );
    }

    if (symbolDefinition.id === "builtin.process.tank") {
      return (
        <div className="h-full w-full" style={surfaceStyle}>
          <BuiltinSymbolPreview symbolId={symbolDefinition.id} normalizedLevel={normalizedLevel} />
        </div>
      );
    }

    if (symbolDefinition.id === "builtin.process.pipe") {
      return (
        <div className="h-full w-full" style={surfaceStyle}>
          <BuiltinSymbolPreview symbolId={symbolDefinition.id} stateOn={stateOn ?? undefined} />
        </div>
      );
    }

    if (symbolDefinition.id === "builtin.hvac.duct") {
      return (
        <div className="h-full w-full" style={surfaceStyle}>
          <BuiltinSymbolPreview symbolId={symbolDefinition.id} />
        </div>
      );
    }

    if (symbolDefinition.id === "builtin.status.lamp") {
      return (
        <div className="h-full w-full" style={surfaceStyle}>
          <BuiltinSymbolPreview symbolId={symbolDefinition.id} stateOn={stateOn ?? undefined} inAlarm={inAlarm ?? undefined} animate={animate ?? undefined} />
        </div>
      );
    }
  }

  if (element.type === "command_button") {
    const disabled =
      !runtimeMode || !canSendCommands || !element.command.deviceId || (!!commandState && !isTerminalCommandStatus(commandState.status));
    const variantClass =
      element.command.variant === "danger"
        ? textTone === "#ffffff"
          ? "bg-red-600 text-white hover:bg-red-700"
          : "border border-red-400/35 bg-red-500/12 text-foreground hover:bg-red-500/18"
        : element.command.variant === "secondary"
          ? textTone === "#ffffff"
            ? "border border-card-border bg-card/18 text-white hover:bg-card/24"
            : "hmi-surface-button"
          : textTone === "#ffffff"
            ? "bg-emerald-600 text-white hover:bg-emerald-700"
            : "hmi-surface-button-active";
    return (
      <div className={`flex h-full w-full flex-col justify-center rounded-[18px] border bg-card/92 p-ds-3 ${chromeClass}`} style={surfaceStyle}>
        <button
          type="button"
          disabled={disabled}
          className={`rounded-xl px-ds-3 py-ds-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${variantClass}`}
          onClick={() =>
            onExecuteCommand({
              elementId: element.id,
              title: element.command.label,
              message:
                element.command.confirmText ||
                `Send "${element.command.action}" to the selected device?`,
              deviceId: element.command.deviceId ?? "",
              payload: {
                ...element.command.payload,
                action: element.command.action,
              },
            })
          }
        >
          {element.command.label}
        </button>
        <div className="mt-ds-2 flex items-center justify-between gap-ds-2 text-micro" style={{ color: mutedTextTone }}>
          <span className={commandStatusTone}>{commandState?.status ?? "ready"}</span>
          {!canSendCommands ? <span>No command permission</span> : null}
        </div>
        {commandState?.error ? <div className="mt-1 text-micro text-red-600">{commandState.error}</div> : null}
      </div>
    );
  }

  if (element.type === "command_toggle") {
    const stateOn = numericState > 0;
    const disabled =
      !runtimeMode || !canSendCommands || !element.toggle.deviceId || (!!commandState && !isTerminalCommandStatus(commandState.status));
    const toggleButtonClass = stateOn
      ? textTone === "#ffffff"
        ? "bg-slate-900 text-white hover:bg-slate-800"
        : "hmi-surface-button"
      : textTone === "#ffffff"
        ? "bg-emerald-600 text-white hover:bg-emerald-700"
        : "hmi-surface-button-active";
    return (
      <div className={`flex h-full w-full flex-col justify-between rounded-[18px] border bg-card/92 px-ds-4 py-ds-3 ${chromeClass}`} style={surfaceStyle}>
        <div className="flex items-center justify-between gap-ds-2">
          <span className="text-caption font-semibold" style={{ color: textTone }}>{element.toggle.label}</span>
          <span className={`rounded-full px-2 py-[2px] text-[10px] font-semibold uppercase tracking-[0.08em] ${stateOn ? "hmi-surface-chip-active" : "hmi-surface-chip"}`}>
            {stateOn ? element.toggle.onLabel : element.toggle.offLabel}
          </span>
        </div>
        <button
          type="button"
          disabled={disabled}
          className={`mt-ds-3 rounded-full px-ds-3 py-ds-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${toggleButtonClass}`}
          onClick={() =>
            onExecuteCommand({
              elementId: element.id,
              title: element.toggle.label,
              message:
                element.toggle.confirmText ||
                `Turn ${stateOn ? element.toggle.offLabel : element.toggle.onLabel} for this device?`,
              deviceId: element.toggle.deviceId ?? "",
              payload: {
                ...(stateOn ? element.toggle.offPayload : element.toggle.onPayload),
                action: element.toggle.action,
              },
            })
          }
        >
          {stateOn ? `Send ${element.toggle.offLabel}` : `Send ${element.toggle.onLabel}`}
        </button>
        <div className={`mt-ds-2 text-micro ${commandStatusTone}`}>{commandState?.status ?? "ready"}</div>
        {commandState?.error ? <div className="mt-1 text-micro text-red-600">{commandState.error}</div> : null}
      </div>
    );
  }

  if (element.type === "setpoint") {
    const disabled =
      !runtimeMode || !canSendCommands || !element.setpoint.deviceId || (!!commandState && !isTerminalCommandStatus(commandState.status));
    const setpointButtonClass =
      textTone === "#ffffff"
        ? "bg-emerald-600 text-white hover:bg-emerald-700"
        : "hmi-surface-button-active";
    return (
      <div className={`flex h-full w-full flex-col justify-between rounded-[18px] border bg-card/92 px-ds-4 py-ds-3 ${chromeClass}`} style={surfaceStyle}>
        <div className="flex items-center justify-between gap-ds-2">
          <span className="text-caption font-semibold" style={{ color: textTone }}>{element.setpoint.label}</span>
          <span className={`text-micro ${commandStatusTone}`}>{commandState?.status ?? "ready"}</span>
        </div>
        <div className="mt-ds-2 flex items-center gap-ds-2">
          <input
            className="ui-control min-w-0 flex-1 py-ds-2"
            type="number"
            value={setpointDraft}
            min={element.setpoint.min}
            max={element.setpoint.max}
            step={element.setpoint.step}
            disabled={!runtimeMode || !canSendCommands}
            onChange={(event) => setSetpointDraft(event.target.value)}
          />
          {element.setpoint.unit ? <span className="text-caption" style={{ color: mutedTextTone }}>{element.setpoint.unit}</span> : null}
        </div>
        <button
          type="button"
          disabled={disabled}
          className={`mt-ds-3 rounded-xl px-ds-3 py-ds-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${setpointButtonClass}`}
          onClick={() =>
            onExecuteCommand({
              elementId: element.id,
              title: element.setpoint.label,
              message:
                element.setpoint.confirmText ||
                `Apply ${setpointDraft}${element.setpoint.unit ? ` ${element.setpoint.unit}` : ""} to this setpoint?`,
              deviceId: element.setpoint.deviceId ?? "",
              payload: {
                ...element.setpoint.payload,
                action: element.setpoint.action,
                value: Number(setpointDraft),
              },
            })
          }
        >
          Apply
        </button>
        {commandState?.error ? <div className="mt-2 text-micro text-red-600">{commandState.error}</div> : null}
      </div>
    );
  }

  if (element.type === "image_asset") {
    const asset = element.asset.assetId ? assetsById.get(element.asset.assetId) : null;
    if (!asset?.storagePath || asset.kind !== "image") {
      return (
        <div className="flex h-full w-full items-center justify-center rounded-[18px] border border-dashed border-border/70 bg-card/80 px-ds-3 text-center text-caption text-muted">
          Upload image asset
        </div>
      );
    }
    return (
      <div className={`h-full w-full rounded-[18px] border bg-card/90 ${chromeClass}`} style={surfaceStyle}>
        <PrivateHmiImage
          storagePath={asset.storagePath}
          dashboardId={dashboardId}
          alt={element.asset.alt || asset.title}
          className="h-full w-full select-none"
          fit={element.asset.fit}
        />
      </div>
    );
  }

  if (element.type === "svg_asset") {
    const asset = assetsById.get(element.asset.assetId);
    if (!asset?.content) {
      return (
        <div className="flex h-full w-full items-center justify-center rounded-[18px] border border-dashed border-border/70 bg-card/80 text-caption text-muted">
          Missing SVG asset
        </div>
      );
    }
    return (
      <div className={`flex h-full w-full items-center justify-center rounded-[18px] border bg-card/90 p-ds-2 ${chromeClass}`} style={surfaceStyle}>
        <div
          className="h-full w-full"
          style={{ aspectRatio: asset.viewBox ? undefined : "1 / 1" }}
          dangerouslySetInnerHTML={{ __html: asset.content }}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full items-center justify-center rounded-[18px] border border-dashed border-border/70 bg-card/80 text-caption text-muted" style={surfaceStyle}>
      {formatElementTitle(element.type)}
    </div>
  );
}

const MemoizedRuntimeElement = memo(RuntimeElement, (previous, next) => {
  return (
    previous.element === next.element &&
    previous.dashboardId === next.dashboardId &&
    previous.bindings === next.bindings &&
    previous.snapshot === next.snapshot &&
    previous.devicesById === next.devicesById &&
    previous.activeAlertFingerprints === next.activeAlertFingerprints &&
    previous.assetsById === next.assetsById &&
    previous.symbolDefinitionsById === next.symbolDefinitionsById &&
    previous.runtimeMode === next.runtimeMode &&
    previous.canSendCommands === next.canSendCommands &&
    previous.commandState === next.commandState &&
    previous.onExecuteCommand === next.onExecuteCommand
  );
});

function PrivateHmiImage({
  storagePath,
  dashboardId,
  alt,
  className,
  fit,
}: {
  storagePath: string;
  dashboardId?: string | null;
  alt: string;
  className?: string;
  fit: "contain" | "cover" | "fill";
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;

    setSrc(null);
    setFailed(false);

    void getHmiImageAssetBlob(storagePath, dashboardId)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        if (!active) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setSrc(objectUrl);
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [storagePath, dashboardId]);

  if (failed) {
    return <div className="flex h-full w-full items-center justify-center px-ds-3 text-center text-caption text-muted">Image unavailable</div>;
  }

  if (!src) {
    return <div className="flex h-full w-full items-center justify-center px-ds-3 text-center text-caption text-muted">Loading image...</div>;
  }

  return <img src={src} alt={alt} draggable={false} className={className} style={{ objectFit: fit }} loading="lazy" />;
}

function collectReferencedImageStoragePaths(
  document: HmiCanvasDocument | null | undefined,
): string[] {
  if (!document) {
    return [];
  }
  const imageAssetIds = new Set<string>();
  for (const element of document.elements) {
    if (element.type !== "image_asset") {
      continue;
    }
    if (element.asset.assetId) {
      imageAssetIds.add(element.asset.assetId);
    }
  }
  if (imageAssetIds.size === 0) {
    return [];
  }
  return document.assets
    .filter((asset) => asset.kind === "image" && asset.storagePath && imageAssetIds.has(asset.id))
    .map((asset) => asset.storagePath)
    .filter((storagePath): storagePath is string => Boolean(storagePath))
    .sort();
}

function useHmiImageAssetReadiness(
  document: HmiCanvasDocument | null | undefined,
  dashboardId?: string | null,
): { ready: boolean; hasImages: boolean } {
  const storagePaths = useMemo(() => collectReferencedImageStoragePaths(document), [document]);
  const hasImages = storagePaths.length > 0;
  const [ready, setReady] = useState(!hasImages);

  useEffect(() => {
    if (!hasImages) {
      setReady(true);
      return;
    }

    let active = true;
    const objectUrls: string[] = [];
    setReady(false);

    const preload = async (storagePath: string) => {
      const blob = await getHmiImageAssetBlob(storagePath, dashboardId);
      const objectUrl = URL.createObjectURL(blob);
      objectUrls.push(objectUrl);
      await new Promise<void>((resolve) => {
        const image = new Image();
        image.onload = () => resolve();
        image.onerror = () => resolve();
        image.src = objectUrl;
      });
    };

    void Promise.allSettled(storagePaths.map((storagePath) => preload(storagePath))).then(() => {
      if (active) {
        setReady(true);
      }
      for (const objectUrl of objectUrls) {
        URL.revokeObjectURL(objectUrl);
      }
    });

    return () => {
      active = false;
      for (const objectUrl of objectUrls) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [dashboardId, hasImages, storagePaths]);

  return { ready, hasImages };
}

function SelectionToolButton({
  label,
  onClick,
  className = "",
  children,
  compact = true,
}: {
  label: string;
  onClick: () => void;
  className?: string;
  children: ReactNode;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center rounded-lg border border-transparent text-muted transition hover:border-border/70 hover:bg-muted-bg/60 hover:text-foreground ${compact ? "h-8 w-8" : "h-8 gap-ds-2 px-ds-3"} ${className}`}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

function ResizeHandle({
  direction,
  className,
  disabled,
  onPointerDown,
}: {
  direction: HmiResizeHandle;
  className: string;
  disabled: boolean;
  onPointerDown: (direction: HmiResizeHandle, event: ReactPointerEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      type="button"
      className={`hmi-resize-handle absolute pointer-events-auto transition ${
        disabled ? "cursor-not-allowed opacity-60" : ""
      } ${className}`}
      onPointerDown={(event) => onPointerDown(direction, event)}
      aria-label={`Resize ${direction}`}
      title={`Resize ${direction}`}
      disabled={disabled}
    />
  );
}

function HmiCanvasStage({
  dashboardId,
  document,
  selectedElementId,
  allLayersSelected,
  runtimeMode,
  viewport,
  snapshot,
  devicesById,
  activeAlertFingerprints,
  symbolDefinitionsById,
  canSendCommands,
  commandStates,
  canEditDashboard,
  onSelect,
  onMoveElement,
  onMoveAllElements,
  onResizeElement,
  onPan,
  onZoomAroundPoint,
  onInteractionStart,
  onInteractionEnd,
  onExecuteCommand,
  onDuplicateSelected,
  onDeleteSelected,
  onToggleLockSelected,
  onReorderSelected,
  onRotateSelectedArrow,
  onToggleSelectedSymbolState,
  onOpenInspector,
}: {
  dashboardId?: string | null;
  document: HmiCanvasDocument;
  selectedElementId: string | null;
  allLayersSelected: boolean;
  runtimeMode: boolean;
  viewport: { x: number; y: number; zoom: number };
  snapshot: HmiRuntimeSnapshot | null;
  devicesById: Map<string, DeviceDto>;
  activeAlertFingerprints: ReadonlySet<string>;
  symbolDefinitionsById: Map<string, HmiSymbolDefinition>;
  canSendCommands: boolean;
  commandStates: Record<string, HmiCommandRuntimeState>;
  canEditDashboard: boolean;
  onSelect: (elementId: string | null) => void;
  onMoveElement: (elementId: string, deltaX: number, deltaY: number) => void;
  onMoveAllElements: (deltaX: number, deltaY: number) => void;
  onResizeElement: (elementId: string, deltaX: number, deltaY: number, handle: HmiResizeHandle) => void;
  onPan: (deltaX: number, deltaY: number) => void;
  onZoomAroundPoint: (factor: number, point: { x: number; y: number }) => void;
  onInteractionStart: () => void;
  onInteractionEnd: () => void;
  onExecuteCommand: (request: HmiPendingCommandRequest) => void;
  onDuplicateSelected: () => void;
  onDeleteSelected: () => void;
  onToggleLockSelected: () => void;
  onReorderSelected: (direction: "forward" | "backward") => void;
  onRotateSelectedArrow: () => void;
  onToggleSelectedSymbolState: () => void;
  onOpenInspector: () => void;
}) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const elementNodeRefs = useRef(new Map<string, HTMLDivElement>());
  const selectedFrameRef = useRef<HTMLDivElement | null>(null);
  const allSelectionFrameRef = useRef<HTMLDivElement | null>(null);
  const dragState = useRef<HmiDragState | null>(null);
  const rafTokenRef = useRef<number | null>(null);
  const queuedPointerRef = useRef<{ clientX: number; clientY: number } | null>(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const assetsById = useMemo(() => new Map(document.assets.map((asset) => [asset.id, asset])), [document.assets]);
  const bindingsByElementId = useMemo(() => {
    const next = new Map<string, HmiBinding[]>();
    for (const binding of document.bindings) {
      const existing = next.get(binding.elementId);
      if (existing) {
        existing.push(binding);
      } else {
        next.set(binding.elementId, [binding]);
      }
    }
    return next;
  }, [document.bindings]);
  const visibleElements = useMemo(
    () =>
      document.elements
        .slice()
        .sort((a, b) => a.zIndex - b.zIndex)
        .filter((element) => element.visible),
    [document.elements],
  );
  const selectedElement = useMemo(
    () => document.elements.find((element) => element.id === selectedElementId) ?? null,
    [document.elements, selectedElementId],
  );
  const selectedSymbolDefinition =
    selectedElement?.type === "symbol_instance"
      ? symbolDefinitionsById.get(selectedElement.symbol.definitionId) ?? getBuiltinSymbolById(selectedElement.symbol.definitionId)
      : null;
  const selectedSymbolStateOn =
    selectedElement?.type === "symbol_instance"
      ? readSymbolOverrideBoolean(selectedElement.symbol.overrides as Record<string, unknown>, "stateOn") ?? true
      : true;
  const allSelectionBounds = useMemo(() => (allLayersSelected ? getCanvasSelectionBounds(document.elements) : null), [allLayersSelected, document.elements]);

  useEffect(() => {
    const resetNodeTransform = (node: HTMLElement | null | undefined) => {
      if (!node) {
        return;
      }
      node.style.transform = "";
      node.style.willChange = "";
    };

    const applyFrameToNode = (node: HTMLElement | null | undefined, frame: HmiElementFrame) => {
      if (!node) {
        return;
      }
      node.style.left = `${frame.x}px`;
      node.style.top = `${frame.y}px`;
      node.style.width = `${frame.width}px`;
      node.style.height = `${frame.height}px`;
      node.style.transform = "";
      node.style.willChange = "left, top, width, height";
    };

    const applyMoveTransform = (node: HTMLElement | null | undefined, deltaX: number, deltaY: number) => {
      if (!node) {
        return;
      }
      node.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0)`;
      node.style.willChange = "transform";
    };

    const resetInteractionStyles = (state: HmiDragState | null) => {
      if (!state) {
        return;
      }
      for (const elementId of Object.keys(state.initialElements)) {
        const node = elementNodeRefs.current.get(elementId);
        if (state.mode === "resize") {
          const frame = state.initialElements[elementId];
          if (frame) {
            applyFrameToNode(node, frame);
            if (node) {
              node.style.willChange = "";
            }
          }
        } else {
          resetNodeTransform(node);
        }
      }
      if (state.mode === "resize" && state.elementId) {
        const frame = state.initialElements[state.elementId];
        if (frame) {
          applyFrameToNode(selectedFrameRef.current, frame);
          if (selectedFrameRef.current) {
            selectedFrameRef.current.style.willChange = "";
          }
        }
      } else {
        resetNodeTransform(selectedFrameRef.current);
      }
      resetNodeTransform(allSelectionFrameRef.current);
    };

    const flushPointerMove = () => {
      rafTokenRef.current = null;
      if (!dragState.current || !queuedPointerRef.current) {
        return;
      }
      const state = dragState.current;
      const { clientX, clientY } = queuedPointerRef.current;
      const deltaX = (clientX - state.startX) / viewport.zoom;
      const deltaY = (clientY - state.startY) / viewport.zoom;
      if (state.mode === "move" || state.mode === "move_all") {
        for (const elementId of Object.keys(state.initialElements)) {
          applyMoveTransform(elementNodeRefs.current.get(elementId), deltaX, deltaY);
        }
        if (state.mode === "move") {
          applyMoveTransform(selectedFrameRef.current, deltaX, deltaY);
        } else {
          applyMoveTransform(allSelectionFrameRef.current, deltaX, deltaY);
        }
      } else if (state.mode === "resize" && state.elementId && state.handle) {
        const initialFrame = state.initialElements[state.elementId];
        const initialElement = document.elements.find((element) => element.id === state.elementId);
        if (initialFrame && initialElement) {
          const draftElement = resizeElementWithHandle(
            elementFromFrame(initialElement, initialFrame),
            state.handle,
            deltaX,
            deltaY,
            1,
            false,
          );
          const draftFrame = frameFromElement(draftElement);
          applyFrameToNode(elementNodeRefs.current.get(state.elementId), draftFrame);
          applyFrameToNode(selectedFrameRef.current, draftFrame);
        }
      } else if (dragState.current.mode === "pan") {
        onPan(clientX - dragState.current.startX, clientY - dragState.current.startY);
        dragState.current = { ...state, startX: clientX, startY: clientY };
      }
    };

    const handleMove = (event: PointerEvent) => {
      if (!dragState.current) {
        return;
      }
      queuedPointerRef.current = { clientX: event.clientX, clientY: event.clientY };
      if (rafTokenRef.current === null) {
        rafTokenRef.current = window.requestAnimationFrame(flushPointerMove);
      }
    };
    const handleUp = (event: PointerEvent) => {
      if (dragState.current) {
        queuedPointerRef.current = { clientX: event.clientX, clientY: event.clientY };
      }
      if (rafTokenRef.current !== null) {
        window.cancelAnimationFrame(rafTokenRef.current);
        rafTokenRef.current = null;
      }
      flushPointerMove();
      const state = dragState.current;
      const queued = queuedPointerRef.current;
      queuedPointerRef.current = null;
      const moved = state && queued ? Math.hypot(queued.clientX - state.startX, queued.clientY - state.startY) > 0.5 : false;
      if (state?.mode === "resize") {
        resetInteractionStyles(state);
      }
      if (state && queued && moved) {
        const deltaX = (queued.clientX - state.startX) / viewport.zoom;
        const deltaY = (queued.clientY - state.startY) / viewport.zoom;
        if (state.mode === "move" && state.elementId) {
          onMoveElement(state.elementId, deltaX, deltaY);
        } else if (state.mode === "move_all") {
          onMoveAllElements(deltaX, deltaY);
        } else if (state.mode === "resize" && state.elementId && state.handle) {
          onResizeElement(state.elementId, deltaX, deltaY, state.handle);
        }
      }
      if (state?.mode !== "resize") {
        resetInteractionStyles(state);
      }
      if (state?.mode === "move" || state?.mode === "resize" || state?.mode === "move_all") {
        onInteractionEnd();
      }
      dragState.current = null;
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    return () => {
      if (rafTokenRef.current !== null) {
        window.cancelAnimationFrame(rafTokenRef.current);
        rafTokenRef.current = null;
      }
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    };
  }, [document.elements, onInteractionEnd, onMoveAllElements, onMoveElement, onPan, onResizeElement, viewport.zoom]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) {
      return;
    }
    const updateStageSize = () => {
      setStageSize({
        width: stage.clientWidth,
        height: stage.clientHeight,
      });
    };
    updateStageSize();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => updateStageSize());
    observer.observe(stage);
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) {
      return;
    }
    const handleWheel = (event: WheelEvent) => {
      if (!event.ctrlKey) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const rect = stage.getBoundingClientRect();
      const point = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      onZoomAroundPoint(event.deltaY < 0 ? 1.08 : 0.92, point);
    };
    stage.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      stage.removeEventListener("wheel", handleWheel);
    };
  }, [onZoomAroundPoint]);

  const floatingToolbarPosition = useMemo(() => {
    if (!selectedElement || runtimeMode || !canEditDashboard) {
      return null;
    }
    const toolbarHeight = 48;
    const toolbarGap = 12;
    const toolbarPadding = 16;
    const estimatedToolbarWidth = 420;
    const rawLeft = viewport.x + (selectedElement.x + selectedElement.width / 2) * viewport.zoom;
    const aboveAnchorTop = viewport.y + selectedElement.y * viewport.zoom - toolbarGap;
    const belowAnchorTop = viewport.y + (selectedElement.y + selectedElement.height) * viewport.zoom + toolbarGap;
    const halfWidth =
      stageSize.width > 0
        ? Math.min(estimatedToolbarWidth / 2, Math.max(128, stageSize.width / 2 - toolbarPadding))
        : estimatedToolbarWidth / 2;
    const left =
      stageSize.width > 0
        ? Math.min(Math.max(rawLeft, toolbarPadding + halfWidth), Math.max(toolbarPadding + halfWidth, stageSize.width - toolbarPadding - halfWidth))
        : rawLeft;
    const canPlaceAbove = aboveAnchorTop >= toolbarPadding + toolbarHeight;
    const canPlaceBelow = belowAnchorTop <= stageSize.height - toolbarPadding - toolbarHeight;
    const placement = !canPlaceAbove && canPlaceBelow ? "below" : "above";
    const top =
      placement === "below"
        ? stageSize.height > 0
          ? Math.min(Math.max(belowAnchorTop, toolbarPadding), Math.max(toolbarPadding, stageSize.height - toolbarPadding - toolbarHeight))
          : belowAnchorTop
        : stageSize.height > 0
          ? Math.min(Math.max(aboveAnchorTop, toolbarPadding + toolbarHeight), Math.max(toolbarPadding + toolbarHeight, stageSize.height - toolbarPadding))
          : aboveAnchorTop;
    return {
      left,
      top,
      placement,
    };
  }, [canEditDashboard, runtimeMode, selectedElement, stageSize.height, stageSize.width, viewport.x, viewport.y, viewport.zoom]);

  const resizeHandles: Array<{ direction: HmiResizeHandle; className: string }> = [
    { direction: "nw", className: "-left-1.5 -top-1.5 h-3 w-3 cursor-nwse-resize rounded-full" },
    { direction: "n", className: "left-1/2 top-0 h-2.5 w-6 -translate-x-1/2 -translate-y-1/2 cursor-ns-resize rounded-full" },
    { direction: "ne", className: "-right-1.5 -top-1.5 h-3 w-3 cursor-nesw-resize rounded-full" },
    { direction: "e", className: "right-0 top-1/2 h-6 w-2.5 translate-x-1/2 -translate-y-1/2 cursor-ew-resize rounded-full" },
    { direction: "se", className: "-right-1.5 -bottom-1.5 h-3 w-3 cursor-nwse-resize rounded-full" },
    { direction: "s", className: "bottom-0 left-1/2 h-2.5 w-6 -translate-x-1/2 translate-y-1/2 cursor-ns-resize rounded-full" },
    { direction: "sw", className: "-left-1.5 -bottom-1.5 h-3 w-3 cursor-nesw-resize rounded-full" },
    { direction: "w", className: "left-0 top-1/2 h-6 w-2.5 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize rounded-full" },
  ];

  return (
    <div
      ref={stageRef}
      className={`hmi-builder-stage relative min-h-0 flex-1 select-none overflow-hidden touch-none ${
        !runtimeMode && canEditDashboard ? "cursor-grab active:cursor-grabbing" : ""
      }`}
      onClick={() => onSelect(null)}
      onPointerDown={(event) => {
        if (!canEditDashboard || runtimeMode || event.button !== 0) {
          return;
        }
        dragState.current = { mode: "pan", startX: event.clientX, startY: event.clientY, initialElements: {} };
      }}
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: document.canvas.grid.enabled
            ? "linear-gradient(color-mix(in srgb, var(--border-strong-ui) 20%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in srgb, var(--border-strong-ui) 20%, transparent) 1px, transparent 1px)"
            : undefined,
          backgroundSize: `${document.canvas.grid.size * viewport.zoom}px ${document.canvas.grid.size * viewport.zoom}px`,
          backgroundPosition: `${viewport.x}px ${viewport.y}px`,
        }}
      />
      {selectedElement && floatingToolbarPosition ? (
        <div
          className="pointer-events-none absolute z-20"
          style={{
            left: floatingToolbarPosition.left,
            top: floatingToolbarPosition.top,
            transform: floatingToolbarPosition.placement === "below" ? "translate(-50%, 0)" : "translate(-50%, -100%)",
          }}
        >
          <div
            className="hmi-floating-toolbar pointer-events-auto inline-flex items-center gap-ds-1 rounded-2xl p-1"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <span className="max-w-[10rem] truncate px-ds-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">
              {selectedElement.name || formatElementTitle(selectedElement.type)}
            </span>
            <SelectionToolButton label="Configure element" onClick={onOpenInspector} compact={false}>
              <Pencil className="h-4 w-4" aria-hidden />
              <span className="text-[11px] font-semibold uppercase tracking-[0.08em]">Configure</span>
            </SelectionToolButton>
            {selectedElement.type === "arrow" ? (
              <SelectionToolButton label="Rotate arrow direction" onClick={onRotateSelectedArrow}>
                <RotateCw className="h-4 w-4" aria-hidden />
              </SelectionToolButton>
            ) : null}
            {selectedElement.type === "symbol_instance" &&
            selectedSymbolDefinition?.builtIn &&
            supportsBuiltinSymbolStateToggle(selectedSymbolDefinition.id) ? (
              <SelectionToolButton
                label={selectedSymbolStateOn ? "Turn asset off" : "Turn asset on"}
                onClick={onToggleSelectedSymbolState}
                compact={false}
              >
                <span className="text-[11px] font-semibold uppercase tracking-[0.08em]">
                  {selectedSymbolStateOn ? "On" : "Off"}
                </span>
              </SelectionToolButton>
            ) : null}
            <SelectionToolButton label="Duplicate element" onClick={onDuplicateSelected}>
              <Plus className="h-4 w-4" aria-hidden />
            </SelectionToolButton>
            <SelectionToolButton label="Bring forward" onClick={() => onReorderSelected("forward")}>
              <ArrowUp className="h-4 w-4" aria-hidden />
            </SelectionToolButton>
            <SelectionToolButton label="Send backward" onClick={() => onReorderSelected("backward")}>
              <ArrowDown className="h-4 w-4" aria-hidden />
            </SelectionToolButton>
            <SelectionToolButton
              label={selectedElement.locked ? "Unlock element" : "Lock element"}
              onClick={onToggleLockSelected}
            >
              {selectedElement.locked ? <Unlock className="h-4 w-4" aria-hidden /> : <Lock className="h-4 w-4" aria-hidden />}
            </SelectionToolButton>
            <SelectionToolButton label="Delete element" onClick={onDeleteSelected} className="text-rose-600 hover:text-rose-700">
              <Trash2 className="h-4 w-4" aria-hidden />
            </SelectionToolButton>
          </div>
        </div>
      ) : null}
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{
          width: document.canvas.width,
          height: document.canvas.height,
          transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
        }}
      >
        {visibleElements.map((element) => {
            const elementBindings = bindingsByElementId.get(element.id) ?? EMPTY_BINDINGS;
            return (
              <div
                key={element.id}
                ref={(node) => {
                  if (node) {
                    elementNodeRefs.current.set(element.id, node);
                  } else {
                    elementNodeRefs.current.delete(element.id);
                  }
                }}
                className={`absolute overflow-hidden rounded-[18px] ${
                  element.locked
                    ? "cursor-not-allowed"
                    : !runtimeMode && canEditDashboard
                      ? "cursor-move"
                      : ""
                }`}
                style={{
                  left: element.x,
                  top: element.y,
                  width: element.width,
                  height: element.height,
                  opacity: element.locked ? Math.max(0.55, element.opacity) : element.opacity,
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  if (allLayersSelected) {
                    return;
                  }
                  onSelect(element.id);
                }}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  if (!canEditDashboard || runtimeMode || event.button !== 0 || element.locked) {
                    return;
                  }
                  onInteractionStart();
                  const initialElements = allLayersSelected
                    ? Object.fromEntries(document.elements.filter((item) => item.visible && !item.locked).map((item) => [item.id, frameFromElement(item)]))
                    : { [element.id]: frameFromElement(element) };
                  dragState.current = allLayersSelected
                    ? { mode: "move_all", startX: event.clientX, startY: event.clientY, initialElements }
                    : { mode: "move", elementId: element.id, startX: event.clientX, startY: event.clientY, initialElements };
                }}
              >
                <MemoizedRuntimeElement
                  element={element}
                  dashboardId={dashboardId}
                  bindings={elementBindings}
                  snapshot={snapshot}
                  devicesById={devicesById}
                  activeAlertFingerprints={activeAlertFingerprints}
                  assetsById={assetsById}
                  symbolDefinitionsById={symbolDefinitionsById}
                  runtimeMode={runtimeMode}
                  canSendCommands={canSendCommands}
                  commandState={commandStates[element.id] ?? null}
                  onExecuteCommand={onExecuteCommand}
                />
              </div>
            );
          })}
        {allLayersSelected && allSelectionBounds && canEditDashboard && !runtimeMode ? (
          <div
            ref={allSelectionFrameRef}
            className="pointer-events-none absolute"
            style={{
              left: allSelectionBounds.x,
              top: allSelectionBounds.y,
              width: allSelectionBounds.width,
              height: allSelectionBounds.height,
            }}
          >
            <div className="hmi-selection-outline absolute inset-0 rounded-[26px] border-2 border-dashed border-[color:var(--accent)] bg-[color:var(--accent)]/5" />
            <div className="absolute -top-8 left-0 rounded-full border border-[color:var(--accent)]/30 bg-card/95 px-ds-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--accent)] shadow-[0_14px_24px_-22px_rgba(15,23,42,0.45)]">
              All layers selected
            </div>
          </div>
        ) : null}
        {selectedElement && canEditDashboard && !runtimeMode ? (
          <div
            ref={selectedFrameRef}
            className="pointer-events-none absolute"
            style={{
              left: selectedElement.x,
              top: selectedElement.y,
              width: selectedElement.width,
              height: selectedElement.height,
            }}
          >
            <div className="hmi-selection-outline absolute inset-0 rounded-[22px] border-2 border-[color:var(--accent)]" />
            {resizeHandles.map((handle) => (
              <ResizeHandle
                key={handle.direction}
                direction={handle.direction}
                className={handle.className}
                disabled={selectedElement.locked}
                onPointerDown={(direction, event) => {
                  event.stopPropagation();
                  if (!canEditDashboard || runtimeMode || event.button !== 0 || selectedElement.locked) {
                    return;
                  }
                  onInteractionStart();
                  dragState.current = {
                    mode: "resize",
                    elementId: selectedElement.id,
                    handle: direction,
                    startX: event.clientX,
                    startY: event.clientY,
                    initialElements: { [selectedElement.id]: frameFromElement(selectedElement) },
                  };
                }}
              />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function HmiDashboardPage({
  dashboard,
  tenantId,
  canEditDashboard,
  onBack,
}: {
  dashboard: DashboardDto;
  tenantId: string;
  canEditDashboard: boolean;
  onBack: () => void;
}) {
  const dialogs = useAppDialogs();
  const [toast, setToast] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const hasStoredHmiLayout = Boolean(
    dashboard.layout &&
      typeof dashboard.layout === "object" &&
      "kind" in dashboard.layout &&
      dashboard.layout.kind === "hmi_canvas",
  );
  const parsedLayout = useMemo(() => readHmiLayout(dashboard.layout), [dashboard.layout]);
  const layout = useMemo(
    () => parsedLayout ?? (hasStoredHmiLayout ? null : createDefaultHmiDocument()),
    [hasStoredHmiLayout, parsedLayout],
  );
  const { ready: imagesReady, hasImages } = useHmiImageAssetReadiness(parsedLayout, dashboard.id);
  const user = useAuthStore((state) => state.user);
  const allWorkspaces = user?.role === "super_admin";
  const { permissionMap } = useEffectivePermissions();
  const canSendCommands = hasModulePermission(user, permissionMap, "commands", "create");
  const initialize = useHmiEditorStore((state) => state.initialize);
  const document = useHmiEditorStore((state) => state.document);
  const selectedElementId = useHmiEditorStore((state) => state.selectedElementId);
  const viewport = useHmiEditorStore((state) => state.viewport);
  const runtimeMode = useHmiEditorStore((state) => state.runtimeMode);
  const saving = useHmiEditorStore((state) => state.saving);
  const saveError = useHmiEditorStore((state) => state.saveError);
  const setViewport = useHmiEditorStore((state) => state.setViewport);
  const setRuntimeMode = useHmiEditorStore((state) => state.setRuntimeMode);
  const selectElement = useHmiEditorStore((state) => state.selectElement);
  const addElement = useHmiEditorStore((state) => state.addElement);
  const addSymbolInstance = useHmiEditorStore((state) => state.addSymbolInstance);
  const updateElement = useHmiEditorStore((state) => state.updateElement);
  const removeSelectedElement = useHmiEditorStore((state) => state.removeSelectedElement);
  const updateBindings = useHmiEditorStore((state) => state.updateBindings);
  const updateDocument = useHmiEditorStore((state) => state.updateDocument);
  const updateDocumentTransient = useHmiEditorStore((state) => state.updateDocumentTransient);
  const duplicateSelectedElement = useHmiEditorStore((state) => state.duplicateSelectedElement);
  const reorderSelectedElement = useHmiEditorStore((state) => state.reorderSelectedElement);
  const beginInteraction = useHmiEditorStore((state) => state.beginInteraction);
  const updateElementTransient = useHmiEditorStore((state) => state.updateElementTransient);
  const endInteraction = useHmiEditorStore((state) => state.endInteraction);
  const markSaving = useHmiEditorStore((state) => state.markSaving);
  const setSaveError = useHmiEditorStore((state) => state.setSaveError);
  const markSaved = useHmiEditorStore((state) => state.markSaved);
  const undo = useHmiEditorStore((state) => state.undo);
  const redo = useHmiEditorStore((state) => state.redo);
  const undoStack = useHmiEditorStore((state) => state.undoStack);
  const redoStack = useHmiEditorStore((state) => state.redoStack);
  const isDirty = useHmiIsDirty();

  const [runtimeSnapshot, setRuntimeSnapshot] = useState<HmiRuntimeSnapshot | null>(null);
  const [pendingCommandRequest, setPendingCommandRequest] = useState<HmiPendingCommandRequest>(null);
  const [commandStates, setCommandStates] = useState<Record<string, HmiCommandRuntimeState>>({});
  const [validating, setValidating] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [sidebarActiveSection, setSidebarActiveSection] = useState<HmiSidebarSection>("insert");
  const [sidebarPanelOpen, setSidebarPanelOpen] = useState(true);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [allLayersSelected, setAllLayersSelected] = useState(false);

  useEffect(() => {
    if (!layout) {
      return;
    }
    initialize(dashboard.id, layout);
  }, [dashboard.id, initialize, layout]);

  const selectedElement = useMemo(
    () => document?.elements.find((element) => element.id === selectedElementId) ?? null,
    [document, selectedElementId],
  );
  const selectedBindings = useMemo(
    () => (selectedElement ? getElementBindings(document?.bindings ?? [], selectedElement) : []),
    [document?.bindings, selectedElement],
  );
  const runtimeRefreshDocument = useMemo(() => document, [document?.bindings, document?.version]);
  const customSymbols = useMemo(
    () => document?.symbols.filter((symbol) => !symbol.builtIn) ?? [],
    [document?.symbols],
  );
  const devicesQuery = useQuery({
    queryKey: ["hmi-runtime-devices", allWorkspaces ? "all" : tenantId, dashboard.id, dashboard.is_global ? "global" : "tenant"],
    queryFn: () =>
      listHmiRenderableDevices({
        tenantId,
        allWorkspaces,
        dashboardId: dashboard.id,
        preferDashboardScope: dashboard.is_global,
      }),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
  const activeAlertSummaryQuery = useQuery({
    queryKey: ["hmi-active-alert-summary", tenantId, allWorkspaces ? "all" : "tenant"],
    queryFn: () => getActiveAlertSummary(tenantId, allWorkspaces ? "all" : null),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
  const devicesById = useMemo(
    () => new Map((devicesQuery.data ?? []).map((device) => [device.id, device])),
    [devicesQuery.data],
  );
  const activeAlertFingerprints = useMemo(
    () => new Set(activeAlertSummaryQuery.data?.fingerprints ?? []),
    [activeAlertSummaryQuery.data?.fingerprints],
  );
  const symbolDefinitionsById = useMemo(
    () => new Map(document?.symbols.map((symbol) => [symbol.id, symbol]) ?? []),
    [document?.symbols],
  );
  const gridSize = document?.canvas.grid.size ?? 16;
  const snapEnabled = document?.canvas.grid.enabled && document.canvas.grid.snap;

  const handleMoveElement = useCallback(
    (elementId: string, deltaX: number, deltaY: number) => {
      updateElementTransient(elementId, (element) => {
        if (element.locked) {
          return element;
        }
        return {
          ...element,
          x: snapToGrid(element.x + deltaX, gridSize, Boolean(snapEnabled)),
          y: snapToGrid(element.y + deltaY, gridSize, Boolean(snapEnabled)),
        };
      });
    },
    [gridSize, snapEnabled, updateElementTransient],
  );

  const handleMoveAllElements = useCallback(
    (deltaX: number, deltaY: number) => {
      updateDocumentTransient((currentDocument) => ({
        ...currentDocument,
        elements: currentDocument.elements.map((element) =>
          element.locked
            ? element
            : {
                ...element,
                x: snapToGrid(element.x + deltaX, gridSize, Boolean(snapEnabled)),
                y: snapToGrid(element.y + deltaY, gridSize, Boolean(snapEnabled)),
              },
        ),
      }));
    },
    [gridSize, snapEnabled, updateDocumentTransient],
  );

  const handleResizeElement = useCallback(
    (elementId: string, deltaX: number, deltaY: number, handle: HmiResizeHandle) => {
      updateElementTransient(elementId, (element) => {
        if (element.locked) {
          return element;
        }
        return resizeElementWithHandle(element, handle, deltaX, deltaY, gridSize, Boolean(snapEnabled));
      });
    },
    [gridSize, snapEnabled, updateElementTransient],
  );

  const handlePanViewport = useCallback(
    (deltaX: number, deltaY: number) => {
      const current = useHmiEditorStore.getState().viewport;
      setViewport({ x: current.x + deltaX, y: current.y + deltaY });
    },
    [setViewport],
  );

  const handleZoomAroundPoint = useCallback(
    (factor: number, point: { x: number; y: number }) => {
      const next = zoomViewportAroundPoint(useHmiEditorStore.getState().viewport, factor, point);
      setViewport(next);
    },
    [setViewport],
  );

  useEffect(() => {
    if (!selectedElement || runtimeMode) {
      setInspectorOpen(false);
    }
  }, [runtimeMode, selectedElement]);

  useEffect(() => {
    if (runtimeMode && allLayersSelected) {
      setAllLayersSelected(false);
    }
  }, [allLayersSelected, runtimeMode]);

  const handleSelectElement = useCallback(
    (elementId: string | null) => {
      setAllLayersSelected(false);
      selectElement(elementId);
    },
    [selectElement],
  );

  const handleSelectAllLayers = useCallback(() => {
    if (!document?.elements.length || runtimeMode || !canEditDashboard) {
      return;
    }
    setAllLayersSelected((current) => {
      const next = !current;
      if (next) {
        selectElement(null);
        setInspectorOpen(false);
      }
      return next;
    });
  }, [canEditDashboard, document?.elements.length, runtimeMode, selectElement]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== "a" || runtimeMode || !canEditDashboard) {
        return;
      }
      const target = event.target;
      if (target instanceof HTMLElement) {
        const tagName = target.tagName;
        if (target.isContentEditable || tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") {
          return;
        }
      }
      event.preventDefault();
      if (!document?.elements.length) {
        return;
      }
      setAllLayersSelected(true);
      selectElement(null);
      setInspectorOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [canEditDashboard, document?.elements.length, runtimeMode, selectElement]);

  const deleteImageAsset = (storagePath: string) => {
    void deleteHmiImageAsset(storagePath).catch(() => {
      void dialogs.alert({
        title: "Image file cleanup incomplete",
        description: "The image element was removed, but the uploaded image file could not be deleted.",
        tone: "danger",
      });
    });
  };

  const handleDeleteSelectedElement = () => {
    const imageStoragePath =
      selectedElement?.type === "image_asset"
        ? getImageAssetStoragePath(document, selectedElement.asset.assetId)
        : null;
    const referencedByAnotherElement = Boolean(
      imageStoragePath &&
        document?.elements.some((element) => {
          if (element.id === selectedElement?.id || element.type !== "image_asset") {
            return false;
          }
          return getImageAssetStoragePath(document, element.asset.assetId) === imageStoragePath;
        }),
    );
    removeSelectedElement();
    if (imageStoragePath && !referencedByAnotherElement) {
      deleteImageAsset(imageStoragePath);
    }
  };

  const saveMutation = useMutation({
    mutationFn: async ({ nextDocument, mode }: { nextDocument: HmiCanvasDocument; mode: HmiPersistMode }) => {
      markSaving(true);
      const now = new Date().toISOString();
      const revisionDocument = stripNestedRevisionHistory(cloneHmiDocument(nextDocument));
      const revisionId = `rev_${Date.now()}`;
      const nextRevisions = [
        ...(stripNestedRevisionHistory(nextDocument).metadata.revisions ?? []),
        {
          id: revisionId,
          createdAt: now,
          note: mode === "publish" ? "Published revision" : "Manual save",
          document: revisionDocument,
        },
      ].slice(-20);
      const documentToValidate: HmiCanvasDocument = {
        ...nextDocument,
        metadata: {
          ...nextDocument.metadata,
          updatedAt: now,
          workflow: {
            publicationStatus: mode === "publish" ? "published" : nextDocument.metadata.workflow?.publicationStatus ?? "draft",
            publishedAt: mode === "publish" ? now : nextDocument.metadata.workflow?.publishedAt,
            publishedRevisionId: mode === "publish" ? revisionId : nextDocument.metadata.workflow?.publishedRevisionId,
            templateId: nextDocument.metadata.workflow?.templateId,
            templateName: nextDocument.metadata.workflow?.templateName,
          },
          revisions: nextRevisions,
        },
      };
      const validation = await validateHmiCanvas(toDashboardLayout(documentToValidate));
      const validatedDocument = readHmiLayout(validation.layout) ?? cloneHmiDocument(documentToValidate);
      const savedDashboard = await updateDashboard(dashboard.id, {
        layout: validation.layout as DashboardDto["layout"],
      });
      return {
        savedDashboard,
        validatedDocument,
      };
    },
    onSuccess: ({ savedDashboard, validatedDocument }) => {
      const savedLayout = readHmiLayout(savedDashboard.layout) ?? validatedDocument;
      markSaved(savedLayout);
      queryClient.setQueryData<DashboardDto>(["dashboard", dashboard.id], (current) =>
        current
          ? {
              ...current,
              ...savedDashboard,
              layout: toDashboardLayout(savedLayout) as DashboardDto["layout"],
            }
          : savedDashboard,
      );
      setValidationMessage("Canvas validated and saved.");
    },
    onError: (error) => {
      setSaveError(error instanceof Error ? error.message : "Could not save HMI document.");
      markSaving(false);
    },
  });

  const sendCommandMutation = useMutation({
    mutationFn: async (request: Exclude<HmiPendingCommandRequest, null>) =>
      sendCommand({
        device_id: request.deviceId,
        command: request.payload as JsonObject,
      }),
    onSuccess: (result, request) => {
      setCommandStates((current) => ({
        ...current,
        [request.elementId]: {
          commandId: result.id,
          status: result.status ?? "pending",
          response: result.response ?? null,
          createdAt: result.created_at ?? null,
        },
      }));
      setPendingCommandRequest(null);
    },
    onError: (error, request) => {
      setCommandStates((current) => ({
        ...current,
        [request.elementId]: {
          status: "failed",
          error: error instanceof Error ? error.message : "Command failed.",
        },
      }));
      setPendingCommandRequest(null);
    },
  });

  useEffect(() => {
    if (!runtimeRefreshDocument) {
      return;
    }
    const engine = new HmiRuntimeEngine({ tenantId, allWorkspaces, dashboardId: dashboard.is_global ? dashboard.id : null });
    const refresh = async () => {
      try {
        const snapshot = await engine.refresh(runtimeRefreshDocument);
        setRuntimeSnapshot(snapshot);
      } catch (error) {
        setRuntimeSnapshot((current) => current ?? null);
        setSaveError(error instanceof Error ? error.message : "Could not refresh telemetry.");
      }
    };
    void refresh();
    const scheduler = new RuntimeScheduler(() => {
      void refresh();
    }, HMI_LIVE_POLL_INTERVAL_MS);
    scheduler.start();
    return () => {
      scheduler.stop();
    };
  }, [allWorkspaces, dashboard.id, dashboard.is_global, runtimeRefreshDocument, tenantId, setSaveError]);

  useEffect(() => {
    const activeEntries = Object.entries(commandStates).filter(([, state]) => state.commandId && !isTerminalCommandStatus(state.status));
    if (activeEntries.length === 0) {
      return;
    }
    const interval = window.setInterval(() => {
      void Promise.all(
        activeEntries.map(async ([elementId, state]) => {
          if (!state.commandId) {
            return null;
          }
          try {
            const result = await getCommandById(state.commandId);
            return { elementId, result };
          } catch {
            return null;
          }
        }),
      ).then((results) => {
        setCommandStates((current) => {
          const next = { ...current };
          for (const item of results) {
            if (!item) {
              continue;
            }
            next[item.elementId] = {
              commandId: item.result.id,
              status: item.result.status ?? "pending",
              response: item.result.response ?? null,
              createdAt: item.result.created_at ?? null,
            };
          }
          return next;
        });
      });
    }, 2500);
    return () => window.clearInterval(interval);
  }, [commandStates]);

  const runValidation = async (candidate: HmiCanvasDocument) => {
    setValidating(true);
    setValidationMessage(null);
    setSaveError(null);
    try {
      const result = await validateHmiCanvas(toDashboardLayout(candidate));
      const normalized = readHmiLayout(result.layout) ?? candidate;
      setValidationMessage(result.migrated ? `Validated and migrated to version ${result.version}.` : `Validated successfully (v${result.version}).`);
      return normalized;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not validate HMI canvas.";
      setSaveError(message);
      throw error;
    } finally {
      setValidating(false);
    }
  };

  const applyTemplate = (templateId: string) => {
    void (async () => {
      const template = hmiTemplates.find((entry) => entry.id === templateId);
      if (!template) {
        return;
      }
      const confirmed = await dialogs.confirm({
        title: `Replace with ${template.title}?`,
        description: "This replaces the current canvas with the selected template.",
        confirmLabel: "Replace canvas",
        tone: "danger",
      });
      if (!confirmed) {
        return;
      }
      initialize(dashboard.id, template.buildDocument());
      setValidationMessage(`Applied template: ${template.title}.`);
    })();
  };

  const restoreRevision = (index: number) => {
    if (!document) {
      return;
    }
    const revision = document.metadata.revisions?.[index];
    if (!revision || !revision.document) {
      return;
    }
    const restored = cloneHmiDocument(revision.document as HmiCanvasDocument);
    initialize(dashboard.id, restored);
  };

  const closeInspector = () => {
    const validationMessage = validateGaugeThresholdOrder(selectedElement);
    if (validationMessage) {
      setToast(validationMessage);
      return false;
    }
    setInspectorOpen(false);
    return true;
  };

  const handleSidebarPanelOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && inspectorOpen && !closeInspector()) {
      return;
    }
    setSidebarPanelOpen(nextOpen);
  };

  const handleSidebarSectionSelect = (sectionId: HmiSidebarSection) => {
    if (sidebarActiveSection === sectionId) {
      handleSidebarPanelOpenChange(!sidebarPanelOpen);
      return;
    }
    setSidebarActiveSection(sectionId);
    setSidebarPanelOpen(true);
  };

  const toggleElementLock = (elementId: string) => {
    updateElement(elementId, (element) => ({
      ...element,
      locked: !element.locked,
    }));
  };

  if (hasStoredHmiLayout && !parsedLayout) {
    return (
      <Card className="m-ds-6 border-error/30 bg-error/5 p-ds-5">
        <p className="text-title text-foreground">Could not load HMI canvas</p>
        <p className="mt-ds-2 text-caption text-muted">
          The saved dashboard layout did not match the HMI schema. Check the browser console for the exact parse error.
        </p>
      </Card>
    );
  }

  if (!document) {
    return (
      <div className="flex min-h-[24rem] items-center justify-center gap-ds-2 text-caption text-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading HMI...
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-background text-foreground">
      <HmiHeader
        dashboard={dashboard}
        canEditDashboard={canEditDashboard}
        runtimeMode={runtimeMode}
        saving={saving}
        isDirty={isDirty}
        saveError={saveError}
        publicationStatus={document.metadata.workflow?.publicationStatus ?? "draft"}
        validating={validating}
        validationMessage={validationMessage}
        onBack={onBack}
        onExportJson={() => {
          const blob = new Blob([JSON.stringify(toDashboardLayout(document), null, 2)], { type: "application/json" });
          const href = URL.createObjectURL(blob);
          const link = window.document.createElement("a");
          link.href = href;
          link.download = `${dashboard.name.replace(/[^a-z0-9]+/gi, "_").toLowerCase() || "hmi_canvas"}.json`;
          link.click();
          URL.revokeObjectURL(href);
        }}
        onImportJson={() => {
          void (async () => {
            const raw = await dialogs.input({
              title: "Import HMI JSON",
              description: "Paste an exported HMI canvas JSON document to validate and load it.",
              label: "Canvas JSON",
              placeholder: "{ ... }",
              multiline: true,
              confirmLabel: "Import JSON",
            });
            if (!raw) {
              return;
            }
            try {
              const parsed = JSON.parse(raw);
              void runValidation(parsed as HmiCanvasDocument)
                .then((normalized) => {
                  initialize(dashboard.id, normalized);
                })
                .catch(() => {});
            } catch (error) {
              setSaveError(error instanceof Error ? error.message : "Could not import HMI JSON.");
            }
          })();
        }}
        onToggleMode={setRuntimeMode}
        onSave={() => {
          void saveMutation.mutateAsync({ nextDocument: document, mode: "save" });
        }}
        onValidate={() => {
          void runValidation(document).catch(() => {});
        }}
        onPublish={() => {
          void saveMutation.mutateAsync({ nextDocument: document, mode: "publish" });
        }}
      />
      <HmiToolbar
        zoom={viewport.zoom}
        allLayersSelected={allLayersSelected}
        runtimeMode={runtimeMode}
        canEditDashboard={canEditDashboard}
        canUndo={undoStack.length > 0}
        canRedo={redoStack.length > 0}
        onSelectAllLayers={handleSelectAllLayers}
        onUndo={undo}
        onRedo={redo}
        onZoom={(delta) => {
          const current = useHmiEditorStore.getState().viewport;
          setViewport({ zoom: current.zoom + delta });
        }}
      />
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <HmiEditorSidebar
          document={document}
          selectedElementId={selectedElementId}
          selectedElement={selectedElement}
          selectedBindings={selectedBindings}
          tenantId={tenantId}
          allWorkspaces={allWorkspaces}
          allLayersSelected={allLayersSelected}
          runtimeMode={runtimeMode}
          canEditDashboard={canEditDashboard}
          customSymbols={customSymbols}
          snapshot={runtimeSnapshot}
          activeSection={sidebarActiveSection}
          panelOpen={sidebarPanelOpen}
          inspectorOpen={inspectorOpen}
          onAddElement={addElement}
          onAddSymbol={addSymbolInstance}
          onApplyTemplate={applyTemplate}
          onSelect={handleSelectElement}
          onToggleLock={toggleElementLock}
          onReorder={reorderSelectedElement}
          onRestore={restoreRevision}
          onSelectAllLayers={handleSelectAllLayers}
          onPanelOpenChange={handleSidebarPanelOpenChange}
          onSectionSelect={handleSidebarSectionSelect}
          onCloseInspector={() => {
            void closeInspector();
          }}
          onDeleteImageAsset={deleteImageAsset}
          onUpdateElement={(updater) => {
            if (!selectedElement) {
              return;
            }
            updateElement(selectedElement.id, updater);
          }}
          onBindingsChange={(updater) => {
            if (!selectedElement) {
              return;
            }
            updateBindings(selectedElement.id, updater);
          }}
          onUpdateDocument={updateDocument}
          onSaveAsSymbol={(existingSymbolId) => {
            void (async () => {
              if (!selectedElement || !canCreateCustomSymbol(selectedElement)) {
                return;
              }
              const title = await dialogs.input({
                title: "Save custom symbol",
                description: "Choose a reusable symbol title for this element.",
                label: "Custom symbol title",
                initialValue: selectedElement.name || "Custom symbol",
                confirmLabel: "Continue",
              });
              if (!title) {
                return;
              }
              const categoryInput = await dialogs.input({
                title: "Symbol category",
                description: "Use one of: hvac, process, utility, generic.",
                label: "Category",
                initialValue: "generic",
                confirmLabel: "Save symbol",
              });
              if (!categoryInput) {
                return;
              }
              const category = ["hvac", "process", "utility", "generic"].includes(categoryInput)
                ? (categoryInput as HmiSymbolDefinition["category"])
                : "generic";
              updateDocument((current) =>
                saveElementAsCustomSymbol(current, selectedElement.id, {
                  title,
                  category,
                  tenantId,
                  existingSymbolId,
                }),
              );
            })();
          }}
          onUpgradeSymbolInstances={(definitionId, mode) => {
            updateDocument((current) => upgradeSymbolInstances(current, definitionId, mode, selectedElement?.id ?? null));
          }}
        />
        <div className="hmi-builder-shell flex min-w-0 flex-1 flex-col overflow-hidden p-ds-4">
          <div className="hmi-builder-stage-frame flex min-h-0 flex-1 overflow-hidden rounded-[28px]">
            {!imagesReady && hasImages ? (
              <div className="flex h-full min-h-[24rem] w-full items-center justify-center gap-ds-2 text-caption text-muted">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading dashboard image...
              </div>
            ) : (
              <HmiCanvasStage
                dashboardId={dashboard.id}
                document={document}
                selectedElementId={selectedElementId}
                allLayersSelected={allLayersSelected}
                runtimeMode={runtimeMode}
                viewport={viewport}
                snapshot={runtimeSnapshot}
                devicesById={devicesById}
                activeAlertFingerprints={activeAlertFingerprints}
                symbolDefinitionsById={symbolDefinitionsById}
                canSendCommands={canSendCommands}
                commandStates={commandStates}
                canEditDashboard={canEditDashboard}
                onSelect={handleSelectElement}
                onMoveElement={handleMoveElement}
                onMoveAllElements={handleMoveAllElements}
                onResizeElement={handleResizeElement}
                onPan={handlePanViewport}
                onZoomAroundPoint={handleZoomAroundPoint}
                onInteractionStart={beginInteraction}
                onInteractionEnd={endInteraction}
                onExecuteCommand={(request) => {
                  if (!request || !runtimeMode) {
                    return;
                  }
                  const currentElement = document.elements.find((item) => item.id === request.elementId);
                  const requiresConfirm =
                    currentElement && "command" in currentElement
                      ? currentElement.command.requireConfirm
                      : currentElement && "toggle" in currentElement
                        ? currentElement.toggle.requireConfirm
                        : currentElement && "setpoint" in currentElement
                          ? currentElement.setpoint.requireConfirm
                          : true;
                  if (!requiresConfirm) {
                    void sendCommandMutation.mutateAsync(request);
                    return;
                  }
                  setPendingCommandRequest(request);
                }}
                onDuplicateSelected={duplicateSelectedElement}
                onDeleteSelected={handleDeleteSelectedElement}
                onToggleLockSelected={() => {
                  if (!selectedElement) {
                    return;
                  }
                  toggleElementLock(selectedElement.id);
                }}
                onReorderSelected={reorderSelectedElement}
                onRotateSelectedArrow={() => {
                  if (!selectedElement || selectedElement.type !== "arrow") {
                    return;
                  }
                  updateElement(selectedElement.id, (element) =>
                    element.type === "arrow"
                      ? {
                          ...element,
                          arrow: {
                            ...element.arrow,
                            direction: getNextArrowDirection(element.arrow.direction),
                          },
                        }
                      : element,
                  );
                }}
                onToggleSelectedSymbolState={() => {
                  if (!selectedElement || selectedElement.type !== "symbol_instance") {
                    return;
                  }
                  updateElement(selectedElement.id, (element) =>
                    element.type === "symbol_instance"
                      ? {
                          ...element,
                          symbol: {
                            ...element.symbol,
                            overrides: {
                              ...element.symbol.overrides,
                              stateOn: !(readSymbolOverrideBoolean(element.symbol.overrides as Record<string, unknown>, "stateOn") ?? true),
                            },
                          },
                        }
                      : element,
                  );
                }}
                onOpenInspector={() => {
                  if (!selectedElement) {
                    return;
                  }
                  setSidebarPanelOpen(true);
                  setInspectorOpen(true);
                }}
              />
            )}
          </div>
        </div>
      </div>
      {pendingCommandRequest ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 p-ds-4">
          <Card className="w-full max-w-md border-border/70 bg-card p-ds-5 shadow-2xl">
            <p className="text-title text-foreground">{pendingCommandRequest.title}</p>
            <p className="mt-ds-2 text-caption text-muted">{pendingCommandRequest.message}</p>
            <pre className="mt-ds-3 max-h-40 overflow-auto rounded-xl bg-slate-950/5 p-ds-3 text-[11px] text-foreground">
              {JSON.stringify(pendingCommandRequest.payload, null, 2)}
            </pre>
            <div className="mt-ds-4 flex justify-end gap-ds-2">
              <Button type="button" variant="secondary" size="sm" disabled={sendCommandMutation.isPending} onClick={() => setPendingCommandRequest(null)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                disabled={sendCommandMutation.isPending}
                onClick={() => {
                  if (!pendingCommandRequest) {
                    return;
                  }
                  void sendCommandMutation.mutateAsync(pendingCommandRequest);
                }}
              >
                {sendCommandMutation.isPending ? "Sending..." : "Confirm"}
              </Button>
            </div>
          </Card>
        </div>
      ) : null}
      {runtimeSnapshot?.errors.length ? (
        <Card className="m-ds-4 mt-0 border-error/30 bg-error/5 p-ds-3">
          <p className="text-caption text-error">
            {runtimeSnapshot.errors.map((error) => error.message).join(" | ")}
          </p>
        </Card>
      ) : null}
      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
    </div>
  );
}

export function HmiDashboardOverviewPreview({
  dashboard,
  tenantId,
}: {
  dashboard: DashboardDto;
  tenantId: string;
}) {
  const parsedLayout = useMemo(() => readHmiLayout(dashboard.layout), [dashboard.layout]);
  const { ready: imagesReady, hasImages } = useHmiImageAssetReadiness(parsedLayout, dashboard.id);
  const [runtimeSnapshot, setRuntimeSnapshot] = useState<HmiRuntimeSnapshot | null>(null);
  const [previewWidth, setPreviewWidth] = useState(0);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const user = useAuthStore((state) => state.user);
  const allWorkspaces = user?.role === "super_admin";

  const devicesQuery = useQuery({
    queryKey: ["hmi-overview-devices", allWorkspaces ? "all" : tenantId, dashboard.id, dashboard.is_global ? "global" : "tenant"],
    queryFn: () =>
      listHmiRenderableDevices({
        tenantId,
        allWorkspaces,
        dashboardId: dashboard.id,
        preferDashboardScope: dashboard.is_global,
      }),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
  const activeAlertSummaryQuery = useQuery({
    queryKey: ["hmi-overview-active-alert-summary", tenantId, allWorkspaces ? "all" : "tenant"],
    queryFn: () => getActiveAlertSummary(tenantId, allWorkspaces ? "all" : null),
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

  const devicesById = useMemo(
    () => new Map((devicesQuery.data ?? []).map((device) => [device.id, device])),
    [devicesQuery.data],
  );
  const activeAlertFingerprints = useMemo(
    () => new Set(activeAlertSummaryQuery.data?.fingerprints ?? []),
    [activeAlertSummaryQuery.data?.fingerprints],
  );
  const symbolDefinitionsById = useMemo(
    () => new Map(parsedLayout?.symbols.map((symbol) => [symbol.id, symbol]) ?? []),
    [parsedLayout?.symbols],
  );
  const assetsById = useMemo(() => new Map(parsedLayout?.assets.map((asset) => [asset.id, asset]) ?? []), [parsedLayout?.assets]);
  const layerBounds = useMemo(() => {
    if (!parsedLayout) {
      return null;
    }
    return (
      getCanvasSelectionBounds(parsedLayout.elements) ?? {
        x: 0,
        y: 0,
        width: parsedLayout.canvas.width,
        height: parsedLayout.canvas.height,
      }
    );
  }, [parsedLayout]);
  const bindingsByElementId = useMemo(() => {
    const next = new Map<string, HmiBinding[]>();
    for (const binding of parsedLayout?.bindings ?? []) {
      const existing = next.get(binding.elementId);
      if (existing) {
        existing.push(binding);
      } else {
        next.set(binding.elementId, [binding]);
      }
    }
    return next;
  }, [parsedLayout?.bindings]);
  const visibleElements = useMemo(
    () =>
      (parsedLayout?.elements ?? [])
        .slice()
        .sort((a, b) => a.zIndex - b.zIndex)
        .filter((element) => element.visible),
    [parsedLayout?.elements],
  );

  useEffect(() => {
    const node = previewRef.current;
    if (!node) {
      return;
    }
    const update = () => {
      setPreviewWidth(node.clientWidth);
    };
    update();
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => update());
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!parsedLayout) {
      return;
    }
    const engine = new HmiRuntimeEngine({ tenantId, allWorkspaces, dashboardId: dashboard.is_global ? dashboard.id : null });
    const refresh = async () => {
      try {
        const snapshot = await engine.refresh(parsedLayout);
        setRuntimeSnapshot(snapshot);
      } catch {
        setRuntimeSnapshot((current) => current ?? null);
      }
    };
    void refresh();
    const scheduler = new RuntimeScheduler(() => {
      void refresh();
    }, HMI_LIVE_POLL_INTERVAL_MS);
    scheduler.start();
    return () => {
      scheduler.stop();
    };
  }, [allWorkspaces, dashboard.id, dashboard.is_global, parsedLayout, tenantId]);

  if (!parsedLayout) {
    return (
      <Card className="p-ds-5">
        <p className="text-title text-foreground">Could not load HMI canvas</p>
        <p className="mt-ds-2 text-caption text-muted">The saved HMI layout is unavailable for overview preview.</p>
      </Card>
    );
  }

  const padding = 24;
  const bounds = layerBounds ?? {
    x: 0,
    y: 0,
    width: parsedLayout.canvas.width,
    height: parsedLayout.canvas.height,
  };
  const contentWidth = Math.max(1, bounds.width + padding * 2);
  const contentHeight = Math.max(1, bounds.height + padding * 2);
  const scale = previewWidth > 0 ? previewWidth / contentWidth : 1;
  const previewHeight = contentHeight * scale;

  return (
    <div className="rounded-[28px] border border-border/60 bg-background p-ds-4 shadow-[0_18px_36px_-30px_rgba(15,23,42,0.24)]">
      <div ref={previewRef} className="relative w-full overflow-hidden rounded-[24px] bg-background" style={{ height: previewHeight }}>
        {!imagesReady && hasImages ? (
          <div className="flex h-full w-full items-center justify-center gap-ds-2 text-caption text-muted">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading dashboard image...
          </div>
        ) : (
          <div
            className="absolute left-0 top-0 origin-top-left"
            style={{
              width: contentWidth,
              height: contentHeight,
              transform: `scale(${scale})`,
            }}
          >
            {visibleElements.map((element) => (
              <div
                key={element.id}
                className="absolute overflow-hidden rounded-[18px]"
                style={{
                  left: element.x - bounds.x + padding,
                  top: element.y - bounds.y + padding,
                  width: element.width,
                  height: element.height,
                  opacity: element.opacity,
                }}
              >
                <MemoizedRuntimeElement
                  element={element}
                  dashboardId={dashboard.id}
                  bindings={bindingsByElementId.get(element.id) ?? EMPTY_BINDINGS}
                  snapshot={runtimeSnapshot}
                  devicesById={devicesById}
                  activeAlertFingerprints={activeAlertFingerprints}
                  assetsById={assetsById}
                  symbolDefinitionsById={symbolDefinitionsById}
                  runtimeMode
                  canSendCommands={false}
                  commandState={null}
                  onExecuteCommand={() => {}}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
